export interface HelpFAQ {
  question: string;
  answer: string;
}

export interface HelpStep {
  text: string;
  tip?: string;
}

export interface HelpSection {
  id: string;
  title: string;
  description: string;
  steps: HelpStep[];
  faqs: HelpFAQ[];
}

export interface TroubleshootingEntry {
  symptom: string;
  cause: string;
  solution: string;
}

export interface ChangelogEntry {
  date: string;
  feature: string;
  description: string;
}

export interface HelpContent {
  lastUpdated: string;
  quickStart: HelpStep[];
  sections: HelpSection[];
  troubleshooting: TroubleshootingEntry[];
  changelog: ChangelogEntry[];
}

const helpContent: HelpContent = {
  lastUpdated: "2026-10-31",

  quickStart: [
    {
      text: "Sign in. If prompted, complete the onboarding wizard — it walks you through first-time setup.",
    },
    {
      text: "Go to Configure → Toolkits → Add Server. Paste your API's Swagger/OpenAPI URL, or upload a JSON/YAML file.",
    },
    {
      text: "Enable the specific tools you want the AI to call by toggling them on in the server detail view.",
    },
    {
      text: "Go to Configure → AI & UX. Select your widget and set a tone of voice and persona instructions for the AI.",
    },
    {
      text: "Go to Configure → User Interface. Set your widget's colors, logo, and greeting message.",
    },
    {
      text: "Go to Configure → Embed. Copy the script tag and paste it before </body> on your website.",
    },
    {
      text: "Open your site and send a test message to confirm the widget is working.",
    },
  ],

  sections: [
    {
      id: "onboarding",
      title: "Getting Started with the Onboarding Wizard",
      description:
        "The wizard guides you through first-time setup in guided steps — the fastest way to go from zero to a working widget.",
      steps: [
        {
          text: "Go to the Sign Up page, enter your email and a password, and click Sign Up.",
        },
        {
          text: "Click the link in the confirmation email we send you. The link works for 24 hours; it creates your account and signs you in.",
          tip: "Your account is created only when you click the link. Until then you can't sign in with that email.",
        },
        {
          text: "Sign in. If this is your first session, the wizard opens automatically. You can also reopen it via Settings → Onboarding at any time.",
        },
        {
          text: "Step 1 — Overview: read the summary of what you will set up.",
        },
        {
          text: "Step 2 — API Documentation: paste your OpenAPI/Swagger URL, upload a JSON or YAML file, or paste raw spec text. Choose the spec type (OpenAPI, GraphQL, etc.).",
          tip: "You can also paste the address of the API documentation page you see in your browser. Click Validate and the specification behind it is found for you.",
        },
        {
          text: "Step 3 — Authorization: select your auth type and enter credentials. Click Test Connection to confirm the AI can reach your API.",
        },
        {
          text: "Step 4 — Generation Progress: the system generates all available tools from your spec. Watch the progress bar — errors are shown inline if any tools fail.",
        },
        {
          text: "Step 5 — Tool Selection: toggle on the specific tools you want the AI to use. You can change this later in Configure → Toolkits.",
        },
        {
          text: "Step 6 — Masking Rules (shown only if the AI detected sensitive fields in your API): review the suggested masking rules and click Approve or Dismiss for each.",
          tip: "You can approve all suggestions at once with Approve All.",
        },
        {
          text: "Step 7 — Done: your widget is ready. Copy the embed snippet shown and paste it on your website.",
        },
      ],
      faqs: [
        {
          question:
            "The wizard closed before I finished — did I lose my progress?",
          answer:
            "No. Progress is saved automatically. Reopen the wizard from Settings → Onboarding and it will resume from where you left off.",
        },
        {
          question: "I already set up manually — do I need the wizard?",
          answer:
            "No. The wizard is optional. Everything it configures is also available in the individual Configure and Security pages.",
        },
        {
          question:
            "Can I re-run the onboarding wizard after setup is complete?",
          answer:
            "Yes. Go to Settings → Onboarding at any time to re-run the full flow or revisit individual steps.",
        },
        {
          question: "What happens if tool generation fails?",
          answer:
            "The wizard shows an error summary step. You can review the errors, fix your API spec or auth settings, and retry generation without starting over.",
        },
        {
          question:
            "The confirmation link doesn't work, or the email never arrived — what now?",
          answer:
            "Sign up again with the same email to get a new link. Each link works once and for 24 hours, and only the newest link works. If the email doesn't arrive, check your spam folder.",
        },
        {
          question: "Do capital letters in my email address matter?",
          answer:
            "No. Anna.Becker@example.com and anna.becker@example.com are the same account, both when you sign up and when you sign in.",
        },
      ],
    },
    {
      id: "connecting-api-server",
      title: "Connecting an API Server",
      description:
        "Link your external APIs so the AI can call them on behalf of your users.",
      steps: [
        { text: "Go to Configure → Toolkits and click Add Server." },
        {
          text: "Choose how to add the server: paste a Swagger/OpenAPI URL, upload a JSON or YAML file, or add endpoints manually one by one.",
          tip: "You can paste the address of the API documentation page itself — the page you see in your browser, such as https://app.example.com/swagger/ui/index. When you click Validate, the specification behind that page is found for you and the field is filled in with it.",
        },
        {
          text: "Click Validate. If the address you entered was a documentation page, the field is replaced with the specification address that was found, and a short note explains why it changed.",
        },
        {
          text: "Choose the authentication type. See the FAQ below for guidance on which type to pick.",
        },
        {
          text: "Click Save. The system reads your API definition and lists every available tool (endpoint).",
        },
        {
          text: "In the server detail view, toggle individual tools on or off. Only enabled tools are available to the AI.",
        },
        {
          text: "To test authentication, click Check Connection — a green checkmark means the AI can reach your API.",
        },
        {
          text: "To check if your API spec has changed since you last imported it, click Check for Updates. The system compares your current tools against the latest spec and shows a diff. Click Apply Updates to regenerate tools from the new spec.",
        },
      ],
      faqs: [
        {
          question: "What authentication types are supported?",
          answer:
            "None (public APIs), OAuth 2.0 (redirects users to login), Basic Auth (username + password pair), Custom Static Token (a single API key stored once), and Custom Endpoint (your API issues tokens dynamically — you provide an endpoint that the system calls to refresh them).",
        },
        {
          question: "I added a server but no tools appeared — why?",
          answer:
            "Check that your Swagger/OpenAPI file is valid JSON or YAML. Confirm the base URL is reachable from the internet (not localhost). If you uploaded a file, re-upload and check for parse errors shown in the UI.",
        },
        {
          question:
            "Do I have to find the exact JSON or YAML address, or can I paste the documentation page I see in my browser?",
          answer:
            "Either works. Paste the documentation page address — for example https://app.example.com/swagger/ui/index — and click Validate. The specification behind that page is found automatically, and the field is replaced with it so you can see exactly what will be imported. Swagger UI, Redoc, RapiDoc, Scalar and Stoplight documentation pages are all recognised.",
        },
        {
          question:
            "I pasted a documentation page and got an error saying the specification could not be found — what now?",
          answer:
            "Some documentation pages build their content in a way that hides the underlying specification address. Open the page in your browser, look for a link labelled something like the raw or JSON version of the specification, and paste that address instead. It usually ends in .json or .yaml.",
        },
        {
          question: "What file formats does the Swagger import accept?",
          answer:
            "JSON and YAML are both supported, following OpenAPI 3.0 or Swagger 2.0. You can paste a URL that points directly to a .yaml or .yml file, paste the address of a documentation page, or upload a file — all three work the same way. The spec must be complete (not just a fragment).",
        },
        {
          question: "Can I add an API that doesn't have a Swagger file?",
          answer:
            "Yes. Use the manual endpoint option to add each endpoint by hand (method, path, parameters, auth).",
        },
        {
          question: 'What does "Check Connection" do?',
          answer:
            "It calls a lightweight test request to your API using the configured auth. If it returns a non-error response, authentication is confirmed working.",
        },
        {
          question: "Can I test an individual tool without using the widget?",
          answer:
            "Yes. In Configure → Toolkits, open a server, find any enabled tool, and click the Test button. A modal opens where you can fill in the tool's parameters and execute it — the raw API response is shown immediately.",
        },
        {
          question: "My API changed — how do I update the tools?",
          answer:
            "Click Check for Updates on the server. The system shows which tools were added, changed, or removed. Click Apply Updates to regenerate from the latest spec. Existing enabled/disabled states are preserved where possible.",
        },
        {
          question: "How do I duplicate a toolkit and its widget?",
          answer:
            "Open Configure → Toolkits, click the three-dot menu on any toolkit card, and choose Copy. The system creates a full copy of the toolkit (including all its tools) and automatically duplicates every widget linked to it. The copied widget keeps all settings — agents, skills, knowledge base selections, handoff configuration, URL visibility rules, and display style. A new embed token is generated for the copied widget (the original continues to work unchanged). Authorization credentials are not copied for security reasons — re-enter them on the copied toolkit.",
        },
        {
          question: "How many servers can one widget use?",
          answer: "Each widget can connect to 1–5 API servers simultaneously.",
        },
      ],
    },
    {
      id: "editing-tools",
      title: "Editing Tools and Toolkits",
      description:
        "Adjust a toolkit's own details, and fine-tune each tool — rename it, change how it appears, set its risk level and safety flags, and edit its parameters.",
      steps: [
        {
          text: "Go to Configure → Toolkits and select the toolkit you want to edit.",
        },
        {
          text: "To edit the toolkit's own details, click Edit. You can change the toolkit Name, Base URL, Specification URL, and Description. Click Save.",
        },
        {
          text: "The Specification URL is the address your API documentation file is read from. Change it if your provider moved the file. As soon as you type a different address it is checked automatically: you will see Checking specification..., then either how many endpoints were found, or the reason it could not be read. Save stays greyed out until the check passes.",
          tip: "Changing the address does not change your tools. After you save, click Check for Updates to see what moved in the new file and choose what to apply.",
        },
        {
          text: "To change how the AI authenticates with this toolkit, expand the Authorization section, update the type and credentials, and click Save.",
        },
        {
          text: "Scroll to the tools list. Each tool shows its display name, with its internal name in grey next to it, plus badges for the HTTP method, risk level, and any safety flags.",
        },
        {
          text: "Use the search box above the list to find a tool by name, description, or endpoint path.",
        },
        {
          text: "To edit a single tool, click Edit on its row. The tool editor opens in place.",
        },
        {
          text: "In the tool editor, change any of these fields: Tool Name (internal), HTTP Method, Endpoint Path, Display Name, Description, Risk Level, and the Destructive, Bulk, and Requires Approval switches. You can also edit the description of each input parameter further down.",
        },
        {
          text: "Tool Name (internal) is the identifier the AI uses to call the tool. It may contain only letters, numbers, hyphens, and underscores, must be 64 characters or fewer, and must be unique within the toolkit — names that differ only in upper and lower case count as the same name. The same rules apply when you add a tool by hand. The Save button stays disabled until the name is valid.",
          tip: "Renaming the internal name is safe — when you later click Check for Updates on the toolkit, your custom name is kept. Only the tool's description and parameters are refreshed.",
        },
        {
          text: "HTTP Method and Endpoint Path set which API endpoint the tool calls. Pick the method from the list and enter a path that starts with /, for example /api/v1/shops.",
          tip: "Check for Updates matches tools by method and path. If you point a tool at a different endpoint, it no longer receives updates for its old one.",
        },
        {
          text: "Click Save Changes to apply your edits.",
        },
        {
          text: "To turn a tool off without deleting it, click Disable on its row. Click Enable to turn it back on. Only enabled tools are offered to the AI.",
        },
        {
          text: "To remove a tool for good, click Delete on its row. A confirmation appears warning that deletion is permanent — click Delete again to confirm.",
          tip: "Deleting a tool cannot be undone. If you only want to stop the AI from using it for now, use Disable instead.",
        },
        {
          text: "To rebuild a single tool from the latest API spec, click Regenerate in the tool editor. This refreshes its description and parameters while keeping your custom internal name.",
        },
        {
          text: "You do not need to rebuild anything after changing tools. Within a few seconds the AI picks up tools you add, edit, enable, disable, or delete — including changes applied with Check for Updates.",
          tip: "Regenerate context and capabilities on the toolkit page is only a fallback. Click it if the AI still does not use a tool a minute or so after you changed it.",
        },
      ],
      faqs: [
        {
          question:
            "What is the difference between the Tool Name (internal) and the Display Name?",
          answer:
            "The internal name is the technical identifier the AI uses to call the tool (for example get_team_member_working_weeks). The display name is a short, friendly label shown to people — in the chat and when a tool is cited as a source. Editing one does not change the other.",
        },
        {
          question: "How do I rename a tool?",
          answer:
            "Open Configure → Toolkits, select the toolkit, find the tool in the list, and click Edit. Change the Tool Name (internal) field at the top of the editor and click Save Changes. The name may contain only letters, numbers, hyphens, and underscores, must be 64 characters or fewer, and must be unique within that toolkit.",
        },
        {
          question: "Will renaming a tool break anything?",
          answer:
            "No. When you next click Check for Updates on the toolkit, the system matches tools by their API address (URL and method), not by name — so your renamed tool keeps its custom name and only its description and parameters are refreshed. One thing to note: messages already sent in past conversations keep the old name where the tool was cited; only new conversations use the new name.",
        },
        {
          question:
            'I got "a tool with this name already exists" — what does that mean?',
          answer:
            "Two tools in the same toolkit cannot share the same internal name, and names that differ only in upper and lower case (get_shops and Get_Shops) count as the same. Pick a different name. This can also happen when two endpoints were auto-named to the same value — renaming one of them to something unique resolves it.",
        },
        {
          question: "Why is the Save button greyed out in the tool editor?",
          answer:
            "Save is disabled while a field is invalid. The usual causes are an empty internal name, an internal name longer than 64 characters or containing spaces or other disallowed characters, a missing HTTP method, an endpoint path that is empty or does not start with /, or a description over the length limit. Fix the highlighted field and Save becomes available.",
        },
        {
          question: "Can I change a tool's HTTP method or endpoint path?",
          answer:
            "Yes. Open the tool editor, change HTTP Method or Endpoint Path, and click Save Changes. The path must start with /. Check for Updates matches tools by method and path, so after you change them the tool no longer receives updates for its old endpoint — if the endpoint simply moved in your API docs, Check for Updates is usually the better way to pick up the change.",
        },
        {
          question: "How do I delete a tool, and can I undo it?",
          answer:
            "Open Configure → Toolkits, select the toolkit, find the tool, and click the Delete button on its row. A confirmation popover appears warning that the action is permanent — click Delete again to confirm. Deletion cannot be undone: to bring the tool back you normally have to re-create it manually. Check for Updates will only restore a deleted tool if that endpoint actually changes in your API docs (a changed endpoint is re-created automatically) — an unchanged endpoint stays deleted. If you only want to stop the AI from using it temporarily, click Disable instead.",
        },
        {
          question:
            "What do the Destructive, Bulk, and Requires Approval flags do?",
          answer:
            "Destructive marks a tool that permanently changes or deletes data. Bulk marks a tool that affects many records at once. Requires Approval pauses execution so the user must confirm before the tool runs — useful for high-risk actions. You set all three in the tool editor. Tools whose HTTP method is DELETE are always created with High risk and both Destructive and Requires Approval on, whatever the AI suggested. Other tools start with the AI's suggestion, so review them and turn on Requires Approval where you want the user to confirm first.",
        },
        {
          question: "How do I rename the toolkit itself, not a tool?",
          answer:
            "Select the toolkit in Configure → Toolkits and click Edit on its details card. Change the Name (you can also update the Base URL, Specification URL and Description) and click Save.",
        },
        {
          question:
            'Check for Updates fails with "failed to load documentation" — what do I do?',
          answer:
            "Your provider has almost certainly moved or renamed the specification file, so the address saved with the toolkit no longer points at anything. Open the toolkit, click Edit, and paste the new address into the Specification URL field. It is checked as you type, and once it passes you can Save and run Check for Updates again.",
        },
        {
          question: "Where do I find the new address of my API specification?",
          answer:
            "It is usually linked from your provider's API documentation page — look for OpenAPI, Swagger, a .yaml/.json file, or a Download specification link. You can also paste the address of the documentation page itself: the system finds the specification behind it and saves that instead, and tells you when it has done so.",
        },
        {
          question: "My toolkit has no Specification URL — can I add one?",
          answer:
            "Yes. Open the toolkit, click Edit, paste the address of your Swagger or OpenAPI file into the Specification URL field, and click Save once the check passes. From then on, Check for Updates compares against the file as it was when you saved, so it only reports changes made after that moment.",
        },
        {
          question:
            "Can I switch a toolkit from Swagger to Readme documentation?",
          answer:
            "No. The Specification URL field changes the address only — the toolkit keeps the documentation format it was created with, and the new address is checked against that format. To move to a different format, create a new toolkit.",
        },
        {
          question:
            "The assistant sends wrong dates or values to my API — can I help it?",
          answer:
            'Yes, through the parameter descriptions. Before a call is sent, its arguments are checked against what your toolkit declares — required fields, allowed values, date and e-mail formats, minimum and maximum — and a relative date such as "last month" is turned into concrete dates using the visitor\'s time zone. A rejected call is not sent; the assistant sees what was wrong and retries with corrected arguments, never the same ones, and stops after three failed attempts and reports them. A clear description, the unit (for example "duration in hours") and a format on each parameter make the first attempt right more often. For a parameter that picks which fields come back (named fields or select), the field names your API documentation declares for the response are listed in its description automatically, so the assistant asks for real names on its first call; on an existing toolkit, click Check for Updates and apply the changes to get them. Every attempt is visible in Analytics → Tool Runs.',
        },
        {
          question: 'What happens when a tool keeps answering "unauthorised"?',
          answer:
            "After the first unauthorised reply the assistant stops retrying that tool in the same answer and tells the visitor the connection needs re-authorising, rather than repeating the failing call. A scheduled or automated run reports that the authorization for that toolkit expired and never deletes the stored connection.",
        },
      ],
    },
    {
      id: "chat-widget",
      title: "Setting Up the Chat Widget",
      description: "Customize and embed the chat widget on your website.",
      steps: [
        {
          text: "Go to Configure → User Interface and select your widget from the dropdown.",
        },
        {
          text: "Set the widget name and description (used internally to identify it in the admin panel).",
        },
        {
          text: "Set the primary color (hex code or color picker), upload a logo, write the greeting message, and choose the widget position (bottom-right, bottom-left, etc.).",
        },
        {
          text: "Choose the display type: Modal (centered overlay), Sidebar (slide in from side), Popup (small floating window), Panel (always visible), Custom Trigger — Sidebar (opened by your own code, fixed width), or Custom Trigger — Panel (opened by your own code, and the visitor can drag it wider).",
          tip: "Both Custom Trigger types use the same snippets and behave the same way for your code. Pick Panel when visitors read long answers, tables or charts in the chat and need more room than the fixed width gives them.",
        },
        {
          text: "If you picked Sidebar or either Custom Trigger type, choose a Display behaviour: Overlay (the chat floats above the page, classic behaviour) or Push (the page content is compressed to the left so the chat sits beside it instead of covering it).",
          tip: "Push works well for admin tools where you want the user to keep seeing the page while they chat. Overlay is the safer default for marketing sites.",
        },
        {
          text: "Under Button Appearance: set button size, margin from the edge, and optionally paste SVG code into the Custom SVG Icon field to replace the default chat bubble. Only the drawing is kept — scripts, event handlers and links in the SVG are removed when you save, and code that is not a valid SVG is rejected with a message.",
        },
        {
          text: "Under Feedback: enable the Feedback toggle to allow users to give thumbs-up/down ratings on AI responses.",
        },
        {
          text: "Under Advanced Colors: override theme colors separately for light mode and dark mode — primary color, sidebar, inputs, message bubbles, borders, and 10 chart color slots.",
        },
        {
          text: "Under Border Settings: set border radius, width, and style (Solid, Dashed, or Dotted) for the widget frame.",
        },
        {
          text: "Under Typography: set a custom Body font — and optionally a separate Heading font — by entering the font name and a Google Fonts link or a direct .woff2/.ttf URL. Leave them blank to keep the default font. The custom font applies to all widget and Customer Portal text; code blocks stay monospace.",
          tip: "Make sure you have a licence to use the font, and that its URL allows cross-origin loading (CORS). Self-hosting the font avoids sending visitor data to Google Fonts.",
        },
        {
          text: "Under Chat labels: type your own wording in Knowledge base badge to replace “Verified” on answers that come from your uploaded documents, and in Invite agent button to replace “Invite an agent” on the buttons visitors use to reach your team — for example “Speak to an Advisor”. Each label takes up to 40 characters. Leave a field empty to keep the built-in wording.",
        },
        {
          text: "Optionally set URL Visibility Rules — include or exclude specific pages where the widget appears.",
        },
        { text: "Go to Configure → Embed. Copy the script tag shown." },
        {
          text: "Paste the snippet before the closing </body> tag on your website.",
          tip: "Disable ad blockers during testing — they can hide the widget.",
        },
        { text: "Open the page and confirm the widget icon appears." },
        {
          text: "Optional — to mount the widget inside a section of your own page instead of floating it, stay on Configure → Embed and turn on Embed inside a container in the Inline embed block. Enter the CSS selector of the element you want the widget to fill (for example #strikeup-widget).",
          tip: "Give that container a height in your page's CSS — the inline widget fills its container, so with no height there is nothing to show.",
        },
        {
          text: "Optional — to let the Customer Portal open inside the same container (no fullscreen pop-out), also turn on Open Customer Portal inline. This applies only if the widget already has the Customer Portal enabled.",
        },
        {
          text: "Copy the generated inline snippet. It includes a placeholder container element plus the script — paste both where you want the chat to appear on your page.",
        },
        {
          text: 'Optional — if your display type is Custom Trigger, use the "Open with a placeholder AI message" block on Configure → Embed to set the message your button opens the chat with.',
        },
        {
          text: 'Optional — turn on the "Send it automatically" switch in that same block if the assistant should answer that message immediately, instead of waiting for the visitor to reply to it.',
          tip: "Automatic sending always opens a new chat, so use it for buttons that ask one fixed question — for example a Summarise this page button.",
        },
      ],
      faqs: [
        {
          question:
            "The embed code is not showing the widget — what do I check?",
          answer:
            "1) The script tag must be before </body>, not in <head>. 2) Open the browser console and look for Content Security Policy (CSP) errors — you may need to add the widget domain to your CSP allowlist. 3) Disable browser ad blockers during testing.",
        },
        {
          question:
            "What is the difference between Modal, Sidebar, Popup, Panel, and Custom Trigger display types?",
          answer:
            "Modal: a centered dialog that appears over the page. Sidebar: slides in from the right or left edge. Popup: a compact floating window anchored to the widget button. Panel: always visible inline with your page content, pushing the layout rather than overlaying it. Custom Trigger — Sidebar and Custom Trigger — Panel: no floating button — the widget opens when your own button or link calls the provided JavaScript snippet.",
        },
        {
          question: "What is the Custom Trigger display type?",
          answer:
            "With either Custom Trigger type, no floating button appears. Instead, Configure → Embed gives you five code snippets: open the widget, open it with a pre-filled message, open it with a developer context (hidden tech message for the AI), check whether the widget is currently open, and close it. Paste any of them into your own button or link on the page.",
        },
        {
          question:
            "What is the difference between Custom Trigger — Sidebar and Custom Trigger — Panel?",
          answer:
            "Only whether the visitor can resize the chat. Both open from your own button, both collapse with the arrow tab on the pane's edge, and both use exactly the same snippets — switching between them needs no change to your page. Custom Trigger — Sidebar keeps the width you set in Configure → User Interface. Custom Trigger — Panel adds a drag handle on the pane's left edge: the visitor can widen it up to 900 pixels or narrow it to 240, and their browser remembers that width for the next visit. Choose Panel when people read long answers, wide tables or charts in the chat. The remembered width belongs to that one visitor's browser — it is not a setting you control, and new visitors still start at the width you set in Configure → User Interface.",
        },
        {
          question: "How do I pre-fill a message when opening the widget?",
          answer:
            'Use the "Open with message" snippet from Configure → Embed. Edit the placeholder text in the snippet to set the message shown when the widget opens. By default that message appears as an assistant bubble the visitor replies to — no answer is generated until they send something. Turn on the "Send it automatically" switch above the snippet if you want the assistant to start answering that message straight away instead.',
        },
        {
          question:
            "Can the widget start answering immediately, without the visitor typing anything?",
          answer:
            'Yes. On Configure → Embed, under "Open with a placeholder AI message", turn on the "Send it automatically" switch. The snippet then opens a brand-new chat with your message already sent as if the visitor had written it, so the answer starts generating the moment the chat opens. Use it for buttons where the question is already decided — "Summarise this page", "Explain this invoice". Leave the switch off when you want the visitor to read a prompt and write their own question.',
        },
        {
          question:
            "What happens to the current conversation when a message is sent automatically?",
          answer:
            "It always starts a new chat. The conversation that was on screen is kept in the visitor's chat history and a fresh one opens with your message. If your widget asks visitors to sign in or authorize a connection first, the message waits and is sent as soon as they finish — never before.",
        },
        {
          question:
            "My custom button gets out of sync — how do I tell whether the widget is open or closed?",
          answer:
            "Use the \"Check if the widget is open\" snippet from Configure → Embed (visible when Display type is Custom Trigger). It calls window.StrikeUpChat.isOpen(), which returns true or false for the widget's real state. This matters because visitors can close the chat in ways your code never sees — the minimize arrow on the pane's edge, and the close button inside the chat itself. If you track only your own open and close calls, your button will believe the chat is still open after a visitor closes it that way. The snippet shows the recommended pattern: one button that checks isOpen() at the moment it is clicked, then opens or closes accordingly.",
        },
        {
          question:
            "Does the widget tell my page when the visitor opens or closes the chat?",
          answer:
            "No — there is no notification event. isOpen() is a check you run when you need the answer, not something that calls your code when the state changes. For a single button that opens and closes the chat, checking it at click time is all you need. If you want a button's appearance (a highlight, or a label that flips between Ask AI and Close assistant) to follow the widget continuously, check isOpen() on a short timer — every 200 milliseconds is more than enough and costs nothing.",
        },
        {
          question:
            "What does isOpen() return for display types other than Custom Trigger?",
          answer:
            "It works for every display type. For Popup, Modal, and Sidebar it returns true while the chat is visible and false once the floating button closes it. For Panel and Inline it returns true while the widget is on the page, because those display types are always open by design. (Custom Trigger — Panel is not one of those: like the other Custom Trigger type it starts closed and reports false until your code opens it.) It returns false before the widget has finished loading, after your code removes the widget, and on pages hidden by your URL Visibility Rules.",
        },
        {
          question:
            "What is inline embedding, and how is it different from a floating widget?",
          answer:
            "Inline embedding mounts the widget directly inside an element on your own page (a container you provide) instead of floating it over the page. There is no launcher button and no backdrop — the widget fills the container, and your page layout controls its size and position. Use it when you want the chat to live inside a section of your app, such as a support panel or a dashboard tile. A floating widget, by contrast, sits on top of your page anchored to a corner or edge.",
        },
        {
          question: "How do I embed the widget inline inside my page?",
          answer:
            "Go to Configure → Embed and select your widget. In the Inline embed block, turn on Embed inside a container and enter the CSS selector of the element you want the widget to fill (for example #strikeup-widget). Copy the generated snippet — it includes a placeholder container element plus the script tag — and paste both into your page where you want the chat to appear.",
        },
        {
          question: "My inline widget is not showing — what is wrong?",
          answer:
            "The most common cause is that the container has no height. The inline widget fills its container, so if the container's height resolves to nothing, the widget collapses and stays invisible. Give the container a real height in your page's CSS (a fixed height, a viewport height, or a flex/grid area all work). Also confirm the selector in your snippet matches the element's id or class exactly. If the selector matches nothing on the page, the widget does not load — it never falls back to floating.",
        },
        {
          question: "Can the Customer Portal open inline too?",
          answer:
            "Yes. In the Inline embed block on Configure → Embed, turn on Open Customer Portal inline. The Portal then opens inside the same container as the chat — no fullscreen modal pop-out. This applies only if the widget already has the Customer Portal enabled under Configure → User Interface → Customer Portal. If you leave this off, an inline chat widget shows no Configure gear icon and stays a pure chat surface.",
        },
        {
          question:
            "What is Display behaviour, and what is the difference between Overlay and Push?",
          answer:
            "Display behaviour controls what happens to your page when the chat appears. Overlay (default) keeps the chat floating above the page — the page does not move. Push compresses your page to the left so the chat sits beside it and nothing is hidden. Display behaviour applies only when Display type is Sidebar or one of the two Custom Trigger types; the other types ignore it.",
        },
        {
          question:
            "Can I tell the AI what page the user is on (without showing it to the user)?",
          answer:
            'Yes — there are two ways. For a one-shot hint tied to opening the widget, use the "Open with developer message" snippet from Configure → Embed (visible when Display type is Custom Trigger) and pass openContext alongside the user-facing message in window.StrikeUpChat.open({ message, openContext }). That hint is invisible to the user and affects the opening turn only (it is not saved and does not carry forward). The older name developerContext still works as a deprecated alias, but use openContext going forward. If instead you want the AI to keep knowing what page the user is on across several questions, use page context injection (window.StrikeUpChat.context.set(...)) — see the "Injecting page context into the widget" section, which persists in the conversation.',
        },
        {
          question: "Can a visitor stop an answer that is taking too long?",
          answer:
            "Yes. While the assistant is writing, the send button becomes a square stop button — clicking it (or pressing Esc) ends the answer at once. The part that was already written stays in the conversation as a normal message, so nothing is lost and the visitor can ask the next question straight away. Stopping also cancels the AI work behind the answer, so it stops consuming credits. No setup is needed — the stop button is always available.",
        },
        {
          question:
            "Can a visitor download a table the assistant shows in the chat?",
          answer:
            "Yes. Every table in an answer has a Download table as CSV button above it, so a visitor can open the figures in Excel, Numbers, or Google Sheets. This works both for tables the assistant writes into its reply and for the separate table card that accompanies a chart. The button appears once the answer has finished writing — while the answer is still arriving the table is incomplete, so no download is offered. Nothing needs to be switched on.",
        },
        {
          question: "Can I enable feedback (thumbs up/down) ratings?",
          answer:
            "Yes. Go to Configure → User Interface and enable the Feedback toggle. Users will see thumbs-up/down buttons on AI responses. Ratings appear in Analytics → Feedback.",
        },
        {
          question: "How do I show the widget only on certain pages?",
          answer:
            "Use URL Visibility Rules under Configure → User Interface. Add Include Patterns to show the widget only on matching URLs, or Exclusion Patterns to hide it on specific pages. Patterns support wildcards (e.g., /support/*).",
        },
        {
          question: "Can I embed the widget on multiple domains?",
          answer:
            "Yes. The same embed snippet works on any number of sites and pages.",
        },
        {
          question: "How do I regenerate the embed token?",
          answer:
            "Under Configure → Embed, click Regenerate Token. Caution: the old embed code stops working immediately — update all your website pages before regenerating.",
        },
        {
          question:
            "Can I update an existing widget to use a different toolkit, or do I need to create a new widget?",
          answer:
            "You can update it in place — no new widget needed. Go to Configure → Embed, select your widget, click Edit, then use the Toolkit toggles to swap in the new toolkit and turn off the old one. Click Save. If your embed script does not use Inject Authorization, no changes to your website code are needed. If your embed script includes injected credentials for the old toolkit, scroll down to the Inject Authorization section on the same page, enable the new toolkit's toggle, and re-copy the updated embed script to your website.",
        },
        {
          question: "Can a widget use more than one toolkit at the same time?",
          answer:
            "Yes. Each widget supports up to 5 toolkits simultaneously. Toggle each toolkit on or off in the Edit view under Configure → Embed. At least one toolkit must remain selected.",
        },
        {
          question:
            "Can I change the “Verified” badge on answers from my knowledge base?",
          answer:
            "Yes. Go to Configure → User Interface, select your widget, and type your own wording in Chat labels → Knowledge base badge — for example “Answered from Acme Help”. You can use up to 40 characters, and each widget has its own label. Click + Add language to show a different label to visitors whose browser uses that language; languages you do not add use your default label. Leave the field empty to go back to “Verified”, which visitors see in their own language. Answers based on a website page show that page's title instead, so this label does not change them.",
        },
        {
          question: "Can I change the “Invite an agent” button text?",
          answer:
            "Yes. Go to Configure → User Interface, select your widget, and type your own wording in Chat labels → Invite agent button — for example “Speak to an Advisor”. You can use up to 40 characters. Your wording replaces every button that brings a person from your team into the chat: “Invite an agent” beside the message box, “Invite again” after the AI has taken over again, and “Connect with agent” under an AI reply. Click + Add language to show different wording to visitors whose browser uses that language; languages you do not add use your default text. Leave the field empty to go back to the built-in wording, which visitors see in their own language.",
        },
        {
          question: "Can I use my own font in the widget?",
          answer:
            "Yes. Go to Configure → User Interface → Colours and styling → Typography. Enter your font's name and a font URL — either a Google Fonts link (e.g. https://fonts.googleapis.com/css2?family=Inter&display=swap) or a direct link to a .woff2/.woff/.ttf/.otf file you host. The Body font is used for all widget and Customer Portal text. You can optionally set a separate Heading font for titles — the widget title, chat names in the history list, the Customer Portal title and menu, and section headings. Leave the Heading font blank to use the Body font everywhere. Code blocks always stay in a monospace font.",
        },
        {
          question:
            "My custom font (or heading font) isn't showing — what should I check?",
          answer:
            "If you set a Heading font but the chat still looks the same, that is expected: the Heading font only applies to titles and headings (widget title, chat names, Customer Portal title and menu, section headers), not to chat messages — message text always uses the Body font, so set the Body font to change the conversation itself. If a font does not load at all, check three things: 1) the font name must exactly match the font's real family name (e.g. 'Inter', not 'inter-regular'); 2) the URL must be https and point to a Google Fonts stylesheet or a .woff2/.woff/.ttf/.otf file; 3) a self-hosted font's server must allow cross-origin requests (CORS), or the browser blocks it. Make sure you have a licence to use the font — self-hosting it with CORS also avoids sending visitor data to Google Fonts.",
        },
        {
          question:
            "The widget now asks visitors to sign in to one of my connected services — why?",
          answer:
            "Because that toolkit needs the visitor's own credentials — for example a personal API token, an OAuth sign-in or per-user header values. The authorization screen appears the first time a visitor uses the widget with such a toolkit and disappears once they have signed in. Toolkits with a fixed key you entered yourself never prompt. If the screen appeared out of nowhere, it was previously not showing at all and those calls were failing silently — nothing in your configuration changed.",
        },
        {
          question:
            "If a visitor reloads the page while an approval card is open, is the approval lost?",
          answer:
            'No. An open approval stays valid for two hours across reloads and reconnects. When the visitor decides, the answer continues with the whole conversation in view — persona, skills, earlier lookups. If something fails while approved actions are being processed, or the visitor presses Stop, the "Processing…" bubble clears and the composer unlocks; an approved action is never run twice.',
        },
        {
          question: "What does the notice that an answer was cut short mean?",
          answer:
            "The model reached its output length limit before it finished. The widget shows the notice so nobody mistakes a truncated table or list for a complete one. Ask the visitor to narrow the question, or in AI Gateway pick a model with a larger output limit for that stage.",
        },
        {
          question:
            "Where do I see which lookups the assistant made in an answer?",
          answer:
            'Open the steps of the answer. Each lookup appears as a line under "Executing tools…", named after the tool as you named it, and a lookup made after an approval joins the same list marked "approved by you". Research steps of a multi-step answer show their own lookups the same way.',
        },
        {
          question:
            "Does the assistant fetch every page of a long list, or stop after the first?",
          answer:
            'It fetches the pages up to a limit and works from one combined result, counting a record only once when pages overlap. If the limit is reached, the answer says how many of the total it fetched. A page-size request such as "in pages of 10" no longer stops after the first page.',
        },
        {
          question:
            "The assistant skipped a step and said there was nothing to act on — is that a fault?",
          answer:
            'No. When a later step depends on records an earlier step did not find — "find the tickets about X and close them" with no such ticket — the assistant skips the dependent step and says so instead of asking for approval of an empty action. A step is also skipped, with the reason, when the step it depended on failed.',
        },
        {
          question:
            'Can a visitor answer "yes" or "go ahead" and have the assistant carry on with what it proposed?',
          answer:
            "Yes. A short confirmation continues the proposed task with the same care as the original question; the assistant does not treat it as a new, trivial message.",
        },
      ],
    },
    {
      id: "agent-configuration",
      title: "Configuring AI Persona",
      description:
        "Set how your AI behaves — tone, personality, and branding instructions.",
      steps: [
        {
          text: "Go to Configure → AI & UX and select your widget from the dropdown at the top.",
        },
        {
          text: 'In the Persona section, fill in Tone of Voice (e.g., "professional and friendly"), Behavior Instructions, and Branding Instructions.',
        },
        { text: "Click Save Persona." },
      ],
      faqs: [
        {
          question:
            "Can I change the dialect — for example, British English instead of American English?",
          answer:
            'Yes. By default the AI responds in the language and dialect the user writes in. To always use a specific dialect regardless of how users write, add an instruction to the Persona field on the AI & UX page — for example: "Always respond in British English."',
        },
        {
          question:
            "I clicked Save and my Behavior Instructions were refused — why?",
          answer:
            "Every instruction is checked before it is stored. The save is refused, and the message names the reason, when the text tries to change who the assistant is, relax approval or guardrail rules, address the platform itself rather than describe the persona, deceive your users, or when it contains credentials, links, encoded text or script. Reword it as guidance about tone and workflow and save again. The same check applies to skill prompts, customer-company instructions and Account Memory.",
        },
        {
          question:
            "Do I need to enable the Support, Copilot or Analytics agents?",
          answer:
            "No. Every widget runs all four agents — General, Support, Copilot and Analytics — all the time. For each question the assistant picks the agent whose role fits best and falls back to General when the intent is mixed or unclear. There is no on/off switch: edit each agent's instructions and policies on the AI & UX page and they apply whenever that agent is chosen.",
        },
        {
          question: "I renamed an agent — will questions still reach it?",
          answer:
            "Yes. The name is a label for you; the assistant routes questions by the agent's role (Support, Copilot, Analytics or General), so a renamed agent keeps its instructions, knowledge-base policy and tool policy.",
        },
        {
          question:
            "Can a customer company's own instruction override my persona, approvals or guardrails?",
          answer:
            "No. Instructions written by a customer company's admin, and the extra persona fields, only adjust tone and workflow. They never relax your Actions & Permissions, guardrails, citation rules or approval rules, and the assistant is told so explicitly.",
        },
        {
          question:
            "Does the assistant know today's date and the visitor's time zone?",
          answer:
            'Yes. The widget passes the visitor\'s time zone, language and local time from their browser with every message, so "this week" or "yesterday" resolve correctly and dates in answers use the visitor\'s zone. A scheduled workflow uses the time zone of its schedule; a run with no visitor and no schedule works in UTC and says so in its answer.',
        },
        {
          question: "Where did the widget's Default Instructions box go?",
          answer:
            "It was removed. That box was never read by the assistant, so nothing changed in how your widget answers. Put persona guidance in the agent's Behavior Instructions on the AI & UX page.",
        },
      ],
    },
    {
      id: "ai-skills",
      title: "Configuring AI Skills",
      description:
        "Skills are specialist modules that activate when users ask about specific topics. You can create and edit them with AI assistance or fill in every field yourself.",
      steps: [
        {
          text: "Go to Configure → Skills. You will see two buttons: Create with AI and Create Manually.",
          tip: "Use Create with AI when you want to describe the skill in plain language. Use Create Manually when you already know exactly what each field should say.",
        },
        {
          text: "To find a skill in a long list, type in the Search skills box above the list. It matches the skill name, category, trigger context and description.",
        },
        {
          text: "If someone else changed skills while you had the page open, click Refresh to reload the list.",
        },
        {
          text: 'To use AI: click Create with AI, then describe what you want the skill to do in plain language. Example: "Help users check their order status and answer shipping questions."',
        },
        {
          text: "The AI will ask clarifying questions. Answer them — this improves the generated skill. When it is ready, a green Create Skill button appears. Click it.",
        },
        {
          text: "To do it yourself: click Create Manually to open a form, then fill in the Name, Description, Trigger Context, and Agent Instructions fields. Category is optional.",
        },
        {
          text: "In the manual form, Agent Instructions is the exact text the AI follows when the skill is active. Write clear, specific instructions. Click Create Skill to save.",
        },
        {
          text: "To change a skill later, open it and click Edit. At the top of the editor, use the Editing mode toggle to choose Manual or AI Assistant.",
        },
        {
          text: "In Manual mode you edit every field directly — including Trigger Context and Agent Instructions — and click Save Changes. No AI is involved.",
          tip: "If the skill activates at the wrong time, switch to Manual mode and edit the Trigger Context to be more specific.",
        },
        {
          text: 'In AI Assistant mode you update the Description or type a Change Request (e.g., "also handle returns"), then click Save Changes to let the AI regenerate the instructions.',
        },
        {
          text: 'Optionally assign the skill a Category tag (e.g., "Billing", "Support") to organize your skills list.',
        },
        {
          text: "Once satisfied, go to Configure → AI & UX, select your widget, and toggle the skill on in the Skills section.",
        },
        {
          text: "Drag skills in the list to reorder priority — skills higher in the list take precedence.",
        },
        {
          text: "To delete a skill, open it and click the Delete button next to Edit. A confirmation appears warning that deletion is permanent — click Delete again to confirm.",
          tip: "Deleting a skill is irreversible and also removes it from every agent it was assigned to. To temporarily disable a skill instead, turn it off for the widget under Configure → AI & UX.",
        },
      ],
      faqs: [
        {
          question: 'What is a "trigger context"?',
          answer:
            'The description of when the skill activates (e.g., "when a user asks about billing or payment issues"). The AI uses it to decide which skill to apply. In AI Assistant mode it is generated for you; in Manual mode you write it yourself.',
        },
        {
          question: "How do I delete a skill, and can I undo it?",
          answer:
            "Open the skill and click the Delete button next to Edit. A confirmation popover appears warning that the action is permanent — click Delete again to confirm. Deletion cannot be undone and also removes the skill from every agent it was assigned to. If you only want to stop using it for now, turn the skill off for the widget under Configure → AI & UX instead.",
        },
        {
          question: "Can I create a skill without using AI?",
          answer:
            "Yes. Click Create Manually on the Skills page to open a form where you fill in every field yourself — Name, Description, Trigger Context, and Agent Instructions. Nothing is sent to the AI; the skill is saved exactly as you typed it.",
        },
        {
          question: "Can I edit the trigger context manually?",
          answer:
            "Yes. Open the skill, click Edit, and set the Editing mode toggle to Manual. You can then edit the Trigger Context (and every other field, including Agent Instructions) directly and click Save Changes. In AI Assistant mode it stays read-only and is regenerated from your Description.",
        },
        {
          question:
            "What is the difference between Manual and AI Assistant editing?",
          answer:
            "Manual mode lets you edit every field directly and saves exactly what you type — no AI involved. AI Assistant mode regenerates the Trigger Context and Agent Instructions from your Description and an optional Change Request. Use Manual for full control; use AI Assistant when you want help rewriting.",
        },
        {
          question:
            "How do I refine a skill without rebuilding it from scratch?",
          answer:
            'Open the skill and click Edit. In AI Assistant mode, use the Change Request field to describe the refinement (e.g., "also handle return requests, not just order status") and click Save Changes. Or switch to Manual mode and edit the fields directly.',
        },
        {
          question: "Can I organize skills into categories?",
          answer:
            'Yes. When creating or editing a skill, assign it a Category tag (e.g., "Billing", "Support", "Onboarding"). Categories are free-form labels used to organize the skills list — they do not affect how the AI uses them.',
        },
        {
          question: "How many skills can I assign to one widget?",
          answer:
            "There is no hard limit. However, keep each skill focused on one topic — many overlapping skills can confuse the AI about which one to use.",
        },
        {
          question:
            "What is the difference between a skill and Behavior Instructions?",
          answer:
            "Behavior Instructions (set in the Persona section) define the AI's default personality and rules. A skill is a specialist module that activates for specific topics — think of skills as expertise cards the AI can pull out on demand.",
        },
        {
          question: "What's the difference between a guardrail and a skill?",
          answer:
            "A skill tells the AI what to do (activate specific behaviors). A guardrail tells the AI what it must never do (block or flag policy violations).",
        },
        {
          question:
            "My skill prompt was refused on save — what does the message mean?",
          answer:
            "Skill prompts and trigger contexts pass the same check as persona instructions. Text that tries to change who the assistant is, relax approvals or guardrails, deceive users, or that contains credentials, links, encoded text or script is refused, and the message states the reason. Describe what the skill should do and how it should answer, then save again.",
        },
        {
          question:
            "In a multi-step answer, which steps does a skill apply to?",
          answer:
            "The steps whose task matches the skill's trigger context. If a skill matches no particular step, or the answer has a single step, it applies to the whole answer. A skill the assistant picked for the question is never dropped along the way.",
        },
      ],
    },
    {
      id: "knowledge-base",
      title: "Building a Knowledge Base",
      description:
        "Upload documents or crawl websites so the AI can answer questions from your content.",
      steps: [
        {
          text: "Go to Configure → Knowledge Base (requires a plan that includes this feature).",
        },
        {
          text: "To create a folder: click New Folder, enter a name, and click Create. Use folders to organize content by topic or audience.",
        },
        {
          text: "To upload a document: click Add Document → select a file → optionally assign it to a folder → click Upload.",
        },
        {
          text: "To add a website: click Add Web Source → enter a name and one or more starting URLs → configure crawl settings (max pages, rate limit, scope) → click Save.",
        },
        {
          text: "To check how much storage you have left: look at the Storage used card at the top of the page. It shows the space used out of your limit, split into documents and web pages. It turns yellow when almost full and red when full.",
          tip: "Every company has a knowledge base storage limit — 10 MB by default — shared by uploaded documents and crawled web pages. A file that does not fit is refused before it uploads, with a message saying how much space is left.",
        },
        {
          text: "Wait for the status badge to change from Processing (yellow) to Ready (green).",
          tip: "Most documents take under 2 minutes; large files or websites can take up to 10 minutes. A 500-page crawl can take around 25 minutes at the default rate limit.",
        },
        {
          text: "To check what a crawl actually captured: click View Pages on the web source row. Every page the crawler found is listed with its status and, where it was not added, the reason.",
        },
        {
          text: "To refresh a single page without re-crawling the whole site: open View Pages and click the refresh button on that page's row.",
        },
        {
          text: "To stop a page from being used in answers: open View Pages and click the block icon on that page's row.",
          tip: "To exclude several pages at once, tick their checkboxes and click Exclude in the bar above the list. Excluded pages stay out every time you sync again.",
        },
        {
          text: "To filter documents: use the Status filter (All / Processing / Ready / Error) and the search box above the document list.",
        },
        {
          text: "To see only items not assigned to any folder, click Uncategorized in the left sidebar.",
        },
        {
          text: "To act on multiple items at once: tick the checkboxes on rows to select them, or use the checkbox in the column header to select all visible items. A bulk action bar appears above the table — use it to delete, move to a folder, or toggle Global for all selected items.",
        },
        {
          text: "Click Test Retrieval in the top right, type a sample question, and press Ctrl+Enter. Verify the AI returns the right content chunks with high relevance scores.",
        },
        {
          text: "To assign this knowledge to a specific widget only, see the FAQ below.",
        },
      ],
      faqs: [
        {
          question: "What file types can I upload?",
          answer:
            "PDF, TXT, Markdown (.md), JSON, JSONL, and Word documents (.doc, .docx). A single file can be up to 100 MB, but it must also fit in your remaining knowledge base storage (10 MB in total by default).",
        },
        {
          question:
            'What is the "Global" toggle on a document, web source, or folder?',
          answer:
            "Global means that item is automatically included in every widget's knowledge base. Non-global items must be explicitly assigned to each widget. Use Global for content every widget should always know (company policies, product catalog). Use non-global for content that only certain widgets should access (e.g., internal HR docs for the employee support widget only).",
        },
        {
          question: "How do I enable the Knowledge Base for only one widget?",
          answer:
            "Leave the Global toggle off on your documents. Then go to Configure → Embed, select your widget, click Edit, and use the KB tree picker to select exactly which folders, documents, or web sources that widget should use.",
        },
        {
          question: "A document shows an Error status — what do I do?",
          answer:
            "Click the document row to see the error message. Common causes: unsupported file format, or a corrupted file. Delete it and re-upload a corrected version.",
        },
        {
          question: "Can I search for a specific document by name?",
          answer:
            "Yes. Use the search box above the document list — it filters by document name in real time.",
        },
        {
          question: "How do I rename or delete a folder?",
          answer:
            "Use the three-dot menu on the folder name in the left panel. Options include Rename, Delete, and Move. Deleting a folder does not delete the documents inside it — they move to the root level.",
        },
        {
          question: "Why would I use folders?",
          answer:
            'Folders let you organize your KB by topic or audience (e.g., "Product Docs", "Legal", "HR"). You can make an entire folder global or assign a whole folder to a specific widget in one click instead of selecting items individually.',
        },
        {
          question: "What is the Uncategorized filter?",
          answer:
            "Uncategorized shows all documents and web sources that are not assigned to any folder. Click Uncategorized in the left sidebar to see them. This is useful for finding items you haven't organized yet and moving them into the right folder.",
        },
        {
          question:
            "How do I select and manage multiple documents or web sources at once?",
          answer:
            "Tick the checkboxes on the rows you want to select, or use the checkbox in the column header to select all visible items. When at least one item is selected, a bulk action bar appears above the table. From there you can delete all selected items, move them to a folder, or toggle the Global setting for all of them in one action.",
        },
        {
          question:
            "My document says Ready but the AI doesn't seem to use it — why?",
          answer:
            "Use the Test Retrieval tool and enter a question the way your users would phrase it. If no chunks are returned, the content may not match the phrasing. Try rephrasing the test query. Also confirm the document is assigned to the correct widget.",
        },
        {
          question: "What do the web source crawl settings mean?",
          answer:
            "Seed URLs: the starting page(s) for the crawler. Max Pages: how many pages to crawl (1–500). Rate Limit: requests per second (lower is gentler on the target site). Scope: HOST_ONLY crawls only the exact domain; SUBDOMAINS also crawls sub.example.com when your seed is example.com. Inclusion/Exclusion Filters: regex patterns to include only certain URLs or skip others.",
        },
        {
          question:
            "The assistant cited a web page — will the link work for my customers?",
          answer:
            "Yes. Source links point to the normal page address on your site, the one a visitor would browse. Behind the scenes the crawler may download a different file format, but that is never shown to anyone.",
        },
        {
          question: "Can I re-crawl a website after it updates?",
          answer:
            "Yes. Click the Sync (refresh) button on any web source row to trigger a new crawl immediately.",
        },
        {
          question:
            "How do I see exactly which pages were added to the knowledge base?",
          answer:
            "Click the View Pages button on any web source row. You will see every page address the crawler found, whether it was added, and — if it was not added — the reason why. Indexed means the page is in your knowledge base and the AI can answer from it. Skipped means the page was reached but not added, with the reason shown next to it. Failed means the page could not be opened at all. Syncing means you asked for that page to be refreshed and it is in progress. Excluded means you removed the page from your knowledge base yourself.",
        },
        {
          question: "Why is a page I expected missing from the knowledge base?",
          answer:
            "Open View Pages and look for it in the list. The most common reason is Max Pages reached, which means the crawler hit your page limit before getting to it — raise Max Pages and sync again. Other reasons are Too little content (the page was almost empty), Duplicate of another page, Skipped because over storage limit (your knowledge base storage was full — free space and sync again), or an HTTP error such as 404. If the page is not listed at all, check your Exclusion Filters — pages blocked by your own filters do not appear in this list.",
        },
        {
          question: "Can I refresh just one page instead of the whole site?",
          answer:
            "Yes. Open View Pages, find the page, and click the refresh button on its row. Only that page is fetched and updated, which is much faster than re-crawling the whole site. This also works on pages marked Max Pages reached — refreshing one of those pulls just that page into your knowledge base without re-crawling everything.",
        },
        {
          question: "Can I remove pages I do not want from the knowledge base?",
          answer:
            "Yes. Open View Pages and click the block icon on the page's row. To remove several at once, tick their checkboxes and click Exclude in the bar above the list. The page changes to Excluded and stops being used in answers within a few minutes. It stays excluded every time you sync again, but the crawler still follows its links, so the pages it links to are not affected. To undo, click the undo icon on the row (or select the pages and click Include again): the page is fetched again and returns to your knowledge base. Excluding works page by page. To keep a whole group of addresses out — for example every page under /release-notes/tags/ — add an Exclusion Filter to the web source instead, so the crawler never visits them.",
        },
        {
          question: "How many pages can one web source crawl?",
          answer:
            "Up to 500. Set the limit with Max Pages when you create or edit a web source. Larger crawls take longer — at the default rate limit, 500 pages takes roughly 25 minutes.",
        },
        {
          question: 'Why did a sync fail with "takes too long to check a URL"?',
          answer:
            "One of your Inclusion or Exclusion Filters is a pattern so complex that checking a single page address took longer than the crawler allows, so the sync stopped. The message under the web source names the filter. Edit the web source, simplify or remove that filter, and sync again. Simple patterns such as /docs/ or \\.pdf$ are never affected. Your knowledge base keeps the pages from the last successful sync until then.",
        },
        {
          question: "How many documents can I upload?",
          answer:
            "As many as fit in your knowledge base storage. Every company has a storage limit — 10 MB by default — shared by uploaded documents and crawled web pages. The Storage used card at the top of the Knowledge Base page shows how much you have left.",
        },
        {
          question: "What happens when my knowledge base storage is full?",
          answer:
            "New uploads and new crawls are refused until you free space. A file that does not fit is refused before it uploads, with a message saying how much space is left. Adding or syncing a web source is refused while storage is full. If a crawl reaches the limit partway through, the pages already stored stay in your knowledge base, and every remaining page is listed in View Pages as Skipped because over storage limit. To free space, delete documents or web sources you no longer need.",
        },
        {
          question: "How do I get more knowledge base storage?",
          answer:
            "Contact support. The StrikeUp team can raise the storage limit for your company. Until then, you can free space by deleting documents or web sources you no longer need.",
        },
        {
          question:
            "How many searches does the assistant run against the knowledge base for one question?",
          answer:
            "Up to three, each phrased for a different aspect of the question, with the results merged and duplicates removed. A plain question still gets a single search. The assistant skips the knowledge base only when it is confident the answer needs no documents and no tool, chart or table — and an agent's knowledge-base policy on the AI & UX page always wins over that judgement.",
        },
        {
          question:
            "Can text inside a document trigger buttons, a handoff or change the assistant's instructions?",
          answer:
            "No. Knowledge-base content is handed to the assistant as data, not instructions. Follow-up buttons and the human-handoff prompt are honoured only when the assistant itself offers them at the end of a reply; the same wording inside a document is shown as plain text.",
        },
      ],
    },
    {
      id: "guardrails",
      title: "Guardrails & Safety",
      description:
        "Define content rules the AI must never violate, or generate a tailored policy automatically from your company website. Test rules before going live, customize the rejection message users see, and see who changed the policy — and restore an earlier version — from the change history.",
      steps: [
        { text: "Go to Security → Guardrails." },
        {
          text: "Quickest start: scroll to Domain-aware Guardrails at the bottom of the page and enter your company website (e.g., https://example.com).",
        },
        {
          text: "Click Save next to the website field to store it on your account.",
        },
        {
          text: "Click Regenerate from website. Confirm in the dialog that appears — this will replace your current Company Policy text.",
          tip: "The Regenerate from website button stays disabled until both a Company Website is set and at least one active toolkit exists. Helper text under the button tells you which is missing.",
        },
        {
          text: "After a few seconds you will see a success message. Scroll up — the Company Policy text area is now filled with the generated rules.",
        },
        {
          text: "Or write the policy yourself in the Company Policy text area. Use plain English. Each rule should state what the AI must not do and what it should say instead.",
        },
        {
          text: "Click Save Policy. The system validates your policy and confirms when it is saved. If the text tries to override safety rules, the error highlights the exact phrase to remove.",
          tip: "Saving an empty policy deactivates your company rules. Click Clear to empty the text area, then click Save Policy to remove the policy entirely.",
        },
        {
          text: "Use the Test Guardrails panel to check your rules without sending real user messages. Type any message and click Send.",
        },
        {
          text: "To reproduce how the widget judges a reply that depends on earlier messages, expand Conversation context and click Add turn. Choose User or Assistant for each turn and type what was said before your test message.",
        },
        {
          text: "Review the result: a green box means the message is allowed; a red box shows the blocking reason and specific violations.",
        },
        {
          text: "To customize the rejection message widget users see when blocked, scroll to Blocked Message Response and enter your custom text.",
        },
        {
          text: "Click Save in the Blocked Message Response section. Leave the field empty to use the system default message.",
        },
        {
          text: "To see earlier versions of your policy, scroll to Change history at the bottom of the page. Each row shows the date, who made the change, the type of change and the version number.",
          tip: "Change history starts from the first save after this feature was introduced. Changes made before that are not listed.",
        },
        {
          text: "Click View on a row to see what changed in that version — removed text is highlighted in red and added text in green — followed by the full policy text of that version.",
        },
        {
          text: "To restore that version, click Load into editor. The text is copied into the Company Policy text area — nothing is saved yet.",
        },
        {
          text: "Review the text, then click Save Company Policy to make it your active policy again.",
        },
        {
          text: "Go to Security → Policy Violations to see a log of every message that triggered a guardrail in production.",
        },
      ],
      faqs: [
        {
          question: "How do I write a guardrail rule?",
          answer:
            'Write it as a direct instruction. Examples: "Never discuss competitor pricing or products. If asked, politely say you can only discuss our own offerings." or "Do not provide medical advice. Direct users to consult a licensed professional." Plain sentences work well — no special syntax needed.',
        },
        {
          question:
            "How do I test my guardrails without sending real user messages?",
          answer:
            "Use the Test Guardrails panel on the Guardrails page. Type any message and click Send. You will see whether the message is allowed or blocked, with the specific reason and any violations listed. Test runs are not logged and do not create policy violation records.",
        },
        {
          question:
            "A message passes the test but gets blocked in the live widget — why?",
          answer:
            "The widget considers the recent conversation when judging a message, while a plain test checks the message on its own. A short reply or a message that builds on an earlier one can be judged differently in context. Expand Conversation context in the Test Guardrails panel and add the earlier turns to reproduce exactly what the widget saw.",
        },
        {
          question: "How do I remove the company policy?",
          answer:
            "Click Clear in the Company Policy section to empty the text area, then click Save Policy. Saving an empty policy deactivates your company rules — the system default safety rules remain active.",
        },
        {
          question:
            "Can I customize the rejection message users see when blocked?",
          answer:
            "Yes. Scroll to the Blocked Message Response section, enter your custom text, and click Save. Leave the field empty to revert to the system default rejection message.",
        },
        {
          question: "What happens when a guardrail is triggered?",
          answer:
            "The AI blocks the response and sends the rejection message to the user — either your custom Blocked Message Response text or the system default if none is set. The full exchange is logged in Policy Violations with the user's original message and the policy version that flagged it.",
        },
        {
          question: "Can I set different guardrails for different widgets?",
          answer:
            "Currently the Company Policy applies to all widgets. For widget-specific restrictions, use a skill with restrictive instructions on that widget.",
        },
        {
          question:
            "The guardrail is blocking too many normal messages — how do I fix it?",
          answer:
            'Use the Test Guardrails panel to quickly try different messages and see exactly what is being blocked and why. Review Policy Violations for production traffic. Rewrite the relevant rule to be more specific — vague rules like "avoid sensitive topics" tend to over-trigger.',
        },
        {
          question: "Is there a policy version history?",
          answer:
            "Yes. The Change history card at the bottom of the Guardrails page lists every saved version with the date, who saved it, the type of change and the version number. Click View to read the full text of any version. The version number is also recorded on every Policy Violation entry, so you can match a violation to the policy that flagged it.",
        },
        {
          question:
            "Someone deleted or overwrote our policy by mistake — can I get it back?",
          answer:
            "Yes, if the change was saved after change history was introduced. Open Change history, click View on the version you want, click Load into editor, and then click Save Company Policy. A cleared policy appears as a Cleared row that still holds the text that was removed.",
        },
        {
          question: "Why is the Regenerate from website button disabled?",
          answer:
            "The button enables only when your account has both a Company Website saved and at least one active toolkit. Helper text under the button tells you which one is missing. Save a website in the Domain-aware Guardrails card and make sure you have at least one toolkit, then the button becomes available.",
        },
        {
          question: "What does Regenerate from website actually do?",
          answer:
            "It writes a new Company Policy tailored to your business by combining what your website says about you with what your toolkits can do. The result fills the Company Policy text area so you can review and edit it. Each run replaces the previous policy text — the earlier version stays in Change history, so you can load it back if you prefer it.",
        },
        {
          question:
            "The Save Policy button rejected my edit — what does the error mean?",
          answer:
            "The system blocks policy text that tries to override the platform's safety rules — for example, instructions telling the assistant to ignore safety, reveal hidden information, or perform a role hijack. When this happens, the error shows the exact phrase that triggered the rejection. Remove or reword that phrase and click Save Policy again. Normal business rules — restricting topics, expanding allowed topics, adjusting tone — are always accepted.",
        },
        {
          question:
            "My website is different from my API or product URL — which one should I enter?",
          answer:
            "Use the URL where your end users actually go to learn about your product or company. The system uses it to understand what your business does, so the more recognizable the URL the better the generated policy will be. You can change it any time and click Regenerate.",
        },
        {
          question:
            "Is it possible to set different violation responses for different violations?",
          answer:
            'Not via the Blocked Message Response field — that is a single fallback message applied to all violations equally. To give a different response for a specific violation, include the desired reply text in the guardrail rule itself. For example: "Never discuss competitor pricing. If asked, say: I can only discuss our own products and pricing." The AI follows the instruction in the rule before falling back to the Blocked Message Response.',
        },
        {
          question:
            "Can a visitor get around the guardrails by clicking a follow-up button?",
          answer:
            "No. Only follow-up buttons that the assistant itself offered at the end of its reply are treated as trusted choices. Wording that merely appears inside a quoted document, ticket or API reply is never turned into a button, so it cannot be clicked to skip a check.",
        },
      ],
    },
    {
      id: "data-privacy",
      title: "Data Privacy / Masking",
      description:
        "Prevent sensitive data in API responses from ever reaching the AI.",
      steps: [
        { text: "Go to Security → Data Privacy." },
        { text: "Click Add Rule." },
        {
          text: "Fill in: Name (a label for the rule), Field Path (the JSON path to the sensitive field, e.g. user.email), Scope (Global / Server / ServerTool), Keep First / Keep Last (characters to leave visible at each end), Masking Symbol (default *), and Priority (0–100, higher runs first).",
        },
        {
          text: "Click Save. The rule takes effect immediately on all new requests.",
        },
        {
          text: "To verify, open the Test Masking tool, paste a sample JSON payload, and click Test. You will see the original and masked versions side by side.",
          tip: "Check the AI Suggestions section at the top — the system auto-detects sensitive fields and suggests rules for you.",
        },
      ],
      faqs: [
        {
          question: "Are masking rules added automatically?",
          answer:
            "Yes. The system analyzes your connected APIs and suggests masking rules for fields it detects as potentially sensitive (passwords, tokens, card numbers, SSNs, etc.). These appear in the AI Suggestions section at the top of the Data Privacy page, grouped by category. Review each suggestion and click Approve to activate it, or Dismiss to ignore it. You can also Approve All or Dismiss All in one click.",
        },
        {
          question:
            "What field categories does the AI suggestion system detect?",
          answer:
            "Passwords & Secrets, API Keys & Tokens, Payment & Financial (card numbers, IBANs, CVVs), Identity Documents (SSN, passport, driver's license), Contact Information (email, phone), Personal Information (name, address, date of birth), and Network (IP addresses).",
        },
        {
          question: "What is the Field Path?",
          answer:
            'It\'s a dot-notation JSON path pointing to the sensitive field in your API\'s response. For example, if your API returns { "user": { "email": "..." } }, the field path is user.email. For arrays, use the field name without indices.',
        },
        {
          question:
            "What is the difference between Global, Server, and ServerTool scope?",
          answer:
            "Global applies to all API traffic regardless of which server or tool is called — use this for fields that appear in many APIs (e.g., user emails). Server applies only to one specific API server. ServerTool applies only to one specific endpoint within one server — the most granular option.",
        },
        {
          question: "Can I temporarily disable a rule without deleting it?",
          answer:
            "Yes. Open the Edit Rule modal and use the Active/Inactive toggle. The rule stays configured but stops being applied until you re-enable it.",
        },
        {
          question: "How does Keep First / Keep Last work?",
          answer:
            "Example: email roman.v@gmail.com with Keep First = 2, Keep Last = 2, symbol = * becomes ro*******om. The first 2 and last 2 characters remain visible; everything in between is replaced with the masking symbol.",
        },
        {
          question: "Will the AI ever see the unmasked value?",
          answer:
            "No. Masking is applied to the API response before it is passed to the AI. The AI only ever sees the masked version.",
        },
      ],
    },
    {
      id: "tool-policies",
      title: "Tool Policies",
      description:
        "Control which API tools are available per customer or subscription tier.",
      steps: [
        { text: "Go to Security → Tool Policies and click Create Policy." },
        { text: 'Enter a name (e.g., "Free Plan") and optional description.' },
        {
          text: 'Enter a Key (e.g., "free_plan"), using lowercase letters, numbers and underscores only.',
        },
        { text: "Click Manage Mappings to open the tool assignment view." },
        {
          text: "Select the server, then check the specific tools that this policy should allow.",
        },
        { text: "Repeat for each server whose tools you want to include." },
        {
          text: "Ask your developers to add the policy keys each customer should have to the login ticket your system creates when it signs that customer in to the chat.",
          tip: "Only keys in the login ticket count. Keys set by a script on your web page are ignored, and a customer who signs in any other way can use every active tool.",
        },
        {
          text: "Use the Policy Preview page (Security → Tool Policies → Preview) to simulate exactly which tools a specific widget + policy combination can access.",
          tip: "Use Compare mode in Preview to diff two policy scenarios side by side before deploying.",
        },
      ],
      faqs: [
        {
          question: 'What is a "tool policy" and why do I need it?',
          answer:
            "A tool policy controls which API tools a customer can use. This lets you offer different capability tiers — for example, Free customers can only use the search tool, while Pro customers can also use the booking and payment tools.",
        },
        {
          question: "What is the Policy Preview tool?",
          answer:
            "It lets you simulate what a specific widget + policy combination looks like before deploying. Select a widget and optional policies, and the preview shows you the exact list of tools available with their descriptions. Use Compare mode to diff two scenarios side by side.",
        },
        {
          question: "Can a policy include tools from multiple servers?",
          answer:
            "Yes. Add a server mapping for each API server whose tools you want to include in the policy.",
        },
        {
          question: "How does a customer get a policy?",
          answer:
            "Your system gives it to them when it signs them in. Your developers add the policy keys to the customer's login ticket, and each new ticket replaces the keys from the last one. Keys that don't match an active policy give no tools. Keys set from the web page itself are ignored, so a visitor can't give themselves extra tools.",
        },
        {
          question: "What happens if no policy is assigned?",
          answer:
            "The customer can use all enabled tools from the widget's connected servers — no restriction. The same applies when the login ticket has no policy keys, and to anyone who opens the chat without being signed in by your system.",
        },
        {
          question: "What happens when I deactivate or rename a policy?",
          answer:
            "Deactivating a policy removes its tools from customers on their next message, and reactivating it brings them back without selecting the tools again. Renaming a policy's key keeps its tools, but customers who still hold the old key get none of them until your system signs them in with the new key.",
        },
        {
          question:
            "What is the difference between disabling a tool in the server and putting it in a policy?",
          answer:
            "Disabling a tool in the server removes it from all widgets globally. A policy restricts access per customer while leaving the tool enabled globally.",
        },
      ],
    },
    {
      id: "dashboard",
      title: "Dashboard",
      description:
        "Your at-a-glance view of activity, usage, performance and AI spend — split into four pages, all driven by one shared date range.",
      steps: [
        {
          text: "Open Dashboard in the sidebar and choose one of its four pages: Overview, Usage, Performance or AI Spend.",
        },
        {
          text: "Overview shows your headline numbers: active sessions, resolution rate, negative-feedback share, at-risk customers, plus message activity and the engagement funnel.",
        },
        {
          text: "Usage shows how much your assistant is being used: daily active users, message volume, your busiest endpoints, and your most-used skills.",
        },
        {
          text: "Performance shows quality and reliability: tool executions, security violations, feedback trends, session quality, and customer health.",
        },
        {
          text: "AI Spend shows what your AI use cost and who spent it — it has its own section in this guide.",
        },
        {
          text: "Set the date range once using the range control at the top right. Pick a quick preset (Last 7 / 14 / 30 days) or choose Custom and set your own From and To dates on the calendar.",
          tip: "The date range you pick is remembered and applies to every dashboard page as you move between them.",
        },
        {
          text: "On the Usage page, scroll to All Skills Usage to see every skill ranked by how often it was used, 10 per page. Click Export CSV to download the full list as a spreadsheet.",
        },
      ],
      faqs: [
        {
          question: "What is the difference between the four dashboard pages?",
          answer:
            "Overview is your headline snapshot, Usage shows how much the assistant is used (volume, endpoints, skills), Performance shows quality and reliability (tools, security, feedback, session quality, customer health), and AI Spend shows what the AI cost and who spent it.",
        },
        {
          question: "Does the date range apply to every chart?",
          answer:
            "Yes. The range you choose at the top of any dashboard page applies to every chart and table on all four pages, and it is remembered as you navigate between them. On AI Spend a range longer than 90 days is cut to the 90 days before its end.",
        },
        {
          question: "How do I export the full list of skills used?",
          answer:
            "On the Usage page, find the All Skills Usage table and click Export CSV. This downloads every skill and its usage count for the selected date range — not just the page you are viewing.",
        },
        {
          question: "Why does the AI Spend page cut my date range short?",
          answer:
            "AI Spend looks back at most 90 days from the end of the range you picked; the other three pages have no such limit. The tiles on AI Spend print the exact dates they cover, so you can see when the range was shortened.",
        },
      ],
    },
    {
      id: "analytics",
      title: "Analytics & Monitoring",
      description:
        "Track AI usage, tool execution, user feedback, and policy violations.",
      steps: [
        {
          text: "Go to Analytics → AI Actions for a log of every AI response. Use filters (agent type, date range, status) to narrow results.",
        },
        {
          text: "Click any row to open the detail view: user message, full AI reasoning trace, injected skills, tools called, model used, token count, request/response size, and duration.",
        },
        {
          text: "Switch to the Activity tab on the same page to see what your AI did per conversation turn — one thing a visitor asked, and everything the assistant did to answer it. It is there for every workspace, whether or not you use the AI Gateway.",
        },
        {
          text: "Use the Period dropdown to look back over the last 1, 7 or 30 days.",
        },
        {
          text: "Tick Only turns with a failure to narrow the list to turns where a call to a provider failed — including the ones where the visitor still got an answer from a backup model.",
        },
        {
          text: "Read across a row: when the turn started, the models it used, how many calls it took, how long the providers spent on it, the tokens and the estimated cost. A 2 failed badge means two of those calls failed before the turn finished.",
          tip: "The Calls column counts attempts, not questions. A turn that had to retry once shows 2 — the visitor still asked only once.",
        },
        {
          text: "Click View on a row to open that turn. It lists every call in order and, inside each call, every attempt — with the model, what chose that model, how long it took and what it cost.",
        },
        {
          text: "Click View call on an attempt to open it as an AI action: the visitor's messages, what was injected, the reply, tools called and timings. The assistant's own operating instructions are never shown.",
        },
        {
          text: "Go to Analytics → Tool Runs for every API call the AI made. Filter by Status (Success / Error) and by failure kind — Rejected before sending (the assistant's arguments did not match the tool's schema and the call never went out), API rejected (4xx), API error (5xx), Authorization, Timeout. Each failed run shows the raw error response from your API; a run that was not sent shows the check that stopped it.",
        },
        {
          text: "Go to Analytics → Agent jobs for every headless run — autonomous agents called by webhook and workflows run on a schedule or from the Portal. Ordinary chat conversations are not listed here; they live in the chat history. Filter by status (including Waiting on a customer) and by trigger, then open a job to see its steps, the approvals it asked for (with the records each one acts on) and who decided them, its event log and its result. This page is read-only: a run that waits for an approval is decided by the customer in their Portal.",
        },
        {
          text: "Go to Analytics → Feedback for user ratings. Filter by time range, vote type (Positive / Negative), triage status, and reason tag.",
        },
        {
          text: "In the Feedback detail panel: set the Triage Status (New, Reviewed, Actioned, Ignored), add internal notes, and click Save Triage. The linked AI action log is shown for full context.",
        },
        {
          text: "Go to Analytics → Policy Violations to audit messages that triggered guardrails. Each violation shows the triggering message, the policy rule matched, severity, and timestamp.",
        },
        {
          text: "Go to Customers in the navigation to view per-user interaction history, full session list, and all feedback submitted by that user.",
        },
      ],
      faqs: [
        {
          question: "What does the AI Actions log show me?",
          answer:
            "Every AI response with: the user message, the full AI reasoning trace, which skills were injected, which tools were called, model used, token count, request/response size, response time, and whether guardrails were checked.",
        },
        {
          question:
            "A conversation went wrong. How do I find out what happened?",
          answer:
            "Open Analytics → AI actions, switch to the Activity tab and find the turn by the time it started. Click View to see every call the assistant made to answer it, in order, and every attempt inside those calls — which model ran, what chose it, whether it succeeded, how long it took and what it cost. A failed attempt followed by a successful one is a provider problem the system worked around; a turn where everything failed is one where the visitor saw the temporarily-unavailable message.",
        },
        {
          question:
            "Why does one turn show more calls than the visitor asked questions?",
          answer:
            "Because answering one question usually takes several calls, and a call that fails is counted again each time it is retried. A single message is checked for safety, sometimes planned, answered, and occasionally handed to a sub-agent — each of those is a call. The Calls column is the total number of attempts made, which is exactly the number you are billed for.",
        },
        {
          question: "Are my visitors' messages stored, and can I read them?",
          answer:
            "Yes. Every turn's messages are kept in full and indefinitely. To read them, open the turn on the Activity tab and click View call on any of its calls: the AI action page shows what the visitor wrote, what was injected for that call (page context, skills, knowledge) and what the assistant replied. What it never shows is the assistant's own operating instructions — those are StrikeUp's, not part of your conversation. There is no setting that shortens message text and nothing removes it after a period of time; if your organisation has a rule about how long customer wording may be stored, ask StrikeUp support to erase the conversations concerned.",
        },
        {
          question:
            "The Activity tab does not show StrikeUp's own background work. Is something missing?",
          answer:
            "No. Activity shows your conversations only. The background work StrikeUp runs on its own models — safety checks, toolkit generation and similar — is not your traffic, is not billed to you, and is not shown here. You can still see what it costs in aggregate by switching the Usage dropdown on Dashboard → AI Spend to StrikeUp services.",
        },
        {
          question: "How do I find all failed tool calls?",
          answer:
            "In Analytics → Tool Runs, filter by Status → Failed. Each failed run shows the error response from your API, which helps diagnose misconfigured auth or broken endpoints.",
        },
        {
          question:
            "The assistant called a tool several times in one answer — how do I see what it tried?",
          answer:
            "Open any of those runs in Analytics → Tool Runs. When the assistant made more than one attempt at the same tool in the same answer, the detail page ends with Attempts on this call: every attempt in order, the arguments it sent, and either your API's response or — for an attempt that was not sent — why it was stopped (the parameter, what was expected, what the assistant sent). A run that was retried also carries an *attempt 2* badge in the list. If the same parameter keeps being rejected, that is the parameter whose description or example on the toolkit needs improving; the assistant reads those before every call.",
        },
        {
          question: "What triage statuses are available for feedback?",
          answer:
            "New (default, unreviewed), Reviewed (someone has read it), Actioned (a follow-up was taken, such as fixing a prompt or a toolkit), Ignored (read, no action needed). Use these to track quality issues as a team.",
        },
        {
          question: "Can I add notes to a feedback item?",
          answer:
            "Yes. Open the feedback detail panel, type in the Notes field, and click Save Triage. Notes are internal — users never see them.",
        },
        {
          question:
            "How do I see the full conversation for a policy violation?",
          answer:
            "Click the violation row, then click the linked AI Action log icon. This opens the full request/response context that triggered the violation.",
        },
        {
          question: "Can I see which skills were active during a conversation?",
          answer:
            'Yes. In the AI Actions detail view, the "Injected Skills" section lists every skill that was active during that request.',
        },
        {
          question: "How does the feedback module work?",
          answer:
            "Enable user feedback in Configure → User Interface — find the Feedback toggle under Data Display and turn it on. Once enabled, users see thumbs-up and thumbs-down buttons on each AI answer, plus a follow-up text field for written comments. All feedback is collected in Analytics → Feedback, where you can filter by rating, triage status, and date, and review the full conversation context for each response.",
        },
        {
          question: "What do the run statuses on Analytics → Agent jobs mean?",
          answer:
            "Running — the job is working. Waiting for approval — an action needs the customer's decision; the job stays parked until they approve or deny, or the approval expires. Waiting for input — the assistant asked the customer a question. Completed or Failed — the run finished. Cancelled — stopped by the customer or by a Stop. A step can also read Skipped, with the reason, when the step it depended on found nothing or failed. Each job also shows who triggered it, its source and its token usage.",
        },
        {
          question: 'A step shows "iteration 2 of 5" — what is that?',
          answer:
            "The step is a loop: a lookup returned more pages than one step takes in one go, so the assistant continues from where it stopped and merges the pages. The count shows which pass it is on and the maximum allowed.",
        },
        {
          question:
            "What is the difference between the AI actions tab and the Activity tab?",
          answer:
            'They are two views of the same record. AI actions is one row per call the assistant made, with the request and reply sizes, the model and the time it took. Activity groups those calls into conversation turns — one row per thing a visitor asked — with the retries, the models and the cost of the whole answer rolled up. Start from Activity when you are asking "what happened to that conversation", and from AI actions when you already know which call you want.',
        },
        {
          question:
            "I used to read the full message text inside the Activity view. Where is it now?",
          answer:
            "One click further. Open the turn, then click View call on the attempt you care about: it opens the AI action page with the visitor's messages, what was injected for that call and the assistant's reply. The turn view itself now shows only the call summary — model, timing, tokens and cost.",
        },
        {
          question: "Do I need the AI Gateway to see the Activity tab?",
          answer:
            "No. Every workspace's turns are traced, so the tab is there for everyone. Before the move it was only visible to workspaces with the AI Gateway.",
        },
        {
          question: "Where did the Configuration changes list go?",
          answer:
            "To Configure → AI Gateway → Current setup, under the table of stages. It records changes to connectors and model choices, so it stays with the settings it audits. It is only there for workspaces with the AI Gateway, because only they have connectors and model choices to change.",
        },
      ],
    },
    {
      id: "customers",
      title: "Managing Customers",
      description:
        "View and search your end-users who have interacted with the widget, explore per-customer engagement and performance analytics, and import customers from a CSV file.",
      steps: [
        { text: "Go to Customers in the main navigation." },
        {
          text: "Use the search bar to find a customer by ID, name, email, or company ID.",
        },
        {
          text: "Use the date filters (Last Updated From / To) to narrow results to a specific time window.",
        },
        {
          text: "Use the Show chips to filter by activity. Has chat session is selected by default, so you only see customers who actually chatted.",
          tip: "A customer record is created as soon as the widget loads for someone, before they type anything. That is why the default chip is on — without it the list fills with records that have no name, email or company.",
        },
        {
          text: "Turn on Opened, never chatted to see people who loaded the widget but never started a conversation.",
        },
        {
          text: "Turn on Anonymous or Preview to include visitors with no identity from your site, or records left behind by your own widget previews.",
        },
        { text: "Click a customer row to open their detail page." },
        {
          text: "On the detail page, review their chat sessions, AI action history, and any feedback they submitted. Customer Chat History lists the date each chat was started, newest first.",
        },
        {
          text: "Scroll to the Engagement & Performance section and click to expand it. This section shows analytics scoped to this specific customer for the last 30 days.",
        },
        {
          text: "View the seven stat tiles: Total Sessions, Total Messages, Tools Executed, Tool Success Rate, Avg. Messages per Session, Last Active date, and Days Since Active. The Days Since Active tile turns orange after 8 days and red after 14 days.",
        },
        {
          text: "View the Engagement Funnel below the tiles to see how far this customer progresses through interactions — from Widget Loaded through to Tool Executed. Each stage shows a count and a step-over-step conversion rate.",
        },
        {
          text: "Scroll to the Authorization card to see which toolkits this customer has saved credentials for, the type, and when they were saved and last updated. The credentials themselves are never shown.",
        },
        {
          text: "To delete them, click Clear Authorization. Only admins see this button.",
        },
        {
          text: "Click Clear Authorization again in the confirmation. The customer is asked to authorize those toolkits again the next time the assistant needs one.",
        },
        {
          text: "Click Clear Filters to reset the search, the dates and the Show chips at once. With no chips selected you see every record.",
        },
        {
          text: "To add many customers at once, click Import from CSV at the top of the Customers page.",
        },
        {
          text: "Read the limit shown under the instructions: each upload takes up to 1,000 customers and 5 MB.",
        },
        {
          text: "Leave Update existing customers unticked to add only new customers. Customers already in your list are skipped and left unchanged.",
          tip: "Tick it only when the file should change existing customers: an email, company or role in the file replaces theirs, and a PIN or password in the file replaces theirs, clears a sign-in lockout and signs them out.",
        },
        {
          text: "Choose your CSV file.",
        },
        {
          text: "If the file is over the limit, click the Upload in parts button the page offers (for example, Upload in 3 parts). The parts are then checked and imported one after another.",
        },
        {
          text: "Click Preview and review the result of every row before anything is saved.",
        },
        {
          text: "Click Commit import.",
        },
      ],
      faqs: [
        {
          question: "Why do I see customers with no name, email or company?",
          answer:
            "A customer record is created the moment the widget loads for a visitor, before they send a message — so the record exists but has nothing in it yet. The details are filled in when that person starts their first chat. The Has chat session chip is on by default so these empty records stay out of your way.",
        },
        {
          question: "What does each Show chip mean?",
          answer:
            "Has chat session: started at least one conversation. Opened, never chatted: loaded the widget but never sent a message. Anonymous: a visitor your site did not identify. Preview: a record left behind by a widget preview in the admin panel. No activity: never used the widget at all, for example someone you imported from a file.",
        },
        {
          question: "Can I combine the Show chips?",
          answer:
            "Yes. Chips add to each other rather than narrowing — selecting Has chat session and Preview shows both groups together. Selecting no chips at all shows every customer.",
        },
        {
          question: "What information is available per customer?",
          answer:
            "Each customer record shows their user ID, email, company ID, last active date, total sessions, and a full history of AI interactions and feedback. The Engagement & Performance section adds seven key metrics and a four-stage funnel scoped to that customer.",
        },
        {
          question: "What does the Engagement & Performance section show?",
          answer:
            "It shows seven stat tiles (Total Sessions, Total Messages, Tools Executed, Tool Success Rate, Avg. Messages per Session, Last Active date, Days Since Active) and a four-stage engagement funnel — all scoped to this specific customer over the last 30 days.",
        },
        {
          question: "What are the four stages in the customer funnel?",
          answer:
            "Widget Loaded (the widget was rendered on the page), Widget Opened (the user clicked to open the chat), Message Sent (the user sent at least one message), and Tool Executed (the AI called at least one API tool). Each stage shows the count and the conversion rate from the previous step.",
        },
        {
          question: "What does the colour on the Days Since Active tile mean?",
          answer:
            "Green means the customer was active within the last 7 days, orange means 8–14 days, and red means more than 14 days. Use this as a quick signal for customers who may be going dormant.",
        },
        {
          question: "Can I delete a customer record?",
          answer:
            "Customer records are created automatically from widget interactions and cannot be manually deleted from the UI. Contact support for data deletion requests.",
        },
        {
          question: "Can I search by partial email?",
          answer:
            "Yes. The search field matches partial values across ID, name, email, and company ID simultaneously.",
        },
        {
          question: 'What does the "company ID" field represent?',
          answer:
            "It is the customer's company identifier as passed by your embed code (the userCompanyId parameter). Use it to group customers by your own tenant or organization.",
        },
        {
          question: "How many customers can I import at once?",
          answer:
            "Up to 1,000 customers and 5 MB per upload. The Import from CSV page shows the limit before you choose a file. For a larger file, click Upload in parts: the page splits it and imports the parts one after another.",
        },
        {
          question: "What happens if one part of a split import fails?",
          answer:
            "The parts before it stay imported, and the part that failed and the ones after it are not sent. Fix the problem and import the same file again: with Update existing customers unticked, the customers already imported are skipped.",
        },
        {
          question: "Does an import change customers I already have?",
          answer:
            "Not unless you tick Update existing customers. Unticked, a row for a customer who already exists is skipped and counted under Skipped. Ticked, an email, company or role in the row replaces theirs, and a PIN or password in the row replaces theirs and signs them out. Empty cells never overwrite anything.",
        },
        {
          question: "Can I see the credentials a customer saved for a toolkit?",
          answer:
            "No. The Authorization card on the customer's detail page lists the toolkits they saved credentials for, the type, and when they were saved and last updated, never the credentials themselves. An admin can click Clear Authorization to delete all of them; the customer is then asked to authorize those toolkits again the next time the assistant needs one.",
        },
        {
          question: "Why does a toolkit show as Deleted toolkit?",
          answer:
            "The toolkit was deleted after the customer saved credentials for it. Click Clear Authorization to remove the leftover entry.",
        },
      ],
    },
    {
      id: "customer-portal",
      title: "Customer Portal",
      description:
        "A self-service area inside the widget where your customers manage their own profile, memory, members, knowledge base, skills, workflows, notifications, prompt shortcuts, and settings. You decide which pages are visible per widget.",
      steps: [
        {
          text: "Go to Configure → User Interface and select the widget you want to enable the Portal for.",
        },
        {
          text: "Scroll to the Customer Portal section.",
        },
        {
          text: "Turn on Require customer authentication if it is not already on. The Portal is customer-scoped, so each user must sign in.",
          tip: "When you enable the Customer Portal, sign-in is required automatically — even if Require customer authentication looks off, the widget will still prompt the customer to log in on first load.",
        },
        {
          text: "Turn on Enable Customer Portal. This is the master switch for the gear (Configure) icon in the chat top bar.",
        },
        {
          text: "Optionally, fill in App page URL with the https address of the page in your app where this widget is embedded.",
          tip: "Email, Telegram and Slack notifications then carry an Open button that lands on that page, and the widget opens the chat or run the message is about once your customer is signed in. Runs open only while Enable Customer Portal is on. Leave it blank and chat notifications carry no link, except in Telegram and Slack for people who connected their account there, when your platform administrator has set up a hosted widget page: Open then signs them straight into the assistant on that chat or run.",
        },
        {
          text: 'Agent runs is always on for every signed-in customer: it lists the runs of their autonomous agents and scheduled workflows, and it is where a run that waits for an approval or an answer is decided. An approval that will change records names them — "Acts on: 101, 103" — so the customer knows exactly what they are approving. A finished run shows its result formatted exactly like an answer in the chat — headings, lists, links, tables and charts included. Everyone in a company also sees the runs of its company workflows, and Admin customers see the other runs of everyone in their company too, except personal workflows that belong to someone else.',
        },
        {
          text: "When the widget is at least 700 pixels wide (its expanded or full-page mode, or a big inline embed), the welcome screen also shows up to three sections under the input — Needs you, with runs waiting for an approval or an answer and an Approve / Decide button on each; In progress, with runs running right now; and Finished runs, with runs that completed, failed or were cancelled. A section appears only when it has something to list. Small docked widgets do not show them.",
        },
        {
          text: "Use the Visible pages checkbox grid to choose which Portal pages your customers should see — Members, Knowledge Base, Skills, Workflows, Notification channels, Usage, Settings, Prompt Shortcuts.",
          tip: "Profile appears in the grid as a locked 'Always on' card, and Memory is not in the grid at all — both are always visible to every customer and cannot be turned off. A page you switch off is hidden and blocked: the Portal refuses it even when someone asks for it directly. Turning the Usage page off does not remove an AI allowance you have set: it still applies, the customer simply is not shown how much of it is left. Notification channels hides only the company's own email, Telegram and Slack connections: the Notifications page stays, so every customer can still choose how they are told.",
        },
        {
          text: "Click Save User Interface Settings.",
        },
        {
          text: "Reload the embedded widget on your site. After your customer signs in, a gear icon appears next to the New chat button in the chat top bar.",
        },
        {
          text: "Tell your customers to click the gear icon to open the Portal. The widget will expand into a near-fullscreen modal showing the pages you enabled.",
        },
        {
          text: "To close the Portal, your customer clicks the X in the Portal top bar. The widget shrinks back to its normal chat size and the conversation continues where they left off — messages, scroll position, and unsent input are preserved.",
        },
        {
          text: "Optional — embed the Portal as a separate page in your own UI. Use your widget bundle URL with ?mode=portal appended (for example, https://your-widget-host/?applicationToken=YOUR_TOKEN&mode=portal). This deep-link works even when the Enable Customer Portal master switch is off.",
          tip: "Per-page toggles still apply to the deep-link Portal page — a page you switch off is hidden and blocked everywhere.",
        },
        {
          text: "Optional — to change any of this on a customer's behalf, go to CRM → Companies and open the company. See the Managing a Customer Company section for what you can do there.",
        },
      ],
      faqs: [
        {
          question:
            "Do I have to wait for my customer to set their own Portal up?",
          answer:
            "No. Everything a customer can change in the Portal, you can change for them from CRM → Companies. Open the company and use the tabs on that page. See the Managing a Customer Company section for the full walkthrough.",
        },
        {
          question: "What is the Customer Portal for?",
          answer:
            "It is a self-service area for your end-users (customers) — they manage their own profile, members of their company, custom knowledge base, custom AI skills, scheduled workflows, notification channels, and company settings, without needing you to do it for them.",
        },
        {
          question: "How do my customers open the Portal?",
          answer:
            "Two ways: 1) Inside the chat widget, click the gear (Configure) icon in the top bar — the widget expands into a near-fullscreen modal showing the Portal pages. 2) Open the Portal as a dedicated page using your widget URL with ?mode=portal appended.",
        },
        {
          question: "Why is the gear icon missing from the chat?",
          answer:
            "Check three things in Configure → User Interface for the affected widget: (a) Enable Customer Portal is on, (b) Require customer authentication is on, (c) your customer is actually signed in. The icon only appears after the customer has authenticated. If you turn the Portal on for an already-deployed widget, your customer needs to reload the page once to pick up the new config.",
        },
        {
          question: "What pages can I enable or disable?",
          answer:
            "There are eight on/off toggles: Members (manage other people in their company), Knowledge Base (upload docs scoped to their company), Skills (custom AI skills for their company), Workflows (scheduled jobs), Notification channels (the company's own email, Telegram and Slack connections, which its Admins set up on the Notifications page; turning it off hides only that part), Usage (whether an AI allowance applies to their company and how much of it is left, as a percentage — never tokens or money), Settings (their company's self-registration and credential preferences), and Prompt Shortcuts (one-click prompts for their company). Four pages are always on and have no toggle: Profile (the customer's own account view), Memory (what the assistant remembers about them), Agent runs, and Notifications (where each customer chooses how they are told about runs that need them). That is eleven pages in total.",
        },
        {
          question:
            "What is the difference between an Admin and a Member customer?",
          answer:
            "Admin customers see every Portal page you have enabled. Member (regular) customers see Profile, Memory, Agent runs, Notifications, Usage, Workflows and Prompt Shortcuts — and Prompt Shortcuts is read-only for them, so they can use the shortcuts but not create or edit them. On Workflows they can view the company's workflows and manage only their own personal ones. On Notifications they only choose how they are told; adding the company's connections is for Admins. A page you turn off is hidden from Members too. You set the role when you create the customer (via CSV bulk import, the Customer API, or the admin app's Customer Companies page). Admins can promote or demote other customers from the Members page inside the Portal.",
        },
        {
          question: "What is the Memory page?",
          answer:
            "It shows each customer every fact the assistant has remembered about them, grouped by category, with a Forget button on each one. There is also a 'Let the assistant remember things about me' checkbox that turns memory off for that customer entirely. Memory is always visible — it is a privacy surface, so it has no tenant toggle.",
        },
        {
          question: "What are Prompt Shortcuts in the Portal?",
          answer:
            "They are one-click prompts scoped to a single customer company, appearing in that company's chat input as chips and in the slash-command dropdown. An Admin customer writes them; every member of that company can then use them. They are the per-company twin of the tenant-wide shortcuts you manage under Configure → Prompt Shortcuts.",
        },
        {
          question: "Can I let my customers register themselves?",
          answer:
            "Yes. On the Customer Companies admin page, edit a customer company and turn on Allow self-registration. Then share your widget URL with ?mode=register&customerCompanyId=COMPANY_ID appended. New users will land on a registration screen and be added to that customer company automatically. They join as members, not admins; you or the company's admin can promote them later. The only exception is the first person to join a customer company that has no users yet, who becomes its admin.",
        },
        {
          question: "Do customers see each other's data?",
          answer:
            "No. Each customer only sees data scoped to their own customer company — their own profile, their own company's members, their own company's knowledge base, etc. Resources you create at the tenant level (knowledge base, skills, integrations etc. without a customer-company assignment) are read-only and visible to everyone, but customer-company-specific items are isolated per company.",
        },
        {
          question:
            "What happens if I disable a page that already has data in it?",
          answer:
            "The data is preserved — switching a page off hides it and blocks it. Your customers cannot open it or change anything on it, even with a direct request, until you turn the page back on. Workflows that are already scheduled keep running. The data continues to be available to the AI in chat conversations as it was before, unless you also remove the underlying assignments.",
        },
        {
          question: "Does turning the Portal off affect the chat experience?",
          answer:
            "Yes in one specific way: when the Portal is enabled, customers must sign in on widget load. When the Portal is fully disabled (master toggle off) and Require customer authentication is also off, chat can run anonymously again. If you want anonymous chat, turn both off.",
        },
        {
          question: "Does the Portal use my widget's theme colours?",
          answer:
            "Yes. The Portal inherits the same colour overrides, fonts, and dark/light mode setting you configure under Configure → User Interface → Colours and styling. The Portal sidebar, buttons, and accent colour all follow your brand.",
        },
        {
          question: "Does the chat survive when the customer opens the Portal?",
          answer:
            "Yes. The chat and Portal are mounted at the same time and just toggled between visible/hidden. The connection to the AI stays alive, message history is preserved, scroll position is kept, and any draft message they had typed is still there when they close the Portal.",
        },
        {
          question: "Does the Portal work on mobile devices?",
          answer:
            "Yes. The Portal modal sizes to 90% of the viewport, capped at 1400 × 900, so it fits whatever screen it opens on. The sidebar can shrink to a narrow icon rail using the panel toggle at the top of the sidebar — this is a manual choice, not automatic on small screens, and each customer's preference is remembered in their browser for next time.",
        },
        {
          question:
            "Can I hide the gear icon but still let customers use the Portal?",
          answer:
            "Yes. Turn off Enable Customer Portal in Configure → User Interface. The gear icon disappears from the chat top bar, but the direct-link Portal URL (?mode=portal) keeps working. This is useful if you embed the Portal as its own page in your dashboard rather than inside the chat.",
        },
        {
          question: "How do I link to the Portal from my own website?",
          answer:
            "Use your widget bundle URL with two query parameters: applicationToken (your widget's token from Configure → Embed) and mode=portal. Example: https://your-widget-host/?applicationToken=YOUR_TOKEN&mode=portal. You can embed this URL in an iframe or open it as a popup from any link or button on your site.",
        },
        {
          question: "What does the Profile page let customers do?",
          answer:
            "They can view and edit their own name and email, and see their role (Admin or Member) and their customer ID. The role is read-only there — only another Admin can change it from the Members page. Profile does not change their PIN or password: that happens in the chat sign-in screen, not in the Portal. Log out lives at the bottom of the Portal sidebar, not on the Profile page.",
        },
        {
          question: "What does the Members page let an admin customer do?",
          answer:
            "List all other members of their customer company, see who is active and who is disabled, change another member's role between Admin and Member, and disable or re-enable other members. Disabling someone signs them out immediately — their active sessions are revoked. Two guards apply: an Admin cannot demote themselves or disable themselves from this page at all, and the system refuses to demote or disable the last active Admin, so every customer company always keeps at least one. There is no invite feature — new customers are created by you, via CSV import, the Customer API, or self-registration.",
        },
        {
          question:
            "What can a customer add to their Knowledge Base from the Portal?",
          answer:
            "They can upload documents. Website crawling is not available in the Portal — that is a tenant-only feature under Configure → Knowledge Base. Uploads are indexed in the background, so the status starts as pending and the customer clicks Refresh to see it complete. The page also has a Test retrieval box where they type a question and see exactly which chunks the AI would pull back. Everything they add is scoped to their own customer company and is not visible to other customer companies on the same widget. Both their content and your tenant-level content are merged at chat time.",
        },
        {
          question: "Can my customers create their own AI skills?",
          answer:
            "Yes, if you enable the Skills page. They do not fill in a form — they click Create skill and describe what they want in a short chat with a wizard, which asks follow-up questions and then compiles the skill (name, category, description, and when to activate it) for them. Once saved, a skill can be switched active or inactive, or deleted. The AI uses these skills only when chatting with customers from that same customer company. You can also share skills tenant-wide from Configure → Skills.",
        },
        {
          question: "Can my customers schedule their own AI workflows?",
          answer:
            'Yes, if you enable the Workflows page — Admin and Member customers both see it. It has three tabs: Workflows (create a workflow with a name and a plain-English description of the job), Schedules (add one or more recurring schedules to the selected workflow), and Run History (every past run and its result). There is also a Run now button that triggers a workflow immediately and jumps straight to Run History so they can watch it. Each customer can start up to five runs per minute across Run now, their schedules and their autonomous agents; a sixth shows "Too many runs in the last minute" and works again a minute later, and a schedule that fires while the budget is spent is retried a little later rather than skipped. Deleting a workflow deletes its schedules with it. Workflows created in the Portal are isolated to their customer company.',
        },
        {
          question:
            "What is the difference between a company workflow and a personal one?",
          answer:
            "Who sees it and who is told about it. A company workflow is shared with everyone in the customer company: they all see it, its schedules and its run history, any of them can approve or decline what a run asks for, and each of them is told about its runs on the channels they switched on. Only Admin customers create, change, schedule, delete or run a company workflow — Members see it marked View only — and it always runs as the Admin who created it, whoever presses Run now. A personal workflow belongs to the person who created it: any customer can create one, and only they see it, its runs and its notifications — not even their company's Admins. An Admin customer picks Company or Personal when creating a workflow; a Member's is always personal. The choice cannot be changed later.",
        },
        {
          question: "Which kind are the workflows my customers already had?",
          answer:
            "Company workflows, if the person who created them belongs to a customer company — so everyone in that company who switched on a notification channel now hears about their runs. A workflow whose creator has no company stays personal. To make a company workflow personal, create it again as a personal workflow and delete the company one.",
        },
        {
          question: "Can my customers connect their own notification channels?",
          answer:
            "Yes, if Notification channels is ticked under Visible pages. An Admin customer opens the Notifications page and adds their company's own email (SMTP), Telegram, Slack or Microsoft Teams connection, at most one of each, then clicks Test to confirm it works before relying on it. An email connection also lets the AI send reports and exports from that address. Their connections are not visible to other customer companies on the same widget, and removing one stops it for everyone in that company. Below the connections, every customer, Admin or Member, chooses which of them they are told through.",
        },
        {
          question:
            "What settings can a customer change for their own company?",
          answer:
            "Turning self-registration on or off for their company, the default credential type new members are offered (PIN, Password, or no preference), and a per-company agent instruction — free text describing tone, branding, and house rules that is merged into the agent's prompt at runtime for that company only. The company name shown on that page is read-only; only your staff can change it.",
        },
        {
          question: "Can I use the Portal without enabling chat?",
          answer:
            "Yes. Embed the widget URL with ?mode=portal as its own page in your application — chat is not loaded in that mode. This is the right setup if you want a dedicated customer area separate from chat.",
        },
        {
          question: "How do customers reset a forgotten PIN or password?",
          answer:
            "There is no self-service 'forgot my PIN' link today — you have to reset it for them. Re-run a CSV import from Customers, Import from CSV, with Update existing customers ticked and a row containing that customer's ID and a new credential; an import only overwrites a credential when the row actually supplies one, so everything else about the customer is left untouched. A customer who still knows their current credential can change it themselves from the chat sign-in screen, which asks for the old one before accepting a new one. Admin customers cannot reset another member's credential from the Members page — that page manages roles and enable/disable only.",
        },
        {
          question:
            "What happens when a customer types the wrong PIN too many times?",
          answer:
            "Their account locks and the widget shows an 'Account locked' screen with a live countdown. The widget re-checks automatically once the countdown expires — they do not need to reload the page. You do not need to unlock anything manually.",
        },
        {
          question:
            "A customer says their account is 'unavailable' — what does that mean?",
          answer:
            "Someone disabled them. Either an Admin customer in their own company disabled them from the Members page, or their whole customer company was set inactive by your staff on the Customer Companies page. Re-enable the person from the Portal's Members page, or reactivate the customer company from the admin app.",
        },
        {
          question: "Is there a guide I can give my customers?",
          answer:
            "Yes. There is a separate end-user guide written for the people who actually use the Portal inside your widget — how to sign in, what each page does, and how to control what the assistant remembers. It is on the docs site under the End User Portal Guide tab, and in the repository as docs/CUSTOMER_PORTAL_USER_GUIDE.md.",
        },
        {
          question:
            "What if a customer is on an older widget version when I change a Portal toggle?",
          answer:
            "Toggles are read on widget load, so any customer who is currently using the widget will not see the change until they reload the page. Closing and reopening the chat (or refreshing the host page) picks up the latest settings.",
        },
        {
          question: "Can I see analytics on Portal usage?",
          answer:
            "Today, Portal opens and per-page navigation are not tracked separately in Analytics. Use Analytics → Customers to see which customers are actively engaging with the widget — chat sessions, tools executed, etc.",
        },
        {
          question: "What can a customer do on the Agent runs page?",
          answer:
            "See every run of their autonomous agents and scheduled workflows with its steps and live progress; approve or deny an action a run is waiting for; answer a question the assistant asked; resume or cancel a run. An Admin customer sees and can decide the runs of everyone in their company; a Member sees only their own. Your staff see the same runs read-only under Analytics → Agent jobs.",
        },
        {
          question: "What is the Needs you list on the welcome screen?",
          answer:
            "The runs waiting for that customer's approval or answer, shown when the widget is wide enough (expanded or full-page mode, or a large inline embed). They can approve in place or open the run. Below it, Recent agent activity lists the last finished runs. Both update live — no reload needed.",
        },
        {
          question:
            "A customer's edit to their company's AI instruction was refused — why?",
          answer:
            "The instruction failed the same content check as your own persona instructions: it tried to change the assistant's identity, relax approval or guardrail rules, deceive users, or contained credentials, links or script. The Portal shows the reason; the customer rewrites it as tone-and-workflow guidance. The same applies to skills a customer creates in the Portal.",
        },
        {
          question: "What does the Portal's Usage page show a customer?",
          answer:
            "Only whether an AI allowance applies to their company and how much of it is left, as a percentage — never token counts, costs or model names. The page has its own on/off toggle in the Customer Portal settings.",
        },
      ],
    },
    {
      id: "integrations",
      title: "Connecting Third-Party Integrations",
      description:
        "Connect Help Scout or Intercom to enable human handoff from the widget.",
      steps: [
        {
          text: "Go to Configure → Integrations.",
          tip: "Every step below — including the Intercom webhook you have to register yourself — is written out with illustrations in Help → Technical docs → Support Integrations — Setup Guide. The Setup guide button on the Integrations page opens it directly.",
        },
        {
          text: "To connect Help Scout: click Connect Help Scout. A popup opens — complete the OAuth flow to authorize access.",
        },
        {
          text: "Before connecting Intercom for the first time: go to developers.intercom.com, open your app, and navigate to Authentication → Permissions. Enable exactly these five permissions — Read and list users and companies, Write users and companies, Read conversations, Write conversations, and Read admins — and uncheck everything else. Save the change.",
          tip: "If you are connecting with a developer access token, generate or regenerate the token after saving the permissions — an existing token keeps whatever permissions were set when it was created.",
        },
        {
          text: "To connect Intercom: in your Intercom app's Basic info page, copy the Access token and the Client secret.",
          tip: "Intercom signs the replies it sends back with your app's client secret. Without it, agent replies are refused and never reach the widget.",
        },
        {
          text: "Click Connect Intercom, enter an optional name, paste the access token and the client secret, then click Connect.",
          tip: "Connecting the same Intercom workspace again updates its existing integration instead of adding a second one. Leave Client secret blank to keep the one already saved.",
        },
        {
          text: "After connecting Intercom, a banner appears with webhook setup instructions. Copy the webhook URL shown and add it to your Intercom developer settings with the required topic subscriptions listed.",
          tip: "Copy the topic list exactly — missing a topic prevents some handoff events from being received.",
        },
        {
          text: "Once connected, each integration shows a status badge: Active or Error. Error means Help Scout or Intercom no longer accepts the saved sign-in, and new handoffs are refused until you reconnect.",
        },
        {
          text: "To rename an integration: click its name inline, type the new name, and press Enter.",
        },
        {
          text: "To reconnect an integration that shows Error: click Reconnect on its card. For Help Scout, authorize again in the popup; for Intercom, paste a current access token. The integration keeps its name and its widgets.",
        },
        { text: "To remove an integration: click Disconnect and confirm." },
      ],
      faqs: [
        {
          question: "What Intercom app permissions do I need to enable?",
          answer:
            "Enable exactly five permissions in your Intercom app under Authentication → Permissions at developers.intercom.com: Read and list users and companies (to search for existing contacts), Write users and companies (to create and update contacts and companies), Read conversations (to fetch conversation details), Write conversations (to create conversations, send messages, and close them), and Read admins (to list teams and retrieve the bot admin identity needed for replies). All other permissions should be unchecked.",
        },
        {
          question: "Why does Intercom need my app's client secret?",
          answer:
            "Because Intercom signs every reply it sends back with it. The client secret lets the platform check that a reply really comes from your Intercom app; without it, agent replies are refused. You find it in the Intercom Developer Hub under Basic info, next to the access token.",
        },
        {
          question:
            "What webhook topics do I need to subscribe to for Intercom?",
          answer:
            "The required topics are shown in the setup banner that appears after connecting. Copy them exactly — missing a topic will prevent some handoff events from being received.",
        },
        {
          question:
            "I connected Help Scout but the inbox list is empty in the AI & UX settings — why?",
          answer:
            "Usually because the Help Scout account you authorized cannot see any mailbox. Give it access to at least one mailbox in Help Scout, then refresh the page. If the integration shows Error, click Reconnect on the Integrations page.",
        },
        {
          question: "Can I connect multiple accounts of the same integration?",
          answer:
            "Yes. You can add several Help Scout or Intercom connections, each with its own name. Connecting the same Intercom workspace again updates its existing connection instead of adding a second one.",
        },
      ],
    },
    {
      id: "notifications",
      title: "Email Notification Channels",
      description:
        "Set up the email, Telegram, Slack and Microsoft Teams channels your whole tenant sends through. An email (SMTP) channel lets the AI send reports, data exports, and summaries directly to users during a conversation or as part of a scheduled workflow. All four are also the fallback for job notifications — approvals, questions and run outcomes — for customer companies that have no channel of that type of their own.",
      steps: [
        {
          text: "Go to Configure → Notifications.",
          tip: "Help → Technical docs → Notification Channels — Setup Guide walks through SMTP, Telegram, Slack and Microsoft Teams with illustrations, including the host and port that work for Gmail, Microsoft 365, SendGrid, Mailgun and Amazon SES. The Setup guide button at the top of the page opens the whole guide; Where do I find these? inside a form opens only the steps for that provider.",
        },
        {
          text: "Click Add Email (SMTP), Add Telegram, Add Slack or Add Microsoft Teams.",
          tip: "You can have one channel of each type here. Once a type has a channel, its Add button disappears — edit the existing channel instead.",
        },
        {
          text: 'Enter a channel name (e.g. "Main SMTP" or "Support bot"). It is only a label for you.',
        },
        {
          text: "For Email (SMTP), fill in the host, port, SSL toggle, username, password, From Email address, and the display name shown to recipients.",
          tip: "The port must be one of the usual email ports: 25, 465, 587, 2465, 2525 or 2587. The host must be a mail server on the public internet; a server on your internal network, or localhost, is refused.",
        },
        {
          text: "For Email (SMTP), under Who the AI can email, choose Signed-in user only, Allowed list or Any address.",
          tip: "Signed-in user only is the default: the AI's emails go only to the signed-in user's own address, whatever address is typed in the chat, and a visitor who is not signed in cannot send any. Any address shows a warning, because someone who tricks the AI through a chat message could have your data sent to their own address.",
        },
        {
          text: "If you chose Allowed list, enter one email address (ops@acme.com) or one whole domain (@acme.com) per line.",
          tip: "The signed-in user's own address is always allowed as well, once it is confirmed. @acme.com allows anyone@acme.com, but not addresses at sub.acme.com.",
        },
        {
          text: "For Telegram, create a bot with @BotFather in Telegram and paste the token it returns. Leave Bot username blank — it is read from the bot itself.",
          tip: "Give every channel a bot of its own. A bot another channel already uses — yours or a customer company's — is refused, because Telegram sends a bot's button presses and replies to one place only.",
        },
        {
          text: "For Slack without a Slack app yet, leave the token blank, click Save, then click Edit on the new channel.",
          tip: "A Slack app you created by hand earlier keeps sending messages. For Approve and Deny buttons, replies and chat as well, create a new app from the manifest in the next steps and paste its token and signing secret instead.",
        },
        {
          text: "Under Create the Slack app from this manifest, click Copy.",
          tip: "The manifest gives the app every permission, event and command it needs, so you never pick them by hand.",
        },
        {
          text: "Go to api.slack.com/apps, choose Create New App → From a manifest, pick your workspace and paste the manifest.",
        },
        {
          text: "Install the app to your workspace.",
        },
        {
          text: "Paste the Bot User OAuth Token (it starts with xoxb-) and the Signing secret from the app's Basic Information page into the channel.",
          tip: "Without the signing secret, Slack messages carry only an Open button — Approve and Deny, replies and chat need it.",
        },
        {
          text: "For Microsoft Teams, create an Azure Bot of type Single Tenant in the Azure portal, then add a client secret in the bot's app registration under Certificates & secrets.",
          tip: "Add Microsoft Teams appears only when your server has a public address Teams can reach. The bot answers only people in your own Microsoft 365 organisation.",
        },
        {
          text: "Paste the Microsoft App ID, the client secret and the Directory (tenant) ID into the channel.",
          tip: "Copy the client secret's Value, not its Secret ID. The channel name is also the app's name in Teams.",
        },
        {
          text: "Click Save. The channel appears in the list with a Disconnected status.",
        },
        {
          text: "Click Test on the channel card. For email, a test message is sent to your logged-in account email address; for Telegram and Slack, the bot token is checked, no message is sent, and the result also says whether buttons and replies are on; for Microsoft Teams, the bot signs in to Microsoft. When the test passes, the status updates to Active.",
          tip: "If the test shows an error, double-check the SMTP host, port, and credentials. The email test tells you what to check rather than showing the mail server's own error text. Port 587 with SSL enabled is standard for most providers. For Gmail, use an App Password — not your regular account password. For Slack, make sure you pasted the bot token (xoxb-), not the user token (xoxp-). For Microsoft Teams, a wrong secret or tenant ID is named in the result.",
        },
        {
          text: "For Microsoft Teams, click Edit on the channel. Under Connect the Teams bot, copy the Messaging endpoint into the Azure Bot's Configuration page, then add the Microsoft Teams channel to the Azure Bot.",
        },
        {
          text: "Click Download app package and upload it in the Teams admin center under Teams apps → Manage apps, or send it to your Teams admin.",
          tip: "Until the app is uploaded, nobody in your organisation can find the bot in Teams. Download a new package after you rename the channel.",
        },
        {
          text: "For Telegram, Slack and Microsoft Teams, each person connects themselves once, from Notifications → My notifications in the Customer Portal — see Job Notifications (Approvals and Questions).",
          tip: "Buttons and replies work only for people who connected through the bot in Telegram, confirmed in Slack or linked their account in Microsoft Teams. Someone who pasted an ID only receives messages.",
        },
        {
          text: "To let the AI send emails, go to Configure → AI & UX, select your widget from the dropdown at the top, and scroll to the Email Notifications section. Toggle the channel on using the switch next to its name, enable the AI email notifications toggle, then click Save Notification Settings.",
          tip: "If you have multiple channels linked to the same widget, mark one as Default — this is the channel the AI will use when several are available.",
        },
        {
          text: "To edit a channel: click Edit on its card. Leave the password, bot token or client secret field blank to keep the stored one unchanged.",
          tip: "For email, enter the password again when you change the host, port or username, or turn SSL off. The stored password is only kept for the same mail server and account, so it is never sent to a server you just typed in.",
        },
        {
          text: "To disable a channel temporarily without deleting it: click Disable on its card. The channel remains configured but nothing is sent through it until re-enabled.",
        },
        {
          text: "To delete a channel: click Delete and confirm. Any widgets linked to this channel will lose email notification capability, and customer companies that relied on it as their fallback stop getting job notifications of that type.",
        },
        {
          text: "Once an email channel is Active and connected to a widget, the AI can use it during conversations. With Allowed list or Any address, the AI asks the user for a recipient email address if one is not already provided; with Signed-in user only, it sends to the signed-in user without asking.",
        },
      ],
      faqs: [
        {
          question: "How does the AI know when to send an email?",
          answer:
            'The AI detects when a user asks it to send a report, summary, or data by email — for example, "Can you email me this?" or "Send this report to my manager at john@company.com". Who receives it depends on the Who the AI can email setting on the email channel. With the default, Signed-in user only, it always goes to the address of the signed-in user.',
        },
        {
          question: "What do the Who the AI can email options do?",
          answer:
            "They decide where the AI's emails may go. Signed-in user only, the default, sends every email to the signed-in user's own address and ignores any other address typed in the chat; a visitor who is not signed in cannot send any. Allowed list sends only to the addresses and @domains you list, plus the signed-in user once their address is confirmed. Any address sends to whatever single address the AI is given. In a scheduled workflow or autonomous agent, the signed-in user is the person the run belongs to. Telegram messages from the AI go to the signed-in user's own linked Telegram. Job notifications — approvals, questions and run outcomes — are not affected.",
        },
        {
          question:
            "Why did the AI send the email to me instead of the address I gave?",
          answer:
            "Because the email channel is set to Signed-in user only, the default. The AI's emails then always go to the signed-in user's own address, and the address you gave is not used. Email channels created before this setting existed use it too. To let the AI email other people, click Edit on the email channel and choose Allowed list or Any address.",
        },
        {
          question: "Why does the AI say it cannot send an email?",
          answer:
            "Usually because nobody is signed in to the chat. With Signed-in user only, a visitor who is not signed in has no address of their own the AI may use, so nothing is sent. The AI also cannot send when the signed-in user has no email address on file, when an address is not on your Allowed list, or when several addresses are typed as one, separated by commas or semicolons. The AI can also send at most 50 emails to one user, and 1,000 across your workspace, in any 24 hours.",
        },
        {
          question: "Why did the AI ask for a code before sending an email?",
          answer:
            "Because the user's email address is not confirmed yet. The first time the AI emails a user whose address they typed in themselves (when they signed up, or changed it in the Portal), it first sends a 6-digit code to that address and asks for it in the chat. Once the user types the code, the email is sent, and later emails need no code. Addresses you add or change through customer import count as confirmed, and so do the addresses of customers who could already sign in before this change. A code expires after 15 minutes, a new one can be sent after a minute, and at most 3 codes go to one address a day. A scheduled workflow cannot ask for a code, so it sends nothing until the user has confirmed the address in a chat.",
        },
        {
          question: "Which email providers work with SMTP channels?",
          answer:
            "Any SMTP-compatible service works: Gmail (smtp.gmail.com, port 587), Outlook / Office 365 (smtp.office365.com, port 587), SendGrid (smtp.sendgrid.net, port 587), Mailgun, Amazon SES, or your own mail server, as long as it is on the public internet and uses port 25, 465, 587, 2465, 2525 or 2587.",
        },
        {
          question: "The test email is not arriving — what should I check?",
          answer:
            "Check that the host, port, username, and password are correct. For Gmail, you must use an App Password (not your regular password) if 2-factor authentication is enabled. Also check your spam folder. If the test says it could not send the test email, the mail server could not be reached or did not accept the message — verify the hostname, the port and the SSL setting, and that the port is not blocked by a firewall.",
        },
        {
          question: "Why does saving ask me to enter the password again?",
          answer:
            "Because you changed the host, port or username, or turned SSL off. The stored password is only reused for the same mail server and account, so it is never sent to a different server. Type the password and click Save again.",
        },
        {
          question:
            "Why does the test say the host is not a public internet address, or the port is not allowed?",
          answer:
            "Because email channels connect only to mail servers on the public internet, on the usual email ports: 25, 465, 587, 2465, 2525 and 2587. A host that points to your internal network or to localhost is refused. Use your provider's public SMTP host, such as smtp.office365.com, with one of those ports.",
        },
        {
          question:
            "Why did the Telegram, Slack or Microsoft Teams test not send me a message?",
          answer:
            "Because a bot can only message people who have connected to it, and nobody has yet. The test checks the bot token with Telegram or Slack, or signs the bot in to Microsoft, instead. Your customers connect themselves under Notifications → My notifications in the Customer Portal, and can send themselves a test message from there.",
        },
        {
          question: "Can the AI attach files such as CSV exports?",
          answer:
            "Yes. When the AI has access to tabular data from a tool call or structured output, it can attach it as a CSV file automatically alongside the email body.",
        },
        {
          question: "Can I configure multiple SMTP channels?",
          answer:
            "No — you can have one channel of each type here: one email, one Telegram, one Slack and one Microsoft Teams. A second channel of the same type is refused, so edit the existing one instead. To send from a different mailbox for one customer, give that customer company its own channel under CRM → Companies → Integrations; it takes priority over yours for that company's people.",
        },
        {
          question: "Why is the status still Disconnected after I saved?",
          answer:
            "The status only changes to Active after a successful connection test. Click Test on the channel — for email, a test message is sent to your account; for Telegram and Slack, the bot token is checked; for Microsoft Teams, the bot signs in to Microsoft. Once the test passes, the status updates automatically.",
        },
        {
          question:
            "I set up a channel and it shows Active, but the AI still won't send emails — what am I missing?",
          answer:
            "Two steps are required. First, create and test the channel under Configure → Notifications. Second, connect it to your widget under Configure → AI & UX → Email Notifications: toggle the channel on, enable the AI email notifications toggle, and click Save Notification Settings. If the second step is skipped, the AI has no channel to use regardless of the Active status.",
        },
        {
          question:
            "Do I need to connect a Telegram, Slack or Microsoft Teams channel to a widget?",
          answer:
            "No, not for job notifications — they need nothing on the widget. The list under Configure → AI & UX → Email Notifications only controls which channels the AI itself may use during a conversation.",
        },
        {
          question:
            "Is this channel also used for the approval notifications my customers get?",
          answer:
            "Yes, as the fallback, for all four types. A customer company that has its own channel of a type under CRM → Companies → Integrations uses that one; for everyone else, approvals, questions and run outcomes go out through the tenant-wide channel of that type you set up here. A company channel that has no bot token (for email, no host; for Microsoft Teams, no App ID, client secret or tenant ID) yet is skipped, so yours keeps working until theirs is finished. See Job Notifications (Approvals and Questions).",
        },
        {
          question:
            'What does "Off on this server — messages carry an Open link only" mean?',
          answer:
            "It means Slack and Telegram cannot reach your platform, so they cannot deliver button presses or replies back to it. Messages still arrive, each with an Open button, and people approve and answer in your app. Everything else works the same. Ask your platform administrator to turn on buttons and replies for your server.",
        },
        {
          question:
            "Why is Add Microsoft Teams missing, or the channel says Microsoft Teams is off on this server?",
          answer:
            "Because Teams sends every message and button press to your server, and your server has no public address Teams can reach. Without one, a Teams channel cannot be created, and an existing one shows the warning instead of its setup steps. Ask your platform administrator to give the server a public address.",
        },
        {
          question:
            "Can people from another company's Microsoft 365 use our Teams bot?",
          answer:
            "No. A Teams channel answers only people in the Directory (tenant) ID you pasted, and ignores everyone else. To reach a customer company in its own Microsoft 365 organisation, add a Microsoft Teams channel for that company under CRM → Companies → Integrations, with an Azure Bot created in their organisation.",
        },
      ],
    },
    {
      id: "team",
      title: "Team Management",
      description:
        "Invite colleagues, manage roles, and update company settings.",
      steps: [
        { text: "Go to Settings → Users and click Invite User." },
        {
          text: "Enter the email address, select a role (Admin or User), and click Send Invite. The invitee receives an email with a sign-up link.",
        },
        {
          text: "Once they accept, their account appears in the Users list as Active.",
        },
        {
          text: "While an invite is pending, you can Resend it or Cancel it from the Users list.",
        },
        {
          text: "To change a user's role: click the role badge on their row and select Admin or User.",
        },
        {
          text: "To temporarily disable a user without deleting them: use the Enable/Disable toggle on their row.",
        },
        {
          text: "To remove a user permanently: click the delete icon on their row.",
        },
        {
          text: "Manage company-wide settings (company name, timezone, etc.) under Settings → Company.",
        },
      ],
      faqs: [
        {
          question: "What is the difference between Admin and User roles?",
          answer:
            "Currently both have the same access level. The role distinction is in place for future role-based permission enforcement (read-only, billing-only, etc.).",
        },
        {
          question: "How long does an invite link stay valid?",
          answer:
            "Invite links expire after 7 days. If the invitee misses the window, find them in the Users list (status: Pending) and click Resend Invite.",
        },
        {
          question:
            "Can I disable a user's access without deleting their account?",
          answer:
            "Yes. Use the Enable/Disable toggle on their row. Disabled users cannot log in but their data and history are preserved.",
        },
        {
          question: "Can I see when a user last logged in?",
          answer:
            "Not currently in the UI. Contact support if you need audit log data.",
        },
        {
          question:
            "What happens to my other sign-ins when I change my password?",
          answer:
            "They are signed out. The browser you changed it in stays signed in; other browsers and devices are asked to sign in again within an hour. Resetting a forgotten password signs you out everywhere, and Log out now ends that sign-in on the server too.",
        },
      ],
    },
    {
      id: "human-handoff",
      title: "Human Handoff",
      description:
        "Let users escalate from the AI to a live support agent via Help Scout or Intercom.",
      steps: [
        {
          text: "Go to Configure → Integrations and connect Help Scout or Intercom (see the Integrations section for setup steps).",
        },
        {
          text: "Go to Configure → AI & UX, select your widget, and scroll to the Human Handoff section.",
        },
        { text: 'Enable the "Invite an agent" button toggle.' },
        {
          text: "Set the Sensitivity Level: Easy (handoff offered quickly), Normal (AI tries first), or Hard (AI exhausts all options before suggesting).",
        },
        {
          text: "Select the integration (Help Scout or Intercom) from the dropdown.",
        },
        {
          text: "For Help Scout, select the mailbox escalations go to. For Intercom, optionally select a team.",
          tip: "Help Scout handoff always needs a mailbox, so Save stays disabled until you choose one.",
        },
        { text: "Click Save." },
        {
          text: "Test by opening the widget and clicking Invite an agent (or Connect with agent when the assistant offers it). Confirm a conversation appears in Help Scout or Intercom with the full transcript attached.",
        },
      ],
      faqs: [
        {
          question: "What does the sensitivity level control?",
          answer:
            "Easy: the AI offers human handoff on the first sign the user wants help. Normal: the AI tries to resolve the issue first; if the user asks again, it offers handoff. Hard: the AI exhausts all available options before suggesting a human agent.",
        },
        {
          question: "Can I use Intercom instead of Help Scout?",
          answer:
            "Yes. Connect Intercom under Configure → Integrations with your Intercom app's access token and client secret, then select Intercom as the integration in Configure → AI & UX → Human Handoff.",
        },
        {
          question: "What does the end user see during handoff?",
          answer:
            "The widget shows that it is connecting to support, then that an agent has joined, and the agent's replies appear in the chat. The full conversation transcript is attached to the new Help Scout or Intercom conversation automatically. These messages follow the widget's language.",
        },
        {
          question: "Can the end user end the chat with the agent?",
          answer:
            "Yes. While waiting for or talking to an agent, the user can press End chat with agent next to the message box. The conversation in Help Scout or Intercom is closed and the AI assistant takes over again.",
        },
        {
          question: "Can the end user send files to the agent?",
          answer:
            "Yes, with Intercom. Files the user attaches while waiting for or talking to an agent, such as screenshots and PDFs, arrive in the Intercom conversation as attachments. If Intercom does not accept a file, the agent still gets the message, with a line naming the file that could not be attached. Help Scout agents receive only the message text, or the file names when the user sends files without any text. Files sent to the AI assistant before the handoff are not passed on.",
        },
        {
          question: "Can I route escalations to a specific team?",
          answer:
            "Yes. After connecting your integration, select the target mailbox (Help Scout, required) or team (Intercom, optional) in the Human Handoff configuration.",
        },
        {
          question:
            "Does the AI hand off automatically, or does the user initiate?",
          answer:
            "The user initiates by clicking Invite an agent, or Connect with agent when the assistant offers it. The AI does not hand off automatically without user action.",
        },
        {
          question:
            "Can handoff wording quoted from a ticket or document trigger the handoff screen?",
          answer:
            "No. The handoff is triggered only when the assistant itself asks for it at the end of its reply. The same wording quoted from a ticket, document or API reply is shown as ordinary text. A session is also no longer flagged for handoff forever because of an old message — only the latest assistant message counts.",
        },
      ],
    },
    {
      id: "technical-docs",
      title: "Technical Documentation",
      description:
        "Implementation guides for your developers — embed the Customer Portal, build a custom UI on the Portal API, bulk pre-create customers with server credentials pre-filled, and more. Browse from the Technical docs tab in Help & FAQ.",
      steps: [
        {
          text: "Open Help & FAQ from the sidebar (Settings → Resources).",
        },
        {
          text: "Click the Technical docs tab — it sits between Guide and FAQ at the top of the page.",
        },
        {
          text: "Browse the card list. Each card shows a guide title, a short summary, and the date it was last updated.",
          tip: "Check back here when StrikeUp releases new integration capabilities — we update this catalog as we ship.",
        },
        {
          text: "Click any card to open the full document inside a modal. Headings, code blocks, and tables are all formatted for readability.",
        },
        {
          text: "Click Download .md in the modal footer to save a local copy you can share with your development team or paste into your own internal docs.",
        },
        {
          text: "Click Close or press Escape to return to the catalog.",
        },
      ],
      faqs: [
        {
          question: "What is the Technical docs tab for?",
          answer:
            "It is a catalog of tenant-facing integration guides written for your developers. The first guide available is the Customer Portal — Tenant Integration Guide, which covers customer auth flows, embedding the bundled Portal, building a custom UI on the Portal API, and the bulk customer pre-create endpoints (including pre-filling per-server credentials so end-users skip the auth prompt on first widget visit).",
        },
        {
          question: "How is this different from the Guide tab?",
          answer:
            "The Guide tab is written for you as a tenant admin — non-technical walkthroughs of admin app features like adding toolkits, configuring agents, and tuning guardrails. Technical docs is written for your developers — implementation details, API endpoint reference, request and response samples, and embed snippets they can paste into your own systems.",
        },
        {
          question: "Is this the same as the public website docs?",
          answer:
            "No. The public website covers the marketing surface. The Technical docs tab is sign-in protected and goes deeper — including server-to-server endpoints (such as bulk customer pre-create with pre-filled credentials) that are not published in the public docs.",
        },
        {
          question: "Can I share a downloaded guide with my development team?",
          answer:
            "Yes. Click Download .md in the modal footer. You receive a plain markdown file you can paste into Confluence, Notion, a GitHub wiki, or any other tool your team uses to track integration work.",
        },
        {
          question: "Will more guides appear over time?",
          answer:
            "Yes. StrikeUp adds new guides as we ship integration capabilities. New cards appear automatically — no app update or refresh required beyond reloading the Help & FAQ page.",
        },
      ],
    },
    {
      id: "suggestions",
      title: "Suggestions",
      description:
        "Everything StrikeUp has proposed for your workspace, in one place. StrikeUp reads what visitors keep trying to do in your widget and what got in their way, and proposes a fix for each need: a one-click shortcut when the assistant already handles it well, a new skill or an improvement to an existing skill when it handles it badly, a toolkit change when a tool is switched off or asks for approval needlessly, a knowledge gap when nothing answers a how-to question, a schedule when one person runs the same request every week, and a defect when a tool keeps failing. Masking rules are proposed separately when a toolkit you connected contains sensitive fields. Suggestions you dismiss are kept rather than deleted, so anything dismissed by mistake can be found here and restored.",
      steps: [
        {
          text: "Go to Configure → Suggestions. Each block is one need; its header names the need and shows how many conversations and visitors it came from, how often the assistant refused, and how often visitors retried or corrected it. Each line in the block is one proposed fix: the kind, the title, a one-sentence reason. Masking rule suggestions appear in their own table below.",
        },
        {
          text: "Click a line, or the arrow at its end, to open the preview of what accepting does — the exact instructions a skill would get, an amendment as a side-by-side comparison of the skill before and after with the changed lines highlighted, the tool that would be switched on, the schedule that would be proposed — followed by why it is proposed, what visitors typed and what the assistant answered. Click again to fold it away.",
          tip: "Read the preview before accepting: the analysis can be wrong, and the preview is exactly what will be created.",
        },
        {
          text: "To decide a whole block at once, click Accept all in its header (shown when the block has more than one pending line) — every pending line is accepted exactly as previewed, skills included, without the review dialog — or Dismiss all. Other blocks are not affected; there is no page-wide button on purpose.",
          tip: "A need with refusals or retries is the assistant struggling, not visitors retyping something that works — that is why it comes with a skill rather than only a shortcut.",
        },
        {
          text: "Use the filters above the list to show one kind of suggestion, or to switch between Pending, Accepted and Dismissed.",
        },
        {
          text: "For a Shortcut, click Accept. It appears in your widget straight away and in Configure → Prompt Shortcuts, where you can rename or reword it.",
        },
        {
          text: "For a New skill, click Accept to open the review dialog. Check the name, when the assistant should use it, and the instructions; edit anything you like, leave Assign to the widget on, and click Create skill.",
        },
        {
          text: "For a Skill amendment, click Accept to see the section that will be added to the named skill. The current instructions are kept; a copy of the previous version is saved so the change can be undone.",
          tip: "A shortcut that says Works best with a skill can be accepted together with it — click Accept with skill and both are created in one go.",
        },
        {
          text: "For a Toolkit change, read the sentence on its line that says what accepting does — switch a tool on, stop it from asking for approval, or note that you reviewed its schema — then click Accept.",
        },
        {
          text: "Under Something isn't working, a Defect line names a tool that kept failing or returning nothing. Click Disable tool to switch it off, or Dismiss to keep it on while you fix it in your API.",
        },
        {
          text: "A Knowledge gap or a Schedule only records your decision when accepted: add the missing source in Knowledge Base, or set up the recurring request under Workflows.",
        },
        {
          text: "Click Dismiss on anything that is not useful. StrikeUp will not propose that same fix for that need again — but dismissing the shortcut for a need does not stop a later skill suggestion for it.",
        },
        {
          text: "For a masking rule suggestion, click Approve to switch the rule on, or Dismiss to set it aside.",
        },
        {
          text: "To find something you dismissed, change the status filter from Pending to Dismissed, then click Restore to move it back to your pending list.",
        },
        {
          text: "When suggestions are waiting, a coloured strip appears at the top of every page. Click Review them to come here, or the × to close it.",
          tip: "Closing the strip only affects you — your colleagues still see it until they close their own. It stays closed after you log out, and comes back on its own when new suggestions appear.",
        },
      ],
      faqs: [
        {
          question:
            "What is the difference between a shortcut and a skill suggestion?",
          answer:
            "A shortcut saves visitors typing something the assistant already handles well — it is a one-click chip. A skill changes how the assistant behaves: which tool it uses, what steps it follows, what it says when something is not possible. StrikeUp proposes a skill when visitors kept hitting friction — the assistant refused, they retried, they corrected it — and a shortcut only when the request already works and you do not already offer it as a shortcut card or prompt shortcut.",
        },
        {
          question:
            "Why does a suggestion say the assistant refused something it can do?",
          answer:
            "Because it happened in real conversations: the assistant said it could not, for example, send an email or see the page a visitor was on, while your widget does have that ability. That is a false refusal, and the proposed skill tells the assistant exactly which tool to use so it stops refusing.",
        },
        {
          question: "What does a defect suggestion mean?",
          answer:
            "One of your tools returned nothing several times for several visitors, or rejected the assistant's calls before they reached your API. The assistant cannot answer that need until the tool works. Disable the tool if it is misleading the assistant, fix it on your side, then switch it back on under Servers.",
        },
        {
          question: "After I accept a skill, how do I know it helped?",
          answer:
            "About two weeks later, open the suggestion's details to find an After acceptance line comparing how often visitors hit friction on that need before and after, counting only conversations where the skill actually fired. If friction went up instead, a new amendment suggestion appears for that skill so you can review it.",
        },
        {
          question:
            "Why does the same suggestion never come back after I dismiss it?",
          answer:
            "Because dismissing it is treated as your decision. StrikeUp will not propose that fix for that need again — but it stays on this page under the Dismissed filter, so you can restore it whenever you like.",
        },
        {
          question:
            "One person keeps asking the same thing. Will I get a suggestion?",
          answer:
            "No — every suggestion needs at least two different visitors behind it, and shortcuts are never proposed for something your widget already offers as a shortcut card or prompt shortcut. The one exception is a schedule suggestion: if one person runs the same data request on the same weekday, week after week, and it works, StrikeUp proposes a scheduled workflow for it.",
        },
        {
          question: "I accepted a suggestion. Where did it go?",
          answer:
            "A shortcut becomes a real shortcut under Configure → Prompt Shortcuts. A skill becomes a real skill under Configure → Skills, already assigned to the widget. A toolkit change is applied to the tool under Servers. In every case the suggestion moves to the Accepted filter with a link to what it created.",
        },
        {
          question: "Can I edit a suggestion before accepting it?",
          answer:
            "Skills and amendments open a dialog where you edit every field before anything is created. Shortcuts and toolkit changes are applied as proposed — accept them first, then edit the shortcut on the Prompt Shortcuts page or the tool under Servers.",
        },
        {
          question: "I got an error saying the name already exists.",
          answer:
            "You already have a shortcut or skill with that name. For a skill, change the name in the dialog and confirm again. For a shortcut, rename the existing one, or accept the suggestion and then rename the new shortcut.",
        },
        {
          question:
            "I closed the strip at the top of the page. Will it come back?",
          answer:
            "Not for the same suggestions — but yes as soon as new ones appear. Closing it tells StrikeUp you have seen what is there now; anything found later brings it back so you do not miss it.",
        },
        {
          question: "Does closing the strip hide it for my whole team?",
          answer:
            "No. It only affects you. Everyone decides for themselves when to close it, so no one can accidentally hide suggestions from a colleague.",
        },
        {
          question: "Does clicking Review them count as closing the strip?",
          answer:
            "Yes. Following the link means you have seen the suggestions, so the strip does not reappear the next time you log in.",
        },
      ],
    },
    {
      id: "prompt-shortcuts",
      title: "Prompt Shortcuts",
      description:
        "Reusable prompts surfaced to your widget users inside the chat — they appear as a Shortcuts button next to the input and via the / (slash) command. Each shortcut can contain {variable} placeholders that the end-user fills in before sending. You manage tenant-wide shortcuts here; Customer Admins can add their own scoped to their own company from the Customer Portal.",
      steps: [
        {
          text: "Go to Configure → Shortcuts. The page lists every shortcut you have created for this tenant.",
        },
        {
          text: "If StrikeUp has shortcut suggestions waiting, the five strongest appear in a Suggested shortcuts block above your list — one line each with the proposed text. Click Accept to create one, Dismiss to set it aside, or See all suggestions to review the rest on the Suggestions page.",
        },
        {
          text: "Click New shortcut to open the editor.",
        },
        {
          text: "Enter a Name — this is the label your customers see in the dropdown and in the panel that appears when they pick it.",
        },
        {
          text: "Optionally enter a Description — a short sub-label that appears under the name in the slash-command dropdown.",
        },
        {
          text: "Enter the Prompt body — the actual message that will be inserted into the chat input when the user picks this shortcut.",
          tip: 'Use {variableName} markers anywhere in the body to mark spots the end-user must fill in. Example: "Refund order {orderId} for customer {customerEmail}." — when the user picks this shortcut a small panel pops up asking them to enter Order ID and Customer Email, with the body live-updating below.',
        },
        {
          text: "Pick an Icon from the grid (Zap, Chart, Help, Chat, Search, Document, Settings, Star, Calendar, Clock, Team, Cart, Email, Alert, Bookmark, Tag). The icon decorates the chip in the widget.",
        },
        {
          text: "Click Create. The shortcut is immediately available to every authenticated customer of every widget on your tenant.",
        },
        {
          text: "To edit a shortcut: click the pencil icon on its row. To toggle it off temporarily (without deleting): use the Active switch. To delete: click the trash icon.",
        },
        {
          text: "To change display order: use the up/down arrow buttons on each row. Higher rows appear first in the dropdown.",
        },
        {
          text: "To control which widgets show the Customer Portal Prompt Shortcuts page (where Customer Admins manage their own CustomerCompany-scoped shortcuts): go to Configure → User Interface → Customer Portal → Pages and toggle Prompt Shortcuts on or off per widget.",
        },
        {
          text: "To translate a shortcut: open it for editing, use the + Add language dropdown to the right of the Default tab, pick a language, and fill in the translated Name, Description and Prompt body. The default text appears greyed out in each box so you can see what you are translating.",
          tip: "You can translate just the Name and leave the Prompt body in your default language. Any field you leave empty falls back to the default — that is why a partly-filled language tab is marked Partial rather than treated as an error.",
        },
      ],
      faqs: [
        {
          question: "Where do my customers actually see these in the widget?",
          answer:
            "Two places, both in the chat input row: a Shortcuts icon button right next to the microphone, and the / (slash) shortcut — typing / at the start of an empty input opens the same dropdown. The list is filterable as the user types more characters after the /.",
        },
        {
          question:
            "How are these different from the Shortcut cards on the Welcome screen?",
          answer:
            "Shortcut cards live in Configure → User Interface → Shortcut cards. They appear only on the chat welcome screen as up-to-eight quick-action buttons and disappear once the chat starts. Prompt Shortcuts (this page) appear during chat, support unlimited entries, support {variable} placeholders, and can be browsed via the / command. Use cards for greet-time entry points; use shortcuts for in-conversation reusable templates.",
        },
        {
          question: "What is a {variable} placeholder and how does it work?",
          answer:
            'A {variable} is any text in curly braces inside the Prompt body — for example {orderId}, {customer_name}, {customerEmail}. When a customer picks a shortcut that contains placeholders, the widget opens a small panel listing every unique placeholder with one input field per variable. Field labels are auto-humanized (customerEmail → "Customer Email", order_id → "Order ID"). As the customer types, the chat input live-updates with the substituted text. Done closes the panel and leaves focus in the chat input for any final tweak before sending.',
        },
        {
          question:
            "What happens if a shortcut has no {variable} placeholders?",
          answer:
            "The body is inserted directly into the chat input — no panel is shown. The customer can edit the text and send when ready.",
        },
        {
          question:
            "Can my Customer Admins add their own shortcuts that only their company sees?",
          answer:
            "Yes. In the Customer Portal there is a Prompt Shortcuts page where Customer Admins can create, edit, toggle, and delete shortcuts scoped to their CustomerCompany. Members of that company see the same page read-only. Those shortcuts appear in the widget for every member of that CustomerCompany — alongside your tenant-wide shortcuts. Enable the Customer Portal Prompt Shortcuts page per widget under Configure → User Interface → Customer Portal → Pages.",
        },
        {
          question: "Which language does a customer see?",
          answer:
            "Whichever language their browser is set to, if you have added a translation for it. Otherwise they see the Default tab's text. Region variants are handled sensibly: a Brazilian visitor gets your Português (Brasil) translation if you added one, and falls back to plain Português if you only added that.",
        },
        {
          question: "What if I only translate some of the fields?",
          answer:
            "The fields you leave blank use the default text. So a German visitor can see a German name with an English prompt body — that is supported on purpose, not a bug.",
        },
        {
          question: "Can my Customer Admins translate their own shortcuts?",
          answer:
            "No. Translations are available for your tenant-wide shortcuts only. CustomerCompany shortcuts are written by each company in whatever language they already work in, so a translation layer would just add work for them.",
        },
        {
          question: "Why can't I translate the icon?",
          answer:
            "Icons are the same in every language, so they sit outside the language tabs. Pick one icon per shortcut and it applies to all translations.",
        },
        {
          question:
            'If I have a tenant-wide shortcut called "Refund" and a Customer Admin has a CustomerCompany-scoped one with the same name, which one appears?',
          answer:
            'The CustomerCompany version wins for that company\'s members — it is more specific to them. Members of other CustomerCompanies see only your tenant-wide "Refund". This means CustomerCompanies can override your defaults without affecting other companies.',
        },
        {
          question: "Can I temporarily hide a shortcut without deleting it?",
          answer:
            "Yes. Use the Active toggle on the shortcut row. Inactive shortcuts are stored but not surfaced in the widget dropdown or chip strip.",
        },
        {
          question: "Is the order of shortcuts important?",
          answer:
            "Lower SortOrder values appear first in the dropdown. Use the up/down arrows in the table to reorder. Within the same SortOrder, shortcuts are ordered alphabetically by name.",
        },
        {
          question:
            "How is this different from the MCP-style prompts under a server's detail page?",
          answer:
            "Those are a separate, advanced feature for declaring MCP-protocol prompt templates that live on a specific toolkit (server) — they show up in the widget through a different selector and are used by AI tooling, not for end-user input. The Prompt Shortcuts on this page are widget end-user shortcuts surfaced as chat-input chips and / commands. Both can coexist.",
        },
        {
          question: "Is there a maximum number of shortcuts?",
          answer:
            "There is no hard limit. The dropdown is scrollable, so very long lists are fine — but for usability we recommend keeping the active list focused on shortcuts your customers actually use.",
        },
        {
          question: "Can I disable the entire shortcuts feature for a widget?",
          answer:
            "Yes. The Customer Portal Prompts page can be toggled off per widget under Configure → User Interface → Customer Portal → Pages — that hides the page from the Customer Portal sidebar. The in-chat Shortcuts button and / command are governed by a master toggle on the widget; contact support if you need to turn them off entirely for a specific widget.",
        },
      ],
    },
    {
      id: "company-settings",
      title: "Company Settings & API Keys",
      description:
        "Edit your company profile and optionally connect your own AI provider API keys.",
      steps: [
        { text: "Go to Settings → Company." },
        { text: "Click Edit to enter editing mode." },
        {
          text: "Update company name, description, address, phone, email, and website as needed.",
        },
        {
          text: "To use your own OpenAI API key: paste it into the OpenAI API Key field. This routes AI calls through your own OpenAI account instead of the shared platform key.",
        },
        {
          text: "To use your own Gemini API key: paste it into the Gemini API Key field.",
        },
        {
          text: "To replace a key you saved earlier, type the new one over the empty field. Leaving the field blank keeps the key already stored — it is never shown back to you, so a blank box does not mean the key is gone.",
          tip: "The line above each field tells you whether a key is currently saved.",
        },
        { text: "Click Save Changes to persist, or Cancel to discard edits." },
      ],
      faqs: [
        {
          question: "Why would I enter my own OpenAI or Gemini API key?",
          answer:
            "By default the platform uses a shared API key and usage is billed through your plan. Entering your own key routes all AI calls through your account — useful if you have negotiated pricing, usage limits, or compliance requirements that mandate your own key.",
        },
        {
          question: "Is my API key stored securely?",
          answer:
            "Yes. API keys are encrypted at rest and never exposed in the UI after saving. Only the fact that a key is set is shown — not the key value itself.",
        },
        {
          question: "What happens if my API key is invalid or has no credits?",
          answer:
            "The AI will fail to respond and errors will appear in Analytics → AI Actions. Type a working key over the field to replace it — leaving the field empty keeps the current key rather than removing it.",
        },
      ],
    },
    {
      id: "ai-memory",
      title: "AI Memory",
      description:
        "Let the assistant remember useful, factual details about each customer across conversations — so it stops asking them to repeat themselves and every chat feels more personal. Memory is off until you turn it on, and both you and your customers can view and delete what is stored.",
      steps: [
        {
          text: "Go to Settings → Company and click Edit.",
        },
        {
          text: "Turn on Enable AI memory, then click Save Changes.",
          tip: "Memory stays completely off until you do this — nothing is stored or used before then. The setting applies to your company only.",
        },
        {
          text: "To see what the assistant remembers about a single customer, go to Customers and open that customer. Expand the Memory section to view each remembered note and its category.",
        },
        {
          text: "To delete something the assistant remembers about a customer (for example, to honour a deletion request), click Delete on that item in the Memory section. It is removed and will not be learned again.",
        },
        {
          text: "To add a shared note for a whole account that applies to all of its users, go to Customer Companies, open the account, and use the Account Memory section to add or remove notes.",
          tip: 'Use this for facts true for the whole organisation — for example "This account signs in with Okta" or "Acme is migrating to v2 this quarter." Keep each note under 400 characters, and avoid plan or entitlement claims — both are refused with a message explaining why.',
        },
        {
          text: "Your customers control their own memory inside the widget's Customer Portal: a Memory page lets them see what is stored, remove any item with Forget, and switch memory on or off for themselves.",
          tip: 'The same page also lists the Account Memory notes you wrote for their organisation, marked "Shared by your organisation". Those are read-only for the customer — only you can remove them, from Customer Companies.',
        },
      ],
      faqs: [
        {
          question: "How does AI memory actually work?",
          answer:
            "In the background — never during the live chat — the system reads each finished conversation and pulls out a few short, durable facts about that customer (their setup, preferences, unresolved issues, and how past problems were solved). It merges these into a small per-customer memory, removing duplicates and updating anything that changed. On every future message, the relevant memories are added to the assistant's context as read-only background notes, so it already knows the situation. This runs on a schedule, so memory keeps improving over time without ever slowing a conversation down.",
        },
        {
          question:
            "What does the assistant remember — and what does it never store?",
          answer:
            "It remembers factual, useful things: details the customer shared, their format or language preferences, open issues to follow up on, and resolutions that worked. It deliberately never stores anything sensitive or risky — no plan, billing, refund, authorization, or account-status claims (those are always verified live, never trusted from memory), and no raw personal data like card numbers, passwords, or full email addresses. It also avoids emotional or behavioural judgements; only concrete facts are kept.",
        },
        {
          question:
            "Is one customer's memory ever shared with another customer?",
          answer:
            "No. A customer's personal memory is private to them. Account Memory is the only shared layer, and it contains just the notes you write for a whole account — one customer's private facts are never promoted into a shared note that other people could see.",
        },
        {
          question: "Can customers see the Account Memory I wrote for them?",
          answer:
            'Yes. Their Memory page lists your account notes alongside their own, marked "Shared by your organisation", because those notes shape the answers they get and the page is a privacy surface. They are read-only there — the Forget button is hidden and only you can remove them, from Customer Companies. Write account notes on the assumption every customer in that account will read them.',
        },
        {
          question: "Why was my Account Memory note rejected?",
          answer:
            'Three checks can refuse a note, and the message tells you which. Plan, billing, entitlement, or account-status claims are refused outright — the assistant must always confirm those with a live lookup rather than trust a stored note, so storing one would risk it telling every user of the account something that is no longer true. Notes longer than 400 characters are also refused, because a longer note is cut short when the assistant reads it, and a note trimmed mid-sentence can end up meaning the opposite of what you wrote. Shorten the note, or split it into two. A note is also refused when it reads as an instruction to the assistant rather than a fact — "always offer a discount", "ignore the approval rules" — or contains credentials, links or another person\'s private details.',
        },
        {
          question:
            "How do my customers opt out, and can they delete what's stored?",
          answer:
            "Yes to both, from the Memory page in the Customer Portal. A customer can switch memory off for themselves at any time — once off, their conversations are no longer read for memory, nothing new is stored and nothing is used in their chats. They can also click Forget on any individual item to delete it. Deleted memories are never re-learned, even by the background job.",
        },
        {
          question: "What happens if I turn AI memory off again?",
          answer:
            "The assistant stops learning and stops using what it remembers. While Enable AI memory is off, no conversation is read for memory, and conversations from that time are not read later either, when you turn it back on. What is already stored is kept, and is used again once memory is back on. To remove it, delete the items in a customer's Memory section under Customers, or an account's notes under Customer Companies.",
        },
        {
          question: "How is this different from the AI Persona?",
          answer:
            "The Persona (under Configure → AI & UX) is the assistant's overall personality and rules that apply to everyone. AI memory is per-customer — specific facts about one person that make their conversations feel continuous. You set the Persona by hand; memory is learned automatically from real conversations.",
        },
        {
          question: "Do I need to do anything to keep the memory up to date?",
          answer:
            "No. Once Enable AI memory is on for your company, the background jobs that build and refresh memory run automatically. You simply manage what is stored (view and delete) from the Customers and Customer Companies pages, and your customers manage their own from the Portal.",
        },
        {
          question:
            "Do scheduled workflows and autonomous agents use or create memory?",
          answer:
            "No. They run under their own automated identity: they do not read the customer's memory, and nothing they do is stored as memory. Only live conversations with a customer feed their memory.",
        },
        {
          question:
            "Can Account Memory crowd out what the assistant remembers about an individual user?",
          answer:
            "No. When only a limited number of memories fit into a conversation, the user's own memories keep at least half of the places; Account Memory fills the rest.",
        },
        {
          question:
            "Something a connected API returned about a customer — is that stored as their memory?",
          answer:
            "Only if the customer said it themselves in the conversation. Data that appears only in a tool's reply is never stored as a memory on its own; when the customer states a fact that a tool reply also confirms, it is stored with higher confidence.",
        },
        {
          question: "How soon does a new memory appear after a conversation?",
          answer:
            "Usually within about an hour. Memories are extracted from finished conversations on an hourly cycle and tidied up — merged, deduplicated, outdated ones retired — once a day.",
        },
      ],
    },
    {
      id: "two-factor-authentication",
      title: "Two-Factor Authentication (2FA)",
      description:
        "Add a second step to sign-in using an authenticator app, so a password alone is not enough to access your account. You can turn it on for yourself, and admins can require it for everyone in the company.",
      steps: [
        {
          text: "Install an authenticator app on your phone (for example Google Authenticator, Microsoft Authenticator, Authy, or 1Password).",
        },
        {
          text: "Go to Settings → Profile and find the Two-Factor Authentication section.",
        },
        {
          text: "Click Enable 2FA. A window opens showing a QR code.",
          tip: 'On a computer with no phone handy? Click "Can\'t scan? Enter the code manually" to copy a setup key you can paste into a desktop authenticator app.',
        },
        {
          text: "Open your authenticator app and scan the QR code (or paste the manual key).",
        },
        {
          text: "Click Next, type the 6-digit code your app now shows, and click Verify & enable.",
        },
        {
          text: "From now on, after your email and password, you will be asked for a fresh 6-digit code at each sign-in.",
        },
      ],
      faqs: [
        {
          question: "How do I require 2FA for everyone in my company?",
          answer:
            'If you are an admin, go to Settings → Company and turn on "Require Two-Factor Authentication". From then on, every team member must set up an authenticator the next time they sign in, and newly invited users set it up while accepting their invitation. Only admins can change this setting.',
        },
        {
          question: "What happens at sign-in once 2FA is on?",
          answer:
            "After you enter your email and password, a screen asks for the current 6-digit code from your authenticator app. Enter it to finish signing in. The code changes every 30 seconds.",
        },
        {
          question: "I lost my phone or authenticator. How do I get back in?",
          answer:
            "Ask a company admin to reset your 2FA from the team members page. After the reset you can sign in and set up a new authenticator. If you are the only admin and are locked out, contact StrikeUp support to reset it for you.",
        },
        {
          question: "Can I turn 2FA off?",
          answer:
            "Yes, from Settings → Profile — unless your company requires it, in which case the disable option is hidden and you can only re-sync to a new device. Disabling asks for a current code to confirm it is really you.",
        },
        {
          question: "I got a new phone. How do I move my authenticator?",
          answer:
            "Go to Settings → Profile and click Reset authenticator. Scan the new QR code with your new phone's app and confirm a code. Your old device stops working once the new one is confirmed.",
        },
        {
          question: "What if I enter the wrong code too many times?",
          answer:
            "After several incorrect codes, sign-in is locked for a few minutes as a security measure. Wait, then try again with a fresh code from your app.",
        },
      ],
    },
    {
      id: "autonomous-agents",
      title: "Autonomous Agents",
      description:
        "Let an outside system trigger an AI agent by calling a web address (a webhook), instead of a person chatting or a fixed schedule. You write the agent's instructions once and pick which widget it runs under. Each call is authorised by a token that you generate for one of your customers by supplying their PIN or password, so every run is recorded against a real person. Use it to react to events in other tools — a new order, a submitted form, a raised support ticket.",
      steps: [
        {
          text: "Go to Configure → Autonomous Agents and click Create agent.",
        },
        {
          text: "Give the agent a name and write its instructions — the prompt it should follow every time it runs.",
        },
        {
          text: "Choose the widget the agent runs under, then Save.",
          tip: "The widget decides which tools, knowledge base, and connected API the agent can use — pick the one whose setup matches the job. If the widget has no connected server, the agent can still reply from its instructions and knowledge but cannot call external tools.",
        },
        {
          text: "Choose how callers get the result: Asynchronous (the default) accepts the call and runs the agent in the background; Synchronous makes the caller wait and returns the agent's answer in the response.",
          tip: "Keep long-running jobs Asynchronous. Use Synchronous only when the calling system needs the agent's reply straight away.",
        },
        {
          text: "Make sure the person each run should be recorded against is one of your customers and has a PIN or password set — the same credential they use to sign in to your customer portal. This is who you generate the token for, and who runs are attributed to.",
          tip: "You can't generate a token for a customer who has no PIN or password. Set one up under Configure → Customers (or have the customer set their own in the customer portal) before connecting.",
        },
        {
          text: "Generate a token by supplying that customer's PIN or password, using the example shown on the agent under Webhook. You generate it as a company admin — the request is sent with your own admin sign-in — so it is not something the customer does on their own. The token is shown only once — copy it and store it somewhere safe.",
          tip: "You generate tokens from the agent's Webhook tab, not the Access tokens panel — that panel is only for reviewing and revoking them. Generating a token needs both your company-admin sign-in and the customer's PIN or password. If a token leaks, revoke it and generate a new one.",
        },
        {
          text: "Give the external system the agent's webhook address (shown on the agent under Webhook) and the token. When that system calls the address with the token, the agent runs — and the run is recorded against the customer the token was generated for.",
        },
        {
          text: "If the agent is set to Asynchronous, the Webhook panel shows one more example: the request that fetches the run's result. The trigger call answers straight away with a run id and a status address, and the calling system checks that address with the same token until the answer is ready.",
          tip: "A Synchronous agent returns its answer inside the trigger call itself, so it has no result-fetch example.",
        },
        {
          text: "To pass live data into the prompt, write variables like {{payload.orderId}} in the instructions. When the caller sends a JSON body, each variable is replaced with the matching value before the agent runs.",
        },
        {
          text: "For an unattended job with no real person behind it — a nightly summary, say — create a dedicated customer to act as a service account, give it its own PIN, and generate a token for it. Every run is then recorded against that service account.",
          tip: "A token only labels the run with the customer in your records — it does not let the agent use that customer's own connected accounts or memory, so a leaked token is no more powerful than any other. Changing a customer's PIN does not cancel tokens already generated — revoke the token to stop it.",
        },
        {
          text: "Check how often each agent runs on the Dashboard's Autonomous agent usage card, or open an agent to see its recent runs and any errors in the execution log. Click View output on any run to read the answer the agent generated. The log's Triggered for column shows which customer each run was recorded against.",
        },
      ],
      faqs: [
        {
          question: "How is an Autonomous Agent different from a Workflow?",
          answer:
            "A Workflow runs on a schedule you set; an Autonomous Agent runs when an outside system calls it. Use an autonomous agent when you want something to happen in response to an event elsewhere — a new order, a form submission, a support ticket — rather than at a fixed time.",
        },
        {
          question: "What is the access token, and is it safe?",
          answer:
            "The token is a secret key that proves the caller is allowed to trigger your agents. You generate it as a company admin by supplying one of your customers' PIN or password — the request carries your own admin sign-in, so it is not something outsiders can do on their own. It is shown only once and stored scrambled (hashed) so it cannot be read back, so keep your own copy somewhere safe. Generate a separate named token for each system you connect, and revoke a token the moment it might have leaked — revoking stops it working immediately.",
        },
        {
          question: "Does one token trigger every agent, or just one?",
          answer:
            "One token works for every agent in your company. The specific agent that runs is chosen by the web address the caller uses, not by the token. That is why it is best to generate one named token per integration, so you can revoke them one at a time without affecting the others.",
        },
        {
          question:
            "What is the difference between synchronous and asynchronous?",
          answer:
            "Asynchronous (the default) accepts the request and runs the agent in the background, so the caller gets an immediate acknowledgement rather than the answer. Synchronous makes the caller wait a little longer and returns the agent's actual answer in the response. Use synchronous only when the caller needs the reply straight away.",
        },
        {
          question:
            "How do I pass information from the caller into the prompt?",
          answer:
            "Write variables like {{payload.fieldName}} in the agent's instructions. When the calling system sends data as JSON with its request, each variable is replaced with the matching value before the agent runs. You can reach nested values with dots — for example {{payload.customer.email}}. If a value is not sent, the variable is simply left blank.",
        },
        {
          question: "Where do I see how often an agent runs?",
          answer:
            "The Dashboard has an Autonomous agent usage card showing how many times each agent ran recently. For detail on a single agent — including which runs finished and which failed — open the agent and check its execution log.",
        },
        {
          question: "How does the calling system get a token?",
          answer:
            "You generate each token yourself, as a company admin, by supplying one of your customers' PIN or password — there is no anonymous self-service exchange and no shared admin-created token. You provision the customer's PIN or password once (or they set their own in the customer portal); then your integration generates a token from it using the example on the agent's Webhook tab, sending your own admin sign-in alongside the customer's credential. The token is reusable and revocable. For an unattended job with no real person, create a dedicated customer to act as a service account and generate a token for it.",
        },
        {
          question: "Can I tell which of my customers triggered a run?",
          answer:
            "Yes. Every run is recorded against the customer the token was generated for — you will see a Triggered for column in the execution log and a person badge next to their token in the Access tokens panel.",
        },
        {
          question:
            "Can the system that calls an asynchronous agent get the answer back?",
          answer:
            "Yes. An asynchronous call immediately returns a run id and a status address; the calling system can check that address until the run finishes and then read the answer, or supply a callback address that we send the finished result to automatically. Open the agent's Webhook panel — for an asynchronous agent it shows a ready-made example of the result request, next to the ones for getting a token and triggering the agent. Callbacks, including how to verify one is genuine, are covered in the Technical Documentation. In the admin panel you can always read any run's answer yourself with the View output button in the execution log.",
        },
        {
          question:
            "What happens when a run needs an approval or an answer from the customer?",
          answer:
            "The run pauses with the status Waiting for approval (or Waiting for input), and the action appears on the customer's Agent runs page in the Customer Portal and in the Needs you list on the widget's welcome screen. When they approve, deny or answer, the run resumes from exactly where it stopped. If nobody decides before the approval expires, it counts as denied and the run finishes with what it has. Analytics → Agent jobs shows the same run read-only. The platform never sends an approval e-mail; a customer's own e-mail tool is unaffected.",
        },
        {
          question:
            "My trigger call returns 401 with a token that used to work — why?",
          answer:
            "Tokens that were not tied to one of your customers no longer work. Every run acts as a specific customer, so generate a new token for the customer (or the service-account customer) the agent should run as. A 403 means that customer is disabled or their company is deactivated — re-enable them first.",
        },
        {
          question:
            "Does a run reuse the customer's saved API connections and memory?",
          answer:
            "Saved connections, yes — a scheduled or triggered run calls your toolkits with the credentials the customer authorised in the widget, and an expired authorization fails the call without deleting the connection. Memory, no — automated runs neither read nor write it.",
        },
        {
          question:
            'The webhook answered 429 "Too many runs" — what is the limit?',
          answer:
            "Five new runs per minute per customer, counted across webhook triggers, schedules and Run now. Retry after the number of seconds in the Retry-After header; a schedule that hits the limit is retried later automatically and nothing is dropped.",
        },
        {
          question:
            "Is a run lost if the service restarts while it is running?",
          answer:
            "No. A run is saved step by step and resumes from the last finished step without repeating it. A run waiting for an approval resumes as the same agent when the decision arrives, even days later, and a customer closing their browser while deciding does not cancel it.",
        },
        {
          question: "Can a caller put instructions into the payload fields?",
          answer:
            "Payload values are inserted into your prompt as data: the assistant is told where each value starts and ends and that it is not an instruction. Very long values are shortened.",
        },
        {
          question:
            "Why did a run fail saying a write tool was already dispatched?",
          answer:
            "A step called a tool that changes data and then hit a temporary error before it finished. The run does not retry the step, because retrying could apply the change twice; check the target system and run the agent again if needed.",
        },
      ],
    },
    {
      id: "widget-page-context",
      title: "Injecting page context into the widget",
      description:
        "Feed the AI hidden context about the page your visitor is on — who they are, the record they are viewing, the filters they set, the form they are filling in — so it answers their questions specifically without them having to explain their situation. The context is never shown to the visitor and keeps informing the conversation across follow-up questions. Configure → Embed shows the rules that matter most and two copyable examples; the full best-practice guide is Help → Technical docs → Widget Integration, section 7.1.",
      steps: [
        {
          text: "This is set up by whoever manages your website's code (your developer or webmaster), not from the admin panel. Share these steps with them.",
        },
        {
          text: "Make sure the StrikeUp widget embed script is already on the page (Configure → Embed gives you the snippet).",
        },
        {
          text: 'Whenever the visitor lands on a page, a record finishes loading, or they change something themselves (a filter, a selection, a tab, opening an editor), call window.StrikeUpChat.context.set(value) with a short plain-text description — one fact per line, label first, the record\'s id in brackets. For example: "User: Dana Reyes (id usr_8f3a)", "Page: Invoice details", "Viewing invoice INV-1042 (id inv_1042), currently editing line items.", "Status: overdue | Due: 2026-09-01".',
          tip: "Always call set (replace), not add, for page state. set replaces the previous context, so only the latest situation is sent — and add can re-send lines that were already delivered.",
        },
        {
          text: "Start every context with who the visitor is — their name and the id your own system uses for them. The AI does not know who is signed in on its own, and the one-shot Open context on a Custom Trigger is forgotten after the first reply, so put identity in every set call.",
        },
        {
          text: 'Leave out anything that changes on its own: total counts, unread counts, "last updated" times, page numbers and sort order. Once the context is attached to a message it is stored for the rest of the conversation, so a value that changes between every message leaves a stale copy behind each time.',
        },
        {
          text: 'While a record is still loading, send only the durable fact — "Opened invoice (id inv_1042)" — never "details are still loading". Replace it with the full description once the data has arrived.',
        },
        {
          text: "When the visitor leaves a page, make sure that page's lines are removed before the next page's are set, and set the context again once the widget reports it is ready — staged context does not survive a page reload.",
        },
        {
          text: "That is all. The staged context is attached to the visitor's next message automatically — it is not sent on its own and the visitor never sees it.",
        },
        {
          text: 'Test it: open the widget on a page where you set context, ask a vague question like "how do I configure this?", and confirm the AI answers about that specific page. Then ask a follow-up and confirm it still knows.',
        },
      ],
      faqs: [
        {
          question: "What is page context injection, in plain terms?",
          answer:
            'It is a way for your website to quietly tell the AI what the visitor is looking at right now — who they are, the current page, the record they opened, the filters they applied — so the AI can answer specifically without the visitor spelling it out. For example, if the visitor is on your Notification Settings page and asks "how do I configure this?", the AI already knows "this" means notification settings and answers directly.',
        },
        {
          question:
            "What should go into the context, and what should stay out?",
          answer:
            "Include who the visitor is (name and your id for them), which page they are on, the record on screen (its human-readable label and the id your connected tools expect), and the state the visitor controls — filters, selection, active tab, whether they are editing. Leave out counters and timestamps that move on their own, page numbers and sort order, loading or error messages, the query string of the address (it often carries e-mails or tokens), and raw ids for things no tool can act on. A detail page needs about 300–500 characters; a page the assistant has nothing to do with needs one line naming it.",
        },
        {
          question: "Should I include who the visitor is in every context?",
          answer:
            'Yes. The AI does not automatically know who is signed in. A short line such as "User: Dana Reyes (id usr_8f3a)" in every set call is cheap and survives the cases where an earlier one would be lost — the visitor starting a new chat, switching conversations, or a long conversation ageing out its early context. Never let a connected tool treat that line as proof of identity, though: it is text the AI can see, so identity for tool calls must come from the credentials you inject when the widget loads.',
        },
        {
          question: "Why does it matter if I send counters or timestamps?",
          answer:
            "Because once context is attached to a message it is stored and re-read by the AI on every later message of that conversation, and older copies are not removed when a newer one arrives. Calling set often is free — only the value staged when the visitor presses send counts. But a value that changes between every message, such as an unread count, leaves one stale copy behind per message, which costs tokens and leaves the AI guessing which number is current.",
        },
        {
          question: "Does the visitor ever see the injected context?",
          answer:
            "No. It is hidden — it is attached to the visitor's next message behind the scenes and never appears in the chat. It is treated as background information for the AI, not as an instruction, so a page cannot use it to hijack the assistant.",
        },
        {
          question:
            "Does the AI keep remembering the page, or only for one message?",
          answer:
            "It keeps remembering. Once the context is attached to a message, it stays in the conversation and keeps informing the AI's answers on later follow-up questions too, until it naturally ages out of the recent conversation. This is the difference from the one-shot Open context on a Custom Trigger, which only affects the moment the widget opens and is not remembered afterwards.",
        },
        {
          question:
            "What is the difference between page context and the Open context on a Custom Trigger?",
          answer:
            'Page context (window.StrikeUpChat.context.set) is for anything that reflects where the visitor is now and should keep informing the whole conversation — set it on every page change. Open context (window.StrikeUpChat.open({ message, openContext })) is a one-shot hint about why the visitor pressed that particular button — for example "Visitor clicked Explain this chart on the Revenue graph" — and is forgotten after the first reply. Do not describe the page or the visitor in Open context; that belongs in page context.',
        },
        {
          question: "What should I send while the page is still loading?",
          answer:
            'Only the fact that stays true: "Opened invoice (id inv_1042)". If the visitor sends a message during that moment, that line is stored for the rest of the conversation — so a line like "details are still loading" would be stored too, and the AI may keep repeating it. Replace the context with the full description as soon as the data has loaded.',
        },
        {
          question: "Is there a size limit on the context?",
          answer:
            "Yes. Keep it under about 8,192 characters. If you go over, the context is not sent (a warning is logged in the browser console) — so keep it to a short, focused description of the situation rather than a full page dump, and cap any lists at around 50 items.",
        },
        {
          question: "Do I need to clear the context when the visitor moves on?",
          answer:
            "Usually not — calling set again on the next page replaces the old context, and it is cleared automatically once it is attached to a message. What you must avoid is leaving the previous page's lines inside the new page's context, so the AI does not see two \"the visitor is on…\" statements at once. Only call clear() if you want to stop sending page context entirely.",
        },
        {
          question:
            "What happens to the context if the visitor reloads the page before sending a message?",
          answer:
            "It is lost, by design — the staged context lives only in the browser until a message is sent. After a reload, set it again once the widget reports it is ready on the new page.",
        },
      ],
    },
    {
      id: "ai-gateway",
      title: "AI Gateway — choosing your models",
      description:
        "Decide which AI model answers each part of a conversation, and connect your own provider account so the calls are billed to you instead of running on the models included with your plan. You can keep it simple and pick one model per difficulty level, or go stage by stage. The AI Gateway is an optional extra: it appears in your menu only if it has been switched on for your workspace, and everything else keeps working exactly the same when it has not. What your AI costs, who spent it, the budgets that cap it and the turn-by-turn activity are not part of the Gateway — they live on Dashboard → AI Spend, Configure → Budgets & limits and Analytics → AI actions, for every workspace.",
      steps: [
        {
          text: "Go to Configure → AI Gateway. If you cannot see it in the menu, the AI Gateway has not been switched on for your workspace — contact StrikeUp to have it added.",
        },
        {
          text: "Stay on the Connectors tab. This lists the provider accounts you have connected. If it is empty, your agents run on the models included with your plan — that is a perfectly valid setup and you can stop here.",
        },
        {
          text: "Click Add connector to connect your own account.",
        },
        {
          text: "Give the connector a name you will recognise later, such as Our OpenAI account.",
        },
        {
          text: "Choose the Type: Single provider for a normal account such as OpenAI, Aggregator for a service that resells many providers behind one key such as OpenRouter, or Self-hosted endpoint for a model you run yourself.",
          tip: "The Provider is fixed once the connector is created — pick it carefully, as changing it later means creating a new connector.",
        },
        {
          text: "Paste your API key into the API key field. It is encrypted before it is saved and is never shown again.",
        },
        {
          text: "For a self-hosted endpoint, enter the Endpoint URL. It must start with https:// and be reachable from the public internet.",
        },
        {
          text: "Click Save. The connector appears in the list.",
        },
        {
          text: "Click Test on the new row. A passing test confirms the key, the endpoint and the model all work together, and it costs a fraction of a cent. Each test is a real call to your provider: it appears on Dashboard → AI Spend under the stage connector_test and counts towards your budgets.",
          tip: "Run Test after every key change. A wrong key is otherwise only discovered the next time a visitor asks a question. You can test one connector up to 5 times a minute, and run up to 200 tests in 24 hours across your workspace. Past that, Test says there have been too many tests in a row; wait and try again.",
        },
        {
          text: "Check the Health column on the connector list. Healthy means nothing has gone wrong recently. Anything else — Recent failures, Recovering, Temporarily skipped — means that provider has been failing, and a Temporarily skipped connector is being left out of conversations until it recovers.",
          tip: "Health is a record of what has already happened, not a live check. To test a connector right now, use Test.",
        },
        {
          text: "Open the Standard Mode tab and pick one model for each of the three levels: Basic, Medium and Difficult. Simple work such as safety checks runs on the Basic model, everyday answering on Medium, and heavy reasoning on Difficult.",
        },
        {
          text: "If you would rather have the level chosen by how hard each individual question is, turn on Per-question routing on the same tab. Read the note beside it first — it adds one extra, cheap call to every message.",
        },
        {
          text: "With Per-question routing on, optionally set the level for difficult questions and turn on Raise the level for difficult questions. This only affects the assistant that answers your visitors.",
        },
        {
          text: "Scroll to Level for each stage further down the same tab to move one individual stage to a different level — for example to run a single agent on Difficult while everything else stays on Medium.",
        },
        {
          text: "In that list, each of your agents also has a Sub-agents of … row underneath it. Those are the extra workers that run when a question is complicated enough to be split into several steps. Leave the row alone and it stays on whatever level the agent above it is on.",
          tip: "Set it yourself only if the workers should differ from the answer your visitor sees — a cheaper level for them makes complicated questions cost less, a more expensive one is worth it when only the research behind an answer needs the stronger model.",
        },
        {
          text: "If one particular stage needs a specific model, open the Advanced Mode tab and choose the model for that stage. Anything you set here overrides the level you picked in Standard Mode.",
        },
        {
          text: "The Sub-agents of … rows appear on the Advanced Mode tab too, and work the same way: leave one on Use the “Standard Mode” level and it follows its agent, or pick a model to give only the workers a different one.",
        },
        {
          text: "Open the Current setup tab to check your work. It lists every stage, the model it will actually use, and which of your settings decided it.",
        },
        {
          text: "Below that table, Configuration changes lists every change made to your connectors, stage models and levels, with who made it and when.",
        },
        {
          text: "That is all — there is nothing to publish. A change to your models reaches live conversations within about a minute.",
        },
      ],
      faqs: [
        {
          question: "I cannot see AI Gateway in my menu. Where is it?",
          answer:
            "The AI Gateway is an optional extra that has to be switched on for your workspace, and it is off unless you have asked for it. Contact StrikeUp to have it added. Nothing is wrong with your account in the meantime — your agents run on the models included with your plan.",
        },
        {
          question:
            "The AI Gateway used to be in my menu and now it is gone. What happened to my settings?",
          answer:
            "Nothing was deleted. The AI Gateway has been switched off for your workspace, which hides the page — your connectors and model choices are still stored and come back exactly as they were if it is switched on again. Spend, budgets and activity are not part of the Gateway: Dashboard → AI Spend, Configure → Budgets & limits and the Activity tab under Analytics → AI actions stay in your menu either way. Your agents carry on answering visitors either way. If the page is still on screen from before, its panels will stop loading; reload the page and it will be gone from the menu.",
        },
        {
          question: "Do I have to set any of this up?",
          answer:
            "No. Everything works out of the box on the models included with your plan. The AI Gateway is only there if you want to use your own provider account, control cost by putting cheap work on a cheap model, or run a specific model for a specific stage.",
        },
        {
          question:
            "What is the difference between Standard Mode and Advanced Mode?",
          answer:
            "Standard Mode groups every stage into three levels — Basic, Medium, Difficult — and you pick one model per level, which is the quickest way to run cheap work on a cheap model. Advanced Mode lets you pick a model for one individual stage. If a stage has a model set in Advanced Mode, that always wins over the level it belongs to.",
        },
        {
          question: "What are the Sub-agents of … rows in the stage lists?",
          answer:
            "They are the extra workers your agent uses when one question needs several steps — looking something up while also checking a policy, for example. They are listed separately so you can give that background work its own level or its own model, which is often where the cost of a complicated question actually sits. A sub-agent row follows the agent above it until you change it, so you can safely ignore it.",
        },
        {
          question:
            "I changed my agent to Difficult and my costs rose more than I expected. Why?",
          answer:
            "Because the sub-agents behind that agent moved with it. Since 27 August 2026, the extra workers a complicated question is split into follow the level of the agent they belong to, instead of always sitting on Medium — one question can therefore run several calls at the new level rather than one. If that is more than you want to spend, set the Sub-agents of … row back to Medium and only the visible answer stays on Difficult.",
        },
        {
          question:
            "I set a sub-agent row and want it to follow its agent again. How do I undo it?",
          answer:
            "Put it back on the same level as the agent above it, and it goes back to following automatically — there is no separate reset. From then on, changing the agent moves the sub-agents with it again. In Advanced Mode the equivalent is choosing Use the “Standard Mode” level, or clicking Clear on that row.",
        },
        {
          question:
            "Why do some stages say Managed by StrikeUp and offer no choice?",
          answer:
            "Those stages run on StrikeUp's own models, not yours — for example the safety checks and the tools that generate a new toolkit for you. They are not billed to you, and because they are not your calls to make, they cannot be changed. Everything that answers your visitors is yours to configure.",
        },
        {
          question: "My endpoint URL was rejected — why?",
          answer:
            "A self-hosted endpoint must be reachable over public HTTPS. Addresses on a private or internal network — anything like localhost, 10.x or 192.168.x — are refused, because accepting them would let a connector reach systems inside our infrastructure. If your model runs only inside your own network, expose it publicly over HTTPS first, then add it here.",
        },
        {
          question:
            "I edited a connector and left the API key box empty. Did I erase my key?",
          answer:
            "No. An empty key box means keep the key you already saved. Because the key is never shown again after saving, that box is always blank when you reopen the form. Only type in it when you actually want to replace the key.",
        },
        {
          question: "Can I use different providers for different stages?",
          answer:
            "Yes. Each stage is bound to a specific model on a specific connector, so you can run one stage on OpenAI, another on a model reached through an aggregator, and another on your own self-hosted endpoint, all at the same time.",
        },
        {
          question:
            "Do calls to my self-hosted endpoint count towards my budgets?",
          answer:
            "No, not by default. You run that model yourself, so its calls show on AI Spend at $0 and do not use up a budget. If StrikeUp agrees a price with you for that endpoint, calls from then on are charged at that price.",
        },
        {
          question:
            "The same model name appears twice in the dropdown — is that a bug?",
          answer:
            "No. The same model can be reachable through more than one of your connectors — directly from the provider and again through an aggregator, for example — and they can differ in price and speed. Each option is labelled with the connector it comes through so you can tell them apart.",
        },
        {
          question:
            "What happens if I delete a connector or a model that a stage was using?",
          answer:
            "Conversations keep working. That stage quietly falls back to your next applicable setting — the level model, or the model included with your plan if you have none. Nothing fails, but it is worth checking the Current setup tab afterwards so you are not surprised by which model is now answering.",
        },
        {
          question:
            "A model I picked now says “Retired model — no longer available”. What happened?",
          answer:
            "StrikeUp has withdrawn that model, usually because the provider replaced it with a newer one. Your choice was not deleted and conversations keep working — that stage falls back to your next applicable setting, the level model or the model included with your plan. The greyed-out entry is there so you can see what the stage was set to instead of an empty box; open the dropdown and pick a current model to replace it. If StrikeUp moved that setting straight onto a successor model instead, you will simply see the new model already selected and there is nothing to do.",
        },
        {
          question: "Is my API key visible to anyone?",
          answer:
            "No. It is encrypted before it is stored, it is never sent back to the browser, and it never appears in your logs. After saving, the only thing shown is whether a key is set.",
        },
        {
          question: "What does Per-question routing actually do?",
          answer:
            'It judges each visitor message before answering it, and uses the level that fits. Short acknowledgements like "thanks" are recognised without any judging call at all. Everything else is sent to a small, cheap model first, which decides whether the question is Basic, Medium or Difficult, and the answer is then produced at that level. With it off, the level comes from the stage instead — a safety check is always Basic, planning is always Difficult.',
        },
        {
          question: "Does Per-question routing cost more?",
          answer:
            "Yes, a little. Every message that is not a short acknowledgement pays for one extra call to the judging model before the answer is produced. That model is small and cheap, but it is still billed and it adds a moment of delay. It usually pays for itself if a lot of your traffic is simple, because those messages then run on a cheaper model — but it is off by default so you can decide.",
        },
        {
          question:
            "Can a visitor make my AI use an expensive model by asking a complicated question?",
          answer:
            "Only if you allow it. By default the judgement can move a question down to a cheaper level but never up. Raising the level for difficult questions is a separate setting you turn on yourself, and even then it only affects the assistant that answers your visitors — never the small background checks.",
        },
        {
          question: "What happens if my provider goes down mid-conversation?",
          answer:
            "The system retries briefly, and if a backup model is configured for that stage it switches to it and the visitor still gets an answer. If nothing works, the visitor sees a short message saying the assistant is temporarily unavailable rather than an error. A provider that keeps failing is set aside for a few minutes so it does not slow every conversation down, and the connector shows as Temporarily skipped until it recovers.",
        },
        {
          question:
            "What is the Backup models column on the Advanced Mode tab?",
          answer:
            "It shows what a stage falls back to if its main model fails. It is set up by StrikeUp on request and is shown here so you can see it — it is not something you edit yourself, because a backup can point at a model that is not on your account.",
        },
        {
          question: "Does the Configuration changes list show my API keys?",
          answer:
            "No. It records what changed and who changed it, never any credential — a connector's key is left out of both the before and the after picture. It also shows only changes made in your workspace; anything StrikeUp changes on the platform side never appears there.",
        },
        {
          question:
            'A visitor replied "yes" to a complex proposal. Does Per-question routing treat that as a simple message?',
          answer:
            'No. A short confirmation — "yes", "go ahead", "do it" — inherits the level of the question it answers, so the task the assistant proposed runs on the model it needs. Closing remarks such as "thanks" stay on the cheapest level.',
        },
        {
          question:
            "What happens when a conversation becomes too long for the model?",
          answer:
            "The call moves to the next model in the stage's backup chain, in the order you configured, instead of failing. Whether that model has a larger context is up to your chain — put your largest-context model last if you want that safety net.",
        },
        {
          question:
            "If a backup model takes over, are charts and tables still produced?",
          answer:
            "Backup models that can return structured answers are tried first, so charts and tables usually survive a fallback. Only when none of them is available does the answer fall back to plain prose.",
        },
        {
          question:
            "The Spend, Usage, Budgets & limits and Activity tabs have disappeared from the AI Gateway. Where are they?",
          answer:
            "They moved out on 16 September 2026, because they are about every workspace's AI use, not about the Gateway. Spend and Usage are one page at Dashboard → AI Spend; Budgets & limits is under Configure, just above Embed; Activity is a tab on Analytics → AI actions. The AI Gateway keeps Connectors, Standard Mode, Advanced Mode and Current setup, which now also holds the Configuration changes list.",
        },
        {
          question:
            "Do I need the AI Gateway for any of the cost, budget or activity pages?",
          answer:
            "No. Those pages are shown to every workspace and record every workspace's calls. The AI Gateway is only needed when you want to choose the models yourself or bring your own provider account.",
        },
      ],
    },
    {
      id: "customer-companies",
      title: "Managing a Customer Company",
      description:
        "Set up and look after a customer company yourself from CRM → Companies — its knowledge base, skills, workflows, prompt shortcuts, email channel and member roles. These are the same things that company's own admins see in the Customer Portal.",
      steps: [
        {
          text: "Go to CRM → Companies.",
        },
        {
          text: "Click the company you want to manage. The company page opens on the Overview tab.",
        },
        {
          text: "Use the tabs across the top to move between areas — Overview, Members, Knowledge base, Skills, Workflows, Prompt shortcuts and Integrations.",
          tip: "The tab you are on is part of the page address, so you can bookmark a tab or send a colleague a link that opens straight to it.",
        },
        {
          text: "On Overview, edit the company name, company ID, self-registration, credential hint and agent instruction, then click Save.",
        },
        {
          text: "Still on Overview, use Account Memory to add facts the assistant should remember for everyone in this company, such as which plan they are on.",
        },
        {
          text: "On Members, use the Role dropdown to switch someone between Admin and Member.",
          tip: "You cannot remove the last active Admin. Demoting or disabling them is blocked so a company is never left with nobody who can manage it.",
        },
        {
          text: "Still on Members, click Disable to stop someone signing in, or Enable to let them back in. Their chat history is kept either way.",
        },
        {
          text: "On Knowledge base, click Upload document and pick a file. It becomes available to this company only.",
          tip: "Indexing takes a moment. The Status column shows Processing and then Indexed — click Refresh to see the latest state.",
        },
        {
          text: "Still on Knowledge base, type a question into Test retrieval and click Search to see exactly what the assistant would find for this company.",
        },
        {
          text: "On Skills, click Add skill, then fill in the name, what the skill does, and when it should trigger.",
        },
        {
          text: "On Workflows, use the switch to pause or resume an automation, or click Edit, Delete, or Run history.",
          tip: "New workflows and new schedules can only be created by the customer inside the Portal, because a schedule has to be tied to the widget it runs through. You can still pause, rename, delete and inspect them here.",
        },
        {
          text: "On Prompt shortcuts, click Add shortcut to create a one-click prompt, and use the arrows to change the order your customers see them in.",
        },
        {
          text: "On Integrations, click Add Email (SMTP), Add Telegram, Add Slack or Add Microsoft Teams to give this company its own delivery channels, then use Send test on each to confirm it works.",
          tip: "One channel per type. Email carries the AI's own emails and the approval notifications; Telegram, Slack and Microsoft Teams carry approval notifications only.",
        },
      ],
      faqs: [
        {
          question: "What can I do here that my customer can also do?",
          answer:
            "Almost everything. The tabs mirror the Customer Portal: knowledge base, skills, workflows, prompt shortcuts, integrations, members and company settings. Two things stay with the customer — creating a new workflow and creating or editing a schedule — because a schedule has to be tied to the widget it runs through.",
        },
        {
          question:
            "Why would I manage a company instead of letting them do it?",
          answer:
            "Two common reasons: setting an account up before anyone from that company has signed in, so their assistant is useful on day one; and helping a customer who is stuck, without asking them to click through the Portal while you talk them through it.",
        },
        {
          question: "Does changing a customer's role or company sign them out?",
          answer:
            "Yes. When a customer's role changes or they move to another company, whether you do it or a customer admin does it in the Portal, they are signed out and sign in again with their usual PIN or password. Disabling a customer, deactivating or deleting their company, and a customer import with Update existing customers ticked that changes their role or company or sets their PIN or password sign them out too. Editing only their name or email does not.",
        },
        {
          question:
            "Can I still change things for a company I have deactivated?",
          answer:
            "Yes. Deactivating stops that company's own users signing in, signs out anyone who is signed in and skips their scheduled workflows; it does not lock you out. The page shows a banner reminding you the company is deactivated, but every tab stays editable so you can finish tidying up or offboarding the account.",
        },
        {
          question:
            "What happens to the scheduled workflows of a deactivated company?",
          answer:
            "They are skipped, not deleted. Each time a schedule comes due, the run appears in the workflow's history as Cancelled with the reason, and the workflow and its schedule stay as they are. Reactivate the company and the next run goes ahead. The same happens to the workflows of a disabled customer, and agent runs for those customers are refused.",
        },
        {
          question:
            "If I pause a customer's workflow, does it really stop running?",
          answer:
            "Yes. A paused workflow no longer runs on its schedule, and each skipped run is recorded in its Run history as Cancelled — so you can see the schedule fired and was deliberately skipped, rather than wondering whether anything happened.",
        },
        {
          question: "Whose workflows appear on the Workflows tab?",
          answer:
            'All of them for that company, company and personal alike. A Company badge marks a workflow shared with everyone in the company; a Personal badge marks one only its creator sees in the Portal. Every workflow was created by one person, and the Owner shown under each one tells you who that is. If someone has since left the company, their workflow still appears marked "Owner not in this company" so you can find and remove it.',
        },
        {
          question: "Is the knowledge base here the same as the main one?",
          answer:
            "No, it is separate. Documents you upload on a company page can only be retrieved for that company. Your main knowledge base under Configure is available to everyone. The company view is deliberately simpler — no folders, no web sources, just a list of files.",
        },
        {
          question: "Why can I not translate a company's prompt shortcuts?",
          answer:
            "Company shortcuts are shown as written and are not translated. If you need a shortcut in several languages, create it as a tenant-wide shortcut under Configure → Prompt Shortcuts instead, where translations are supported.",
        },
        {
          question:
            "What happens to notifications if I do not set up SMTP for a company?",
          answer:
            "They go out through your own tenant-wide email channel. Giving a company its own SMTP server is optional — do it when their notifications should come from their domain rather than yours. Telegram and Slack work the same way: without a channel here, they go out through your tenant-wide Telegram or Slack channel, if you have one.",
        },
        {
          question:
            "How do I see how much AI a company has used, and can I cap it?",
          answer:
            "Open the company's Usage tab: it shows tokens and cost for the period by customer, source (chat, workflow, autonomous agent, Portal), workflow, autonomous agent and the costliest runs. To cap it, create a budget for that company in AI Gateway → Budgets and turn on Enforce as a hard limit; until you do, a budget only alerts. When an enforced budget is used up, new conversations in that company get a short notice and its running agent jobs pause until the period resets or you raise the amount.",
        },
        {
          question:
            "I saved a company's AI instruction and it was refused — why?",
          answer:
            "It failed the content check every instruction passes before it is stored — an attempt to change the assistant's identity, relax approvals or guardrails, deceive users, or a credential, link or script in the text. The message names the reason; reword it as tone-and-workflow guidance and save again.",
        },
      ],
    },
    {
      id: "ai-spend",
      title: "AI Spend — what AI costs and who spent it",
      description:
        "An estimate of what your AI use has cost, by model, stage, widget and day, and who spent it — by customer company, customer, source, workflow, autonomous agent and run. Shown for every workspace, whether or not the AI Gateway is switched on.",
      steps: [
        {
          text: "Go to Dashboard → AI Spend. The page is there for every workspace — spend is metered whether or not you use the AI Gateway.",
        },
        {
          text: "Read the Spend section first: every figure is an estimate worked out from the prices each provider publishes — StrikeUp adds no markup and this is not an invoice.",
        },
        {
          text: "Set the date range at the top right — the same control as on the other Dashboard pages, and the range you pick there is remembered here too. Pick Last 7, 14 or 30 days, or Custom for your own dates; a range longer than 90 days is cut to the 90 days before its end.",
        },
        {
          text: "Use the Usage dropdown to switch between Your agents, StrikeUp services and Everything. Your agents is what your own configuration ran; StrikeUp services is background work on our models that is never charged to you.",
        },
        {
          text: "Read the four tiles at the top for the range you picked: estimated cost, number of AI calls, tokens in and tokens out. Each tile names the exact dates it covers.",
        },
        {
          text: "Look at the chart below them for the estimated cost day by day, and at the three tables under it for the same total split by model, by stage and by widget.",
          tip: "A row labelled Not attributed is real spend that could not be tied to a model, stage or widget — usually calls made before this breakdown existed. It is included in the total.",
        },
        {
          text: "If a notice says some calls are not priced, those calls ran on a model we hold no published price for. They still count towards the calls and tokens tiles, but they add nothing to the cost figure, so the real total is a little higher than shown. Budgets do count these calls, at a typical rate, so a budget can show more spend than this page.",
        },
        {
          text: "Scroll to the Usage by customer and source section to see who spent the tokens and how.",
        },
        {
          text: "Read the Allowance card at the top. It says no hard limit applies — usage is unlimited — until you enforce a budget; once you do, it shows the budget's name, how much of it is used and when it resets.",
        },
        {
          text: "Read the tables under it: by customer company, by customer, by source (chat, workflow, autonomous agent, Portal run now), by workflow, by autonomous agent, and the costliest runs. They cover the same date range and the same Usage choice as the Spend section above.",
          tip: "The same view sits on each customer company's page (its Usage tab) and inside a customer's details (the AI usage section).",
        },
      ],
      faqs: [
        {
          question: "How is the cost on the AI Spend page worked out?",
          answer:
            "It is an estimate, not a bill. Each call records how many tokens went in and came out, and those counts are multiplied by the price the provider published for that model on the day the call ran. Storing the price at the moment of the call is why an old figure does not change when a provider changes its prices. StrikeUp adds no markup, and if you use your own provider account the real charge comes from that provider, not from us.",
        },
        {
          question: "Why do some calls say they are not priced?",
          answer:
            "Because we hold no published price for the model that answered them. Those calls are still counted in the number of calls and in the token totals, but they add nothing to the estimated cost, so the true total is slightly higher than the figure shown. Calls made before spend tracking existed are the most common reason. Budgets are the exception: they charge these calls at a typical rate (the middle of the published prices for that provider's models), so a model with no price still uses up a budget.",
        },
        {
          question:
            "Why does Everything not equal Your agents plus StrikeUp services?",
          answer:
            "Because some older calls were recorded before the two were told apart. Everything counts every call, including those, while Your agents and StrikeUp services each count only the calls that were labelled. The gap shrinks as older activity falls outside the period you are looking at.",
        },
        {
          question:
            "I used to look at Spend and Usage inside the AI Gateway. Where are they now?",
          answer:
            "On Dashboard → AI Spend, as one page: the Spend section (cost by model, stage, widget and day) on top and Usage by customer and source below it. Nothing was lost in the move — the same figures, the same breakdowns, the same allowance card.",
        },
        {
          question: "Do I need the AI Gateway switched on to see AI Spend?",
          answer:
            "No. Every workspace's AI calls are metered and priced, so the page is there for everyone. The AI Gateway only controls which models answer; it has no bearing on whether spend is tracked.",
        },
        {
          question:
            "Why does AI Spend cover fewer days than the range I picked?",
          answer:
            "Because it looks back at most 90 days from the end of your range. Pick a longer custom range and the tiles show the 90 days before the end date you chose — the exact dates are printed under each tile, so what is counted is never in doubt. Overview, Usage and Performance are not limited this way.",
        },
        {
          question:
            "Why is there one Usage dropdown at the top instead of one per section?",
          answer:
            "So both sections always describe the same calls. Your agents, StrikeUp services and Everything apply to the Spend section and to Usage by customer and source together, and so does the date range.",
        },
        {
          question:
            "Can I see the spend of one customer company or one customer on its own?",
          answer:
            "Yes. Open the company under CRM → Companies and use its Usage tab, or open a customer's details and scroll to AI usage. Both are the same tables, narrowed to that company or person, with the allowance their company draws on.",
        },
      ],
    },
    {
      id: "budgets",
      title: "Budgets & limits",
      description:
        "Be warned when your AI spend passes a level you set — or turn a budget into a hard limit that pauses work once it is used up. Available to every workspace; nothing is capped unless you say so.",
      steps: [
        {
          text: "Go to Configure → Budgets & limits to be warned when your AI spend passes a level you choose — or to cap it. Budgets are available to every workspace, with or without the AI Gateway.",
        },
        { text: "Click Add budget." },
        {
          text: "Give the budget a name you will recognise in an alert, such as Monthly AI spend.",
        },
        { text: "Enter the amount in US dollars." },
        {
          text: "Choose the period: every month, every week or every day. Periods follow the calendar, so a monthly budget resets on the 1st and a weekly one on Monday.",
        },
        {
          text: "Enter the points you want to be warned at, as percentages separated by commas. The default is 50, 80, 100.",
        },
        {
          text: "Choose what the budget applies to: All AI usage, one widget, or one customer company. A customer-company budget counts only that company's conversations, workflows and autonomous agents.",
        },
        {
          text: "Leave Enforce as a hard limit off to be alerted only. Turn it on to make the budget an allowance: when the amount is used up, new conversations in its scope get a short notice instead of an answer and running agent jobs pause until the period resets or you raise the amount.",
          tip: "Nothing is capped unless you say so. Every budget starts as an alert; raising an enforced budget's amount or switching the limit off lets paused runs continue within a minute.",
        },
        {
          text: "Click Save. The budget appears in the list with a bar showing how much of it the current period has used, a Hard limit badge if it is enforced, and any alerts it has already raised appear in Recent alerts below.",
          tip: "A budget only measures spend you are charged for. Background work on StrikeUp's own models and demo conversations are left out of the total.",
        },
        {
          text: "To see the spend a budget is measured against, go to Dashboard → AI Spend; the Allowance card there shows how much of an enforced budget is left.",
        },
      ],
      faqs: [
        {
          question:
            'A customer says the assistant answered "This workspace\'s AI allowance for the current period is used up". What happened?',
          answer:
            "A budget with Enforce as a hard limit on has reached its amount for the period, in a scope that covers that customer (the whole workspace, their widget, or their customer company). New conversations in that scope get that notice instead of an answer, and agent runs in progress pause between steps, until the period resets or you raise the amount or switch the limit off on Configure → Budgets & limits — paused runs continue within a minute.",
        },
        {
          question: "Does a budget stop conversations when it is exceeded?",
          answer:
            'Not unless you ask it to. A budget starts as an alert — it never blocks, slows or downgrades a conversation, even at 200% of the amount you set. Turn on Enforce as a hard limit to change that: when an enforced budget is used up, new conversations in its scope get a short notice ("This workspace\'s AI allowance for the current period is used up…") instead of an answer, and agent runs in progress pause between steps. They continue on their own when the period resets, or within a minute of you raising the amount or switching the limit off.',
        },
        {
          question: "How soon after I pass a threshold does the alert appear?",
          answer:
            "Within a few minutes, usually less than five. Spend is added up shortly after a conversation rather than during it, so that checking a budget never slows an answer down. Each threshold raises exactly one alert per period no matter how many messages cross it.",
        },
        {
          question: "Where do budget alerts get sent?",
          answer:
            "They appear in Recent alerts on the Budgets & limits page, and are emailed to the addresses on the budget — or, with none set, to every workspace administrator. There is no webhook for them.",
        },
        {
          question: "One of my budgets says Deleted widget — what happened?",
          answer:
            "The widget that budget was watching has been deleted. The budget is now measuring nothing and will never raise another alert. Either edit it to watch a different widget or all AI usage, or delete it.",
        },
        {
          question:
            "Budgets used to be a tab in the AI Gateway. Where did they go?",
          answer:
            "To Configure → Budgets & limits, just above Embed. A budget is a setting, so it sits with the other settings; the spend it watches is on Dashboard → AI Spend. Your existing budgets, their thresholds, hard limits and alert history moved with them unchanged.",
        },
        {
          question: "Do I need the AI Gateway to set a budget or a hard limit?",
          answer:
            "No. Budgets and hard limits work for every workspace, because spend is metered whether or not you use the AI Gateway. The AI Gateway only decides which models answer.",
        },
        {
          question: "The budget alert e-mail links to a page — which one?",
          answer:
            "To Configure → Budgets & limits, where the budget and its Recent alerts are. E-mails sent before the move still open the AI Gateway page; the budget itself is no longer there, so use the menu.",
        },
        {
          question: "Where do I see how much of a budget is used up?",
          answer:
            "In two places. The list on Budgets & limits shows a bar per budget for the current period. Dashboard → AI Spend shows an Allowance card at the top of Usage by customer and source — but only when a budget in scope has Enforce as a hard limit on; an alert-only budget shows nothing there.",
        },
        {
          question: "Why does a budget show more spend than AI Spend?",
          answer:
            "Because a budget also charges calls that have no published price. AI Spend adds nothing to its cost figure for those calls and marks them as not priced. A budget charges them at a typical rate instead: the middle of the published prices for that provider's models, or of all models when we hold no price for that provider. Without this, a budget on a model with no price would never move. The two figures match when every call in the period is priced. Calls to a self-hosted endpoint you run yourself are never charged this typical rate.",
        },
      ],
    },
    {
      id: "notification-preferences",
      title: "Job Notifications (Approvals and Questions)",
      description:
        "When a background run stops for an approval or to ask a question, it can reach the person who owns it (for a company workflow, everyone in that company) by email, Telegram, Slack or Microsoft Teams — and, if they choose, so can a run that starts, fails, pauses or finishes, including a chat they closed before its reply arrived. In Telegram and Slack, everything about one run or chat arrives as one thread, and each message can carry an Open button that takes them straight to it. A run or chat that finishes sends its whole answer there, charts and tables included. Where buttons and replies are on, they can also approve or deny with a button, answer a question by replying, and chat with the assistant without leaving Slack, Telegram or Microsoft Teams, charts and tables included. Nothing is sent to anyone who is watching it.",
      steps: [
        {
          text: "Go to CRM → Companies and open the company whose people should be notified.",
        },
        { text: "Open the Integrations tab." },
        {
          text: "Click Add Email (SMTP), Add Telegram, Add Slack or Add Microsoft Teams.",
          tip: "One channel per type per company. A type you leave out is simply not offered to that company's people. Each form has a Where do I find these? button that opens the setup steps for that provider only.",
        },
        {
          text: "For Email (SMTP), fill in host, port, SSL, username, password, From address and From display name.",
        },
        {
          text: "For Telegram, create a bot with @BotFather in Telegram and paste the token it returns. Leave Bot username blank — it is read from the bot itself.",
        },
        {
          text: "For Slack without a Slack app yet, leave the token blank, click Save, then click Edit on the new channel.",
          tip: "Each company uses its own Slack app, installed in its own workspace.",
        },
        {
          text: "Under Create the Slack app from this manifest, click Copy.",
          tip: "The manifest gives the app every permission, event and command it needs. If someone else manages the company's Slack workspace, send them the manifest for this step and the next two.",
        },
        {
          text: "Go to api.slack.com/apps, choose Create New App → From a manifest, pick the company's workspace and paste the manifest.",
        },
        {
          text: "Install the app to that workspace.",
        },
        {
          text: "Paste the Bot User OAuth Token (it starts with xoxb-) and the Signing secret from the app's Basic Information page into the channel.",
          tip: "Without the signing secret, Slack messages carry only an Open button — Approve and Deny, replies and chat need it.",
        },
        {
          text: "For Microsoft Teams, create an Azure Bot of type Single Tenant in the company's own Microsoft 365 organisation, add a client secret, and paste the Microsoft App ID, the client secret and the Directory (tenant) ID into the channel.",
          tip: "The bot answers only people in that Directory (tenant) ID. If someone else manages the company's Microsoft 365, ask them for these three values.",
        },
        {
          text: "Click Save, then click Send test on the channel to confirm it works. For Telegram and Slack, the result also says whether buttons and replies are on; for Microsoft Teams, it says whether the bot signed in to Microsoft.",
          tip: "A saved password or bot token is never shown again — the field reads Token stored. Leave it blank whenever you edit something else and the stored value is kept.",
        },
        {
          text: "For Microsoft Teams, click Edit on the channel, copy the Messaging endpoint under Connect the Teams bot into the Azure Bot's Configuration page, and add the Microsoft Teams channel to the Azure Bot.",
        },
        {
          text: "Click Download app package and have the company's Teams admin upload it in the Teams admin center under Teams apps → Manage apps.",
          tip: "Until the app is uploaded, the company's people cannot find the bot in Teams.",
        },
        {
          text: "Under Approvals from chat apps on the same tab, choose the highest risk this company's people may approve with a button: Everything (default), Up to medium risk, Low risk only or Deny only.",
          tip: "Above the limit, the message shows only Deny and Open, and the approval is made in your app. Deny always works from the message. The company's Customer Admin can change the same setting in the Portal.",
        },
        {
          text: "Tell the company's people to open the Portal and go to Notifications → My notifications, where they switch on each channel they want to be reached on.",
        },
        {
          text: "For Telegram, your customer clicks Connect, presses Start in the chat that opens with the bot, then clicks I've pressed Start.",
          tip: "The link is good for 10 minutes — if it expires, click Connect again. A customer who would rather not use the link can paste their Telegram chat ID instead, but a pasted ID only receives messages: buttons, replies and chat work only for a chat connected through the link.",
        },
        {
          text: "For Slack, the customer is matched automatically by the email on their profile. If no match is found, they paste their Slack member ID. How do I find my member ID? under the field shows them where it is: in Slack, profile picture → Profile → ⋮ → Copy member ID.",
          tip: "The member ID must come from the Slack workspace your Slack app is installed in. An ID from another workspace reaches no one.",
        },
        {
          text: "To use buttons and replies in Slack, the customer clicks Send confirmation to Slack, presses Confirm in the direct message the bot sends, then clicks I've confirmed.",
          tip: "This proves the Slack account is really theirs — a matching email alone does not. Once it is done, and in Telegram once they connected through the link, My notifications shows ✓ Buttons and replies work here. The confirmation appears only when buttons and replies are on for your server.",
        },
        {
          text: "For Microsoft Teams, your customer clicks Connect Microsoft Teams and sends the link message it shows to the bot in Teams. The page updates by itself once the message arrives.",
          tip: "The code is good for 10 minutes — if it expires, click Connect Microsoft Teams again. A customer who cannot find the bot needs the company's Teams admin to upload the app first. Linking in Teams also turns on buttons and replies there.",
        },
        {
          text: "Ask each person to click Send test once — the message arrives in that channel straight away.",
        },
        {
          text: "Each person also chooses, under Also tell me when on the same page, whether a background job that starts, a run that fails or pauses, or a run that finishes reaches them too.",
          tip: "Fails or pauses is on by default; starts and finishes are off — a workflow that runs every hour would otherwise message them every hour. Approvals and questions always reach them and have no switch.",
        },
        {
          text: "Nothing else is needed. From then on, a run of theirs that waits for approvals or answers sends to every channel they switched on: one message for each approval or question in Telegram, Slack and Microsoft Teams, one message listing them by email. A run that starts, fails, pauses or finishes sends one if they asked for it. That includes a chat they close before the reply arrives: it keeps working, and its outcome reaches them the same way.",
          tip: "Approvals and questions wait about a minute and a half before the message goes out, and a customer who decides inside that pause is never messaged. One who keeps the chat open without deciding is checked again a minute and a half later, so closing the tab afterwards still reaches them. Started, failed, paused and finished runs are sent within seconds.",
        },
        {
          text: "To give each message an Open button, set App page URL for the widget under Configure → User Interface → Customer Portal.",
          tip: "Open lands on your page, and the widget opens the chat or run the message is about. Without it, messages about background runs link to the Portal when your workspace has one configured, and chat messages have no link. In Telegram, Slack and Microsoft Teams, when your platform administrator has set up a hosted widget page, Open instead signs a person who connected their account straight into the assistant on that chat or run, so your page's own sign-in and redirects are not in the way, and it works without App page URL. That link works for 7 days and stops once they disconnect or reconnect the account, so treat a forwarded message like a sign-in link.",
        },
      ],
      faqs: [
        {
          question: "What exactly sets one of these notifications off?",
          answer:
            "Six things, four of which each person controls. A run that stops for an approval and a run that stops to ask a question always notify. A background job that starts notifies only if the person switched that on. A run that fails, and a run that pauses because the AI allowance is used up, notify unless the person switched that off. A run that finishes notifies only if the person switched that on. A chat counts as a run too, but only when the person left before its reply arrived, and it never sends a start message. A run someone cancels sends nothing.",
        },
        {
          question:
            "Will a customer get a message every time the assistant answers them in the chat?",
          answer:
            "No — only when they left before the answer arrived. A reply they watch arrive in the chat is never sent again. If they close the chat or the browser tab while the assistant is still working, it keeps working, and when it is done they get the first lines of the reply (if they switched on finished runs) or a note that it could not be answered (if failures and pauses are on).",
        },
        {
          question: "Does closing the chat stop the assistant?",
          answer:
            "No. The assistant finishes the answer even if the customer closes the chat, closes the browser tab or loses their connection — only the Stop button ends it early. The reply is saved to the conversation, so it is there when they open the chat again.",
        },
        {
          question:
            "What does a customer see if they reopen the chat before the reply is ready?",
          answer:
            "The chat shows that it is still working, and the reply appears there as soon as it is ready. If they try to send another message in the meantime, they are asked to wait for the reply first. Nothing is sent to their channels, because they are back in the chat.",
        },
        {
          question:
            "A customer was told their run is paused — do they need to do anything?",
          answer:
            "No. A run pauses when the AI allowance for the period is used up, and it carries on by itself when the period resets or the allowance is raised. The message tells them so.",
        },
        {
          question:
            "Will a customer be messaged while they already have the chat open?",
          answer:
            "No. The check happens at the moment the message would go out, and both having that chat open and having the run's own page open in the Portal count as being there. If they close the tab while a run still waits for their approval or answer, they are messaged about a minute and a half later; a chat reply that arrives after they closed the tab is sent within seconds.",
        },
        {
          question:
            "A company has no channel of its own — does anything still go out?",
          answer:
            "Yes: it falls back to your tenant-wide channels under Configure → Notifications, for email, Telegram, Slack and Microsoft Teams alike. A type that has neither a company channel nor a tenant-wide one is not offered to that company's people.",
        },
        {
          question: "Can one person be reached on more than one channel?",
          answer:
            "Yes. Every channel they switch on under Notifications → My notifications gets the message, so someone can have both email and Telegram on.",
        },
        {
          question: "Who is told about a company workflow's run?",
          answer:
            "Everyone in that company who switched on a channel. When the run waits for an approval, each of them hears about it and the first to decide settles it for everyone; a run that fails, pauses or finishes reaches each person according to their own Also tell me when switches. Someone watching the run is skipped, and people who share one Telegram chat, Slack channel or mailbox get a single message there. A personal workflow's run reaches only the person who created it.",
        },
        {
          question: "Does the message contain what the run was doing?",
          answer:
            "In Telegram, Slack and Microsoft Teams, a run or chat that finishes sends its whole answer under a heading such as Finished: and the run's name, with its choices, chart, table and CSV file; by email, only a short preview of a chat reply. For an approval or a question it names the step, the tools involved and the deadline; for a failed, paused or finished run it names the run and, for a failure, the reason it stopped. By email, for a chat that got its reply after the customer left, it names the chat and quotes the first 200 or so characters of the reply as plain text; for a chat that could not be answered, it says so without quoting the technical error. Each message has an Open button (a link, in email) when you set App page URL for the widget: it opens your page, and the widget opens that chat or run. Without it, messages about background runs link to the Portal when your workspace has one configured, and chat messages have no link — the customer reopens the chat on your site. If your chats handle sensitive data, keep in mind that the reply leaves the chat: a preview goes to the customer's own email, and the whole answer to their own Telegram, Slack or Microsoft Teams.",
        },
        {
          question: "Why do messages about one run arrive as a thread?",
          answer:
            "So that one run or chat stays in one place. In Slack, later messages are replies under the first one, and approvals and questions also show in the main conversation so they are not missed. In Telegram, later messages reply to the first one. If the person switched on starts, the start message opens the thread; otherwise the first message does. Email has no threads — each message arrives on its own; Microsoft Teams has none either, so messages arrive one after another in the chat with the bot.",
        },
        {
          question:
            "A run waits on several approvals at once — is that several messages?",
          answer:
            "Yes, in Telegram, Slack and Microsoft Teams: one message per approval, up to five, then one message saying how many more are waiting. Each is sent only once, even after a restart. Email sends a single message listing them all.",
        },
        {
          question: "Why is there no Open button on a message?",
          answer:
            "Most often because the widget has no App page URL set under Configure → User Interface → Customer Portal — a chat message then has no link. A message about a background run has none either when App page URL is set but Enable Customer Portal is off, because a run opens in the Portal. Telegram also refuses button links to some addresses, such as a local test address; the link then appears in the message text instead. A run or chat that finished in Telegram, Slack or Microsoft Teams has Open only when its answer has a chart or table, because everything else is already in the message. People who connected their Telegram, Slack or Microsoft Teams account get Open even without App page URL when your platform administrator has set up a hosted widget page.",
        },
        {
          question:
            "The API restarted while a run was waiting — does the customer still get told?",
          answer:
            "Yes, for a run that is waiting. On start-up, every run still waiting for a person is checked again, and anyone who was not already messaged about that wait is messaged then. A run that was already notified is not messaged twice. A failed, paused or finished run is announced within seconds, so only one that ended in the last few seconds before the restart can be missed.",
        },
        {
          question:
            "Can customers approve or answer from Slack, Telegram or Microsoft Teams?",
          answer:
            "Yes, once buttons and replies are on and they connected through the bot in Telegram, confirmed in Slack or linked their account in Microsoft Teams. Each approval message then has Approve, Deny and Open buttons, and a question is answered by replying to its message (in Slack, in its thread; in Microsoft Teams, by typing in the box on the question and pressing Send answer). The first decision settles it: every copy of the message, for every person and channel, changes to say who decided and where — for example ✅ Approved by Ada via Slack — and the run carries on, with its next step or its outcome arriving in the same thread. Without buttons and replies, the Open button takes them to your app, where they approve or answer.",
        },
        {
          question:
            "Can customers chat with the assistant from Slack, Telegram or Microsoft Teams?",
          answer:
            "Yes, on the same terms. In Slack, they send the bot a direct message or type /start-chat followed by their message, and reply in that message's thread to continue. In Telegram, /new or /start_chat starts a new chat, a plain message continues the chat they used in the last 24 hours, and replying to one of the bot's messages continues that chat. Microsoft Teams works like Telegram: new starts a new chat, a plain message continues the chat used in the last 24 hours, and quoting one of the bot's messages in a reply continues that chat. /help (help in Teams) lists what the bot can do. The chat also appears in their chat history in your app. Tools use the sign-in already stored for the customer, and they need to have opened the assistant on your site at least once so the bot knows which one to use.",
        },
        {
          question:
            "What does an answer look like in Slack, Telegram or Microsoft Teams?",
          answer:
            "Much like in the widget. While the assistant works, a status message shows what it is doing — ⏳ Working on it…, then steps such as 🔍 Searching the knowledge base… or 🛠 Calling getOrders… — and disappears when the answer arrives, so the answer comes with its own notification. Choices the assistant offers appear as buttons, and pressing one sends that answer as if the customer had typed it; a line under them says the customer can also type their own answer (in Slack, as a reply in the thread); more than eight are listed instead. Charts and tables appear in the chat — as a chart and a table in Slack (a stacked chart, or one with many points or series, as a picture), as a picture and a fixed-width table in Telegram, as a chart and a table card in Microsoft Teams (a chart Teams cannot draw, as a picture) — a table also arrives as a CSV file with every row (in Microsoft Teams, as a Download link that works for 24 hours), and an Open button leads to the full view in your app. A table the assistant writes into the text of its answer comes as fixed-width columns in all three. In Microsoft Teams, the bot also shows as typing while it works. After the first answer, the chat gets a short name, the same one it has in the chat history in your app.",
        },
        {
          question: "Why does a table arrive in Slack without its CSV file?",
          answer:
            "Because the company's Slack app was created before it could send files. Open the Slack channel, click Copy under Create the Slack app from this manifest, paste it under App Manifest at api.slack.com/apps, save, and reinstall the app. Until then, Test on the channel says files need one more permission; everything else keeps working. The same permission sends a chart Slack cannot draw itself as a picture; until then, such a chart arrives as text.",
        },
        {
          question:
            "What else can customers do with commands in Slack, Telegram and Microsoft Teams?",
          answer:
            "They can check on their work and start workflows without opening your app, and the bot answers in its direct message. /chats lists their recent chats with a link to each and a button to pick one up again, /pending lists the approvals and questions waiting for them, /runs lists their recent workflow runs and how each went, /run-workflow followed by a workflow's name (in Telegram and Microsoft Teams, /run and the name) starts that workflow, and /stop stops the answer the assistant is writing. They can start only the workflows they could start in the Portal, so company workflows are for their company's Admins. Without a name, or with a name that matches more than one workflow, the bot lists the ones they can run. In Slack, a command typed in a channel is still answered in the bot's direct message, and only the person who typed it sees a short note saying so. In Microsoft Teams, the slash is optional — chats, pending, runs, run and the name, and stop work as plain words, and the bot's command menu offers them too. Each chat in the list from /chats also has a 💬 button. Pressing it brings that chat, even one started in your app, into Slack, Telegram or Microsoft Teams with its name and the start of its last answer. The customer then carries on by replying in that message's thread in Slack, or by sending their next message in Telegram or Microsoft Teams. Tapping the chat's name opens it in your app instead. A Telegram bot set up before these commands existed shows the old, shorter menu until you open its Telegram channel and click Test once.",
        },
        {
          question: "Why does an approval message show only Deny and Open?",
          answer:
            "Because the approval is riskier than the company's limit under Approvals from chat apps. The customer can still deny it from the message; to approve it, they click Open and approve it in your app. You change the limit on the company's Integrations tab, and their Customer Admin can change it in the Portal.",
        },
        {
          question:
            "Why does a message have no Approve and Deny buttons at all?",
          answer:
            "Usually because that person has not connected through the bot in Telegram, confirmed in Slack or linked their account in Microsoft Teams — a pasted ID only receives messages. The other reasons are that buttons and replies are off for your server, that the company's Slack channel has no signing secret, or that the message is the one saying how many more approvals are waiting, which has only Open.",
        },
        {
          question:
            "What happens to the buttons when someone decides in the app instead?",
          answer:
            "The message is updated within seconds and its buttons go away: it reads ✅ Approved in the app or ❌ Denied in the app, or ⌛ Expired — nobody decided in time when the approval ran out. If the API restarted in between, the buttons stay until someone presses one, and that message then shows it was already decided.",
        },
        {
          question:
            'The bot answers "This account isn\'t linked" — what should the customer do?',
          answer:
            "Connect again under Notifications → My notifications in the Portal: in Telegram with Connect, in Slack with Send confirmation to Slack, in Microsoft Teams with Connect Microsoft Teams. The bot says this when it cannot match the Slack, Telegram or Microsoft Teams account to exactly one active person who has that channel switched on — for example after they switched the channel off, or their account or company was disabled.",
        },
        {
          question:
            "Can I choose the channels for a customer, the way I can with their other Portal settings?",
          answer:
            "No — and this is the one exception. You control which channels a company has (CRM → Companies → Integrations); each person controls where they are reached (Portal → Notifications → My notifications). Their Telegram chat, Slack ID and Microsoft Teams account are personal, so no staff screen sets them.",
        },
      ],
    },
  ],

  troubleshooting: [
    {
      symptom: "Widget not showing on my website",
      cause: "Embed snippet missing, in the wrong place, or blocked by CSP",
      solution:
        "Place the <script> tag before </body>. Check the browser console for CSP errors and add the widget domain to your CSP allowlist if needed.",
    },
    {
      symptom: "Tools are not executing",
      cause: "Server authentication misconfigured",
      solution:
        "Open Configure → Toolkits, click Check Connection on the server, and review the error shown. Reconfigure auth settings accordingly.",
    },
    {
      symptom: "Knowledge base retrieval returns nothing",
      cause:
        "Document still indexing, failed, or content doesn't match the query phrasing",
      solution:
        "Check document status in Configure → Knowledge Base. If Failed, re-upload. If Ready, use the Retrieval Test tool with the exact phrasing your users would use.",
    },
    {
      symptom: "Users prompted for credentials unexpectedly",
      cause: "Server configured with dynamic auth instead of a static key",
      solution:
        "In Configure → Toolkits → [server] → Auth Settings, switch to Custom Static Token and enter the fixed API key.",
    },
    {
      symptom: "Guardrail blocking too many normal messages",
      cause: "Rule description too vague",
      solution:
        "Rewrite the rule to be more specific. Review Security → Policy Violations to understand exactly which messages are triggering it.",
    },
    {
      symptom: "Human handoff button not appearing in widget",
      cause: "No handoff integration connected, or the toggle is off",
      solution:
        'Go to Configure → Integrations and connect Help Scout or Intercom. Then go to Configure → AI & UX and enable the "Invite an agent" toggle for your widget.',
    },
    {
      symptom: "Skill is not activating on relevant questions",
      cause:
        "Skill not assigned to the widget, or another skill is taking priority",
      solution:
        "Go to Configure → AI & UX, confirm the skill is toggled on for the widget. Check skill order — skills higher in the list take priority.",
    },
    {
      symptom:
        "AI-suggested masking rules keep reappearing after I dismiss them",
      cause:
        "Suggestions are re-generated when servers change or new tools are analyzed",
      solution:
        "This is expected. Dismissed rules stay dismissed unless the underlying API changes significantly. If a suggestion reappears, the API schema changed — review and dismiss again.",
    },
    {
      symptom: "Knowledge base retrieval test returns low relevance scores",
      cause: "Document content doesn't closely match the query phrasing",
      solution:
        "Try rephrasing the test query to match how users would actually ask. Consider adding a document that explicitly contains the answer in clear, direct language.",
    },
    {
      symptom: "Widget shows the wrong language",
      cause:
        "Language detection is based on the user's browser locale or first message",
      solution:
        "The widget automatically detects the user's language. If a specific language is required, use a skill with language instructions (e.g., \"Always respond in English regardless of the user's language\").",
    },
    {
      symptom: "Onboarding wizard tool generation failed",
      cause:
        "API spec is invalid, unreachable, or auth credentials are incorrect",
      solution:
        "Review the error details shown in the Generation Progress step. Fix your spec URL or upload a corrected file, update auth credentials, and click Retry. The wizard saves your progress — you do not need to restart.",
    },
    {
      symptom: "AI stopped responding after I added my own API key",
      cause:
        "The custom OpenAI or Gemini API key is invalid, expired, or has no credits",
      solution:
        "Go to Settings → Company, click Edit, and remove or replace the API key. Errors from the key appear in Analytics → AI Actions.",
    },
  ],

  changelog: [
    {
      date: "2026-04-23",
      feature: "FAQ & Help page",
      description:
        "In-app Help & FAQ page added, accessible via Settings → Resources in the sidebar.",
    },
    {
      date: "2026-04-23",
      feature: "Help page — expanded docs",
      description:
        "All help sections rewritten with comprehensive step-by-step guides, FAQs, and troubleshooting covering every major feature.",
    },
    {
      date: "2026-04-23",
      feature: "Help page — full coverage",
      description:
        "Added 4 new sections (Onboarding Wizard, Customers, Integrations, Company Settings) and expanded 7 existing sections with previously undocumented features.",
    },
    {
      date: "2026-05-25",
      feature: "Guardrails — Generate from your website",
      description:
        "New Domain-aware Guardrails card at the bottom of the Guardrails page. Enter your company website and click Regenerate from website — the system writes a Company Policy tailored to your business and what your toolkits can do. Each run replaces the current policy text and can be edited before saving.",
    },
    {
      date: "2026-05-25",
      feature: "Onboarding — Company website in Step 1",
      description:
        "Step 1 of the onboarding wizard now asks for your company website (pre-filled from the URL you provide). It is used so the system can prepare a domain-aware guardrails policy for you while your toolkit is being set up.",
    },
    {
      date: "2026-05-25",
      feature: "Sign Up — optional company website",
      description:
        "The sign-up form now has an optional Company Website field. Provided values are pre-filled into onboarding and used to generate your initial guardrails policy.",
    },
    {
      date: "2026-04-28",
      feature: "Guardrails — Test panel",
      description:
        "New Test Guardrails panel on the Guardrails page lets admins send a test message and instantly see whether it is allowed or blocked, without creating a violation log entry.",
    },
    {
      date: "2026-04-28",
      feature: "Guardrails — Custom rejection message",
      description:
        "Admins can now set a custom rejection message shown to widget users when their message is blocked by guardrails; leave empty to use the system default.",
    },
    {
      date: "2026-04-28",
      feature: "Guardrails — Clear policy",
      description:
        "The Clear button on the Guardrails page allows admins to remove the company policy entirely without manually erasing text.",
    },
    {
      date: "2026-04-30",
      feature: "FAQ — Guardrails violation responses",
      description:
        "Added FAQ explaining that per-violation response text is set in the rule itself; the Blocked Message Response field is a single fallback for all violations.",
    },
    {
      date: "2026-04-30",
      feature: "FAQ — Language dialect (British English)",
      description:
        "Added FAQ explaining how to lock the AI to a specific dialect by adding an instruction to the Persona field in Agents Configuration.",
    },
    {
      date: "2026-04-30",
      feature: "FAQ — Feedback module",
      description:
        "Added FAQ explaining how to enable user feedback and where ratings and written comments flow in Analytics → Feedback.",
    },
    {
      date: "2026-05-06",
      feature: "Widget — Toolkit assignment",
      description:
        "New section documenting how to assign 1–5 toolkits to a widget from Configure → Embed → Edit Widget.",
    },
    {
      date: "2026-05-01",
      feature: "Toolkit — YAML spec support",
      description:
        "YAML-format OpenAPI and Swagger specs can now be loaded by URL in addition to file upload; both OpenAPI 3.0 and Swagger 2.0 YAML are supported.",
    },
    {
      date: "2026-05-06",
      feature: "Widget toolkit management",
      description:
        "Documented how to swap or update toolkits on an existing widget via Configure → Embed → Edit — no new widget or embed code change needed.",
    },
    {
      date: "2026-05-05",
      feature: "Email Notification Channels",
      description:
        "Admins can now connect SMTP email channels so the AI can send reports, summaries, and data exports directly to users by email during a conversation or via scheduled workflows.",
    },
    {
      date: "2026-05-06",
      feature: "Customer analytics — Engagement & Performance panel",
      description:
        "Each customer detail page now shows an Engagement & Performance section with seven stat tiles (sessions, messages, tools, success rate, last active, days since active) and a four-stage engagement funnel scoped to that specific customer over the last 30 days.",
    },
    {
      date: "2026-05-15",
      feature: "Widget — Display behaviour (Overlay vs Push)",
      description:
        "New per-widget setting in Configure → User Interface for Sidebar and Custom Trigger display types. Overlay keeps the panel floating above the page; Push compresses the host page to the left so the chat sits beside it instead of covering it.",
    },
    {
      date: "2026-05-15",
      feature: "Widget — Open context for Custom Trigger",
      description:
        "Custom Trigger buttons now accept an openContext field alongside the user-facing message — a hidden tech message (page URL, entity ID, filters, etc.) that is injected as system context for the AI on the opening turn but never shown to the user. A new copy-snippet is visible in Configure → Embed when Display type is Custom Trigger. The old developerContext name still works as a deprecated alias.",
    },
    {
      date: "2026-05-20",
      feature: "Customer Portal — In-chat Configure button",
      description:
        "New gear icon in the widget's chat top bar expands the iframe into a near-fullscreen modal containing the Customer Portal. The chat conversation, scroll position, and unsent input are preserved while the customer is in the Portal — closing returns to the same chat.",
    },
    {
      date: "2026-05-20",
      feature: "Customer Portal — Per-widget visibility settings",
      description:
        "New section in Configure → User Interface lets admins turn the Portal on or off per widget and pick which Portal pages (Members, Knowledge Base, Skills, Workflows, Integrations, Settings) are visible to customers. Profile is always visible. Require customer authentication was relocated here from Data display so it sits next to the Portal toggles.",
    },
    {
      date: "2026-05-20",
      feature: "Customer Portal — Comprehensive Help section",
      description:
        "New Customer Portal Help section with full setup walkthrough and 24 FAQs covering page-by-page capabilities, role differences, direct-link embedding, customer-scoped data isolation, mobile behaviour, and reset flows.",
    },
    {
      date: "2026-05-26",
      feature: "Help & FAQ — Technical docs tab",
      description:
        "New Technical docs tab in Help & FAQ (between Guide and FAQ) listing tenant-facing integration guides as clickable cards. Each card opens a modal with the full markdown content and a Download .md button so developers can save the guide locally. The first guide available is the Customer Portal — Tenant Integration Guide.",
    },
    {
      date: "2026-05-27",
      feature: "Prompt Shortcuts — Configure page",
      description:
        'New Configure → Prompt Shortcuts page lets admins create reusable in-chat prompts surfaced to widget end-users as a Shortcuts icon (next to the mic) and via the / (slash) command. Each shortcut supports {variable} placeholders that pop a fill-panel for the end-user — labels are auto-humanized (customerEmail → "Customer Email"). Includes create/edit/delete, active toggle, up/down reorder, and a 16-icon picker.',
    },
    {
      date: "2026-05-27",
      feature: "Customer Portal — Prompts page",
      description:
        "Customer Admins can now manage CustomerCompany-scoped prompt shortcuts from the Customer Portal. Their shortcuts merge with tenant-wide ones in the widget chat dropdown; on name collisions the CustomerCompany version wins for that company's members. Per-widget toggle lives under Configure → User Interface → Customer Portal → Pages → Prompts.",
    },
    {
      date: "2026-05-27",
      feature:
        "Widget chat — Shortcuts button and / command with placeholder panel",
      description:
        "New Shortcuts icon in the chat input action row (next to the mic) opens the same filterable dropdown as typing /. Picking a shortcut with {variable} placeholders opens a panel listing every unique variable with one input field each — the chat input live-updates with the substituted text. Shortcuts without placeholders insert directly.",
    },
    {
      date: "2026-06-04",
      feature: "Integrations — Intercom required permissions",
      description:
        "Added setup step and FAQ explaining the five Intercom Developer Hub permissions required before connecting: Read and list users and companies, Write users and companies, Read conversations, Write conversations, and Read admins.",
    },
    {
      date: "2026-06-03",
      feature: "Widget — Inline embedding",
      description:
        "New Inline embed block in Configure → Embed lets you mount the widget inside a container element on your own page instead of floating it. The widget fills the container with no launcher button or backdrop — your layout controls its size and position. Enter the container's CSS selector and copy the generated snippet (container element + script).",
    },
    {
      date: "2026-06-03",
      feature: "Customer Portal — Inline mode",
      description:
        "When embedding the widget inline, you can also turn on Open Customer Portal inline so the Portal opens inside the same container instead of a fullscreen modal. Applies only to widgets that already have the Customer Portal enabled.",
    },
    {
      date: "2026-06-09",
      feature: "Widget — Custom fonts",
      description:
        "Set a custom Body font (and an optional separate Heading font) under Configure → User Interface → Colours and styling → Typography, using a Google Fonts link or a direct .woff2/.ttf font URL. The Body font applies to all widget and Customer Portal text and charts; the Heading font applies to titles (widget title, chat names, Portal title and menu, section headers). Code blocks stay monospace.",
    },
    {
      date: "2026-06-10",
      feature: "Knowledge Base — Uncategorized filter",
      description:
        "New Uncategorized entry in the left sidebar shows all documents and web sources not assigned to any folder — a quick way to find and organize unstructured content.",
    },
    {
      date: "2026-06-10",
      feature: "Knowledge Base — Status labels simplified",
      description:
        "The intermediate Uploaded state is no longer shown separately. All pre-ready states now display as Processing (yellow badge), so the status column reads either Processing or Ready.",
    },
    {
      date: "2026-06-10",
      feature: "Knowledge Base — Bulk actions",
      description:
        "Checkboxes on every document and web source row let you select multiple items and then delete, move to a folder, or toggle Global for all selected items in a single action.",
    },
    {
      date: "2026-06-13",
      feature: "Skills — Manual create & edit (no AI)",
      description:
        "The Skills page now has a Create Manually button alongside Create with AI, opening a form where you fill in every field yourself. The skill editor has an Editing mode toggle — Manual lets you edit Trigger Context and Agent Instructions directly with no AI involved; AI Assistant keeps the change-request flow.",
    },
    {
      date: "2026-06-13",
      feature: "Skills — Delete with confirmation",
      description:
        "A Delete button now sits next to Edit on each skill. Clicking it opens a confirmation popover warning that deletion is permanent and removes the skill from all agents; you confirm before anything is removed.",
    },
    {
      date: "2026-06-13",
      feature: "Toolkits — Delete a tool",
      description:
        "Each tool in a toolkit now has a Delete button next to Enable/Disable and Edit. It opens a confirmation popover warning that deletion is permanent before the tool is removed from the toolkit. Use Disable instead to turn a tool off temporarily.",
    },
    {
      date: "2026-06-15",
      feature: "AI Memory",
      description:
        "The assistant can now remember durable, factual details about each customer across conversations so they don't have to repeat themselves. Turn it on per company under Settings → Company → Enable AI memory. View and delete a customer's memories on their Customers detail page, add shared Account Memory notes on the Customer Companies page, and let customers view, forget, and opt out of memory from the Memory page in the Customer Portal. Memory never stores plan, billing, or authorization claims or raw personal data, and one customer's private memory is never shared with another.",
    },
    {
      date: "2026-06-15",
      feature: "Widget authentication required by default",
      description:
        "New widgets now require visitors to set a PIN or password before chatting; you can turn this off when creating or editing a widget.",
    },
    {
      date: "2026-06-16",
      feature: "Guardrails — Test with conversation context",
      description:
        "The Test Guardrails panel now has an optional Conversation context section. Add prior User/Assistant turns to reproduce exactly how the live widget judges a message in context — useful when a message passes a plain test but is blocked in the widget.",
    },
    {
      date: "2026-06-16",
      feature: "Two-Factor Authentication (2FA)",
      description:
        "You can now secure sign-in with an authenticator app from Settings → Profile, and admins can require 2FA for everyone from Settings → Company. A lost device is recovered by an admin reset.",
    },
    {
      date: "2026-07-01",
      feature: "Widget page context injection",
      description:
        "Your website can now feed the AI hidden context about the page a visitor is on (window.StrikeUpChat.context.set/add/clear/get) so it answers their questions specifically — the context is never shown to the visitor, attaches to their next message, and persists across follow-up questions. The Custom Trigger one-shot developerContext field was renamed to openContext (the old name still works as a deprecated alias).",
    },
    {
      date: "2026-07-02",
      feature: "Autonomous Agents",
      description:
        "You can now trigger an AI agent from an outside system with a webhook instead of a schedule. Create an agent under Configure → Autonomous Agents (name, instructions, the widget it runs under, and synchronous or asynchronous replies), then create a one-time access token that all of your company's agents share. Callers can send a JSON body that you weave into the prompt with {{payload.*}} variables, and the Dashboard's Autonomous agent usage card plus each agent's execution log show how often they run.",
    },
    {
      date: "2026-07-02",
      feature: "Widget — Detailed chart view",
      description:
        "Charts in the widget chat are now cleaner — bars with a subtly rounded top, smaller data points, a fainter grid, fewer rounded Y-axis numbers, and axis labels plus the legend hidden by default to reduce clutter in the narrow chat (a toggle reveals both). Hover tooltips now lead with what you hovered (e.g. the endpoint) and its value. Each chart also has a new View detailed chart button that opens a large, near-full-screen view with everything shown — without moving or hiding the chat widget. The detailed view matches your widget's light/dark theme and chart colors.",
    },
    {
      date: "2026-07-02",
      feature: "Dashboard — cleaner charts",
      description:
        "The admin dashboard charts got the same readability pass as the widget: bars have a subtly rounded top, the Y-axis shows fewer rounded numbers with fewer grid lines, and hover tooltips lead with the category (e.g. the endpoint or tool) and its value.",
    },
    {
      date: "2026-07-02",
      feature: "Dashboard — Overview, Usage & Performance pages",
      description:
        "The dashboard is now split into three focused pages — Overview, Usage, and Performance — under the Dashboard menu, so each page loads faster and is easier to scan. A single date-range control (with 7/14/30-day presets and a custom From/To calendar) drives every chart on all three pages and is remembered as you move between them. The Usage page adds an All Skills Usage table with CSV export, and — for admins — Token Usage by Model and Credits Usage charts.",
    },
    {
      date: "2026-07-02",
      feature: "Autonomous Agents — per-user tokens",
      description:
        "Your own customers can now trigger an autonomous agent as themselves. If a customer has a PIN or password (the same one used for the customer portal), their system swaps it for a personal token using the exchange example shown on the agent under Webhook. Runs started with a personal token are labelled with that customer — a new Triggered for column in the execution log and a person badge in the Access tokens panel — while the agent still uses the widget's own tools and credentials, never the customer's personal accounts or memory.",
    },
    {
      date: "2026-07-05",
      feature: "Autonomous Agents — per-user tokens only",
      description:
        "Autonomous agent tokens are now always minted by one of your customers exchanging their PIN or password — the admin-created shared token has been removed. Every run is recorded against the customer whose credential minted the token, and the Access tokens panel is now for reviewing and revoking tokens only. For an unattended job with no real person, create a dedicated customer to act as a service account and mint a token from it. Any tokens created before this change keep working until you revoke them.",
    },
    {
      date: "2026-07-06",
      feature: "Autonomous Agents — async results by polling or callback",
      description:
        "An asynchronous webhook call now returns a run id and a status address the caller can check until the agent finishes and then read its answer, or you can supply a callback address to have the finished (optionally signed) result delivered automatically. Admins can also read any run's answer directly with the new View output button in the execution log.",
    },
    {
      date: "2026-07-08",
      feature: "Widget — Send button hover colour",
      description:
        "You can now set the colour the chat's send button turns on hover, with separate values for light and dark themes, under Configure → User Interface → Colours and styling. Leave it unchanged to keep the default blue.",
    },
    {
      date: "2026-07-13",
      feature: "Autonomous Agents — result request example",
      description:
        "An asynchronous agent's Webhook panel now shows a copy-ready example of the request that fetches a run's result, alongside the existing examples for getting a token and triggering the agent.",
    },
    {
      date: "2026-07-15",
      feature:
        "Autonomous Agents — token generation now requires admin sign-in",
      description:
        "Generating an autonomous-agent token now requires your company-admin sign-in in addition to the customer's PIN or password — the token endpoint is no longer open to anonymous callers. You (or your backend, signed in as a company admin) generate a token for one of your customers; triggering the agent and checking an async run's status still use only the generated token. Tokens generated before this change keep working.",
    },
    {
      date: "2026-07-23",
      feature: "Widget chat — Stop button",
      description:
        "While the assistant is writing, the chat's send button becomes a square stop button (Esc also works). Stopping ends the answer immediately, keeps the part already written as a normal message, and cancels the AI work behind it so it stops consuming credits.",
    },
    {
      date: "2026-08-02",
      feature: "AI Gateway",
      description:
        "A new Configure → AI Gateway page lets you connect your own AI provider accounts — including aggregators and self-hosted endpoints — and choose which model answers each part of a conversation, either by difficulty level or stage by stage. Your provider keys are stored encrypted and never shown again, and a Current setup tab shows exactly which model every stage will use and why.",
    },
    {
      date: "2026-08-03",
      feature: "AI Gateway — provider outages and per-question routing",
      description:
        "Conversations now survive a provider outage: the system retries, switches to a backup model where one is set up, and sets a repeatedly failing provider aside so it does not slow everything down — with a Health badge on each connector showing what has been happening. A new Per-question routing option judges how hard each message is and answers it at the level that fits, instead of always using the level attached to the stage.",
    },
    {
      date: "2026-08-03",
      feature: "AI Gateway — spend and budget alerts",
      description:
        "A new Spend tab estimates what your AI use has cost over the last 7, 30 or 90 days, broken down by model, by stage, by widget and by day, with background work on StrikeUp's own models shown separately so it never looks like your spend. A new Budget alerts tab lets you set an amount per month, week or day and be warned as you pass 50%, 80% and 100% of it — budgets warn only, and never block a conversation.",
    },
    {
      date: "2026-08-03",
      feature: "AI Gateway — Activity tab",
      description:
        "A new Activity tab lists every conversation turn over the last 1, 7 or 30 days with the models it used, the number of calls it took, how long the providers spent on it and what it cost, and View opens the turn to show each call and each retry inside it. The same tab controls how much message text is kept alongside those figures — short extracts by default, full text only as a time-boxed choice that expires by itself — and shows a Configuration changes list of every change made to your connectors, models, levels and budgets, and by whom.",
    },
    {
      date: "2026-08-10",
      feature: "Custom Trigger — check whether the widget is open",
      description:
        "Your page can now ask the widget whether it is currently open, using window.StrikeUpChat.isOpen(). Previously a custom trigger button had no way to know that the visitor had closed the chat with the minimize arrow or the close button inside it, so the button drifted out of sync. Configure → Embed shows a copy-ready snippet for a single button that opens and closes correctly.",
    },
    {
      date: "2026-08-10",
      feature: "Web sources — support for AI-friendly documentation sites",
      description:
        "Crawling a documentation site that publishes an AI-friendly version of its pages now works properly. Previously those sites returned no usable content at all, or text cluttered with stray backslashes. The crawler now reads that version, follows every page listed in the site's index, and avoids saving the same page twice.",
    },
    {
      date: "2026-08-11",
      feature: "Web sources — see exactly which pages were crawled",
      description:
        "A new View Pages button on each web source shows every page address the crawler found, whether it was added to your knowledge base, and the reason when it was not — such as the page limit being reached or the page being empty.",
    },
    {
      date: "2026-08-11",
      feature: "Web sources — refresh a single page",
      description:
        "You can now refresh one page on its own from the View Pages list, instead of re-crawling the whole site. It also works on pages that were skipped because the page limit was reached.",
    },
    {
      date: "2026-08-11",
      feature: "Web sources — page limit raised to 500",
      description:
        "Max Pages now goes up to 500 (previously 200), so larger documentation sites can be covered in one web source.",
    },
    {
      date: "2026-08-11",
      feature: "Knowledge base — sources link to the real page",
      description:
        "When the assistant cites a crawled web page, the link now points to the normal page address instead of the behind-the-scenes file the crawler downloaded. Crawls are also faster, because each page is now fetched once rather than twice.",
    },
    {
      date: "2026-08-12",
      feature: "Customers — filter by activity",
      description:
        "The Customers list has new Show chips: Has chat session, Opened never chatted, Anonymous, Preview and No activity. Has chat session is on by default, so empty records created when the widget merely loads no longer clutter the list.",
    },
    {
      date: "2026-08-12",
      feature: "Customer Chat History — created date",
      description:
        "The Customer Chat History table on a customer's detail page now shows when each chat was started, and lists the most recent chat first.",
    },
    {
      date: "2026-08-14",
      feature: "Custom Trigger — send the opening message automatically",
      description:
        'Configure → Embed has a new "Send it automatically" switch on the "Open with a placeholder AI message" block. With it on, your button opens a new chat with the message already sent, so the assistant starts answering instantly instead of waiting for the visitor to reply.',
    },
    {
      date: "2026-08-14",
      feature: "Translate shortcut cards, prompt shortcuts and welcome text",
      description:
        "Shortcut cards, Prompt Shortcuts and the welcome screen text now have a Default tab plus + Add language. Customers automatically see the version matching their browser language, and any field you leave blank falls back to the default text.",
    },
    {
      date: "2026-08-14",
      feature: "Customer Portal — Prompts renamed to Prompt Shortcuts",
      description:
        "The Customer Portal page is now called Prompt Shortcuts, matching the admin app, and company Members can see the list read-only instead of not seeing the page at all.",
    },
    {
      date: "2026-08-14",
      feature: "Fixed — Customer Portal page toggles now stick",
      description:
        "The Prompt Shortcuts checkbox under Configure → User Interface → Customer Portal → Pages always reappeared switched on after a reload, even when it had been turned off. It now shows the value you actually saved.",
    },
    {
      date: "2026-08-14",
      feature: "Fixed — Configure button missing until refresh",
      description:
        "When a customer's saved session had expired, the gear (Configure) button that opens the Customer Portal did not appear until the page was reloaded. It now appears as soon as the session is re-established.",
    },
    {
      date: "2026-08-14",
      feature: "Customer Portal — Memory and Prompt Shortcuts pages documented",
      description:
        "The Customer Portal section now lists all nine pages. Memory (what the assistant remembers about a customer, with a Forget button) and Prompt Shortcuts were previously missing from this guide.",
    },
    {
      date: "2026-08-14",
      feature: "Customer Portal — corrected what Members can see",
      description:
        "Member-role customers see Profile, Memory and Prompt Shortcuts — not only Profile as previously documented. Prompt Shortcuts is read-only for them.",
    },
    {
      date: "2026-08-14",
      feature:
        "Customer Portal — corrected Knowledge Base, Profile and password-reset answers",
      description:
        "The guide said customers could crawl websites from the Portal, change their credential on the Profile page, and use a 'Forgot PIN / password' link. None of those exist. The FAQ now describes what the Portal actually does, and how to reset a forgotten credential by re-running a CSV import.",
    },
    {
      date: "2026-08-14",
      feature: "New — Customer Portal guide for your end users",
      description:
        "A separate guide written for the people who use the Portal inside your widget, covering signing in, every page, and privacy. Share docs/CUSTOMER_PORTAL_USER_GUIDE.md with your customers, or point them at the End User Portal Guide tab on the docs site.",
    },
    {
      date: "2026-08-14",
      feature: "Toolkit — documentation page addresses now accepted",
      description:
        "The API Documentation URL field now also accepts the address of the documentation page you see in your browser, such as a Swagger UI page. Click Validate and the specification behind the page is found automatically and filled in for you. If it cannot be found, the error now explains that the address is a documentation page instead of showing a technical parsing message.",
    },
    {
      date: "2026-08-14",
      feature: "New — Suggestions page",
      description:
        "A new page under Configure that gathers everything StrikeUp has proposed for your workspace. Suggestions you dismiss are now kept instead of deleted, so anything dismissed by mistake can be found here and restored.",
    },
    {
      date: "2026-08-14",
      feature: "Suggested widget shortcuts",
      description:
        "When several different customers keep asking for the same thing, StrikeUp now proposes it as a one-click shortcut for your widget. Accept it and the shortcut is created for you, ready to use.",
    },
    {
      date: "2026-08-16",
      feature: "Suggestion strip now remembers that you closed it",
      description:
        "The strip at the top of the page used to reappear every time you logged in. It now stays closed for you personally until new suggestions appear, and closing it never hides it from your colleagues.",
    },
    {
      date: "2026-08-25",
      feature: "Widget — download any table in an answer",
      description:
        "Tables the assistant writes into a reply now carry a Download table as CSV button, the same one the separate table card already had, so a visitor can open the figures in a spreadsheet. The button appears once the answer has finished writing.",
    },
    {
      date: "2026-08-26",
      feature: "AI Gateway is now an optional extra",
      description:
        "The AI Gateway page appears in your menu only if it has been switched on for your workspace. Nothing changes for anyone already using it, and workspaces without it carry on running on the models included with their plan. Ask StrikeUp if you would like it added.",
    },
    {
      date: "2026-08-27",
      feature: "AI Gateway — sub-agents can now be configured",
      description:
        "The extra workers that run when a question is split into several steps now appear as their own Sub-agents of … rows on the Standard Mode and Advanced Mode tabs, so you can give them their own level or model. Each row follows the agent it belongs to until you change it — which means an agent already set above Medium now costs more per complicated question than before, and moving only that row back is how you keep it cheap.",
    },
    {
      date: "2026-08-28",
      feature: "Manage a customer company from its company page",
      description:
        "CRM → Companies → a company is now organised into tabs, and you can manage that company's knowledge base, skills, workflows, prompt shortcuts, email channel and member roles yourself — the same things its own admins see in the customer portal. Deactivated companies stay editable by you.",
    },
    {
      date: "2026-09-08",
      feature: "AI Memory transparency and account-memory safety checks",
      description:
        "The Customer Portal's Memory page now also lists the account memory shared by the customer's organisation, read-only; adding Account Memory refuses plan or entitlement claims and notes longer than 400 characters.",
    },
    {
      date: "2026-09-08",
      feature: "AI Gateway — a withdrawn model is named instead of blank",
      description:
        "When StrikeUp withdraws a model you had chosen for a stage or a level, the Standard Mode and Advanced Mode tabs now show it as “Retired model — no longer available” rather than leaving the box empty, so you can see what the setting was and pick a replacement yourself. Before this, saving the page with an empty box could quietly change that setting.",
    },
    {
      date: "2026-09-08",
      feature: "Chat — connected services ask for sign-in when they need it",
      description:
        "Toolkits that need a visitor's own credentials (a personal token, OAuth or per-user header values) now show the authorization screen in the widget the first time the visitor needs them; before, the prompt never appeared and those calls failed silently.",
    },
    {
      date: "2026-09-08",
      feature: "Safety — quoted text cannot trigger buttons or a handoff",
      description:
        "Follow-up buttons and the human-handoff prompt are honoured only when the assistant itself offers them at the end of its reply; the same wording inside a document, ticket, tool reply or research note is shown as plain text and can no longer be clicked past the guardrails. Customer-company instructions and persona extras now adjust tone and workflow only — they never relax approvals, guardrails or citation rules.",
    },
    {
      date: "2026-09-08",
      feature: "Instructions are checked before they are saved",
      description:
        "Persona instructions, skill prompts, customer-company instructions (from the admin app or the Portal) and Account Memory are checked when saved. Text that tries to change the assistant's identity, relax approvals or guardrails, deceive users, or that contains credentials, links, encoded text or script is refused with the reason shown.",
    },
    {
      date: "2026-09-08",
      feature: "Approvals — clean recovery and no double execution",
      description:
        'Approved actions are never executed twice, only the person who owns the conversation can decide its approvals, and if processing approved actions fails or is stopped the "Processing…" bubble clears and the composer unlocks.',
    },
    {
      date: "2026-09-11",
      feature: "Agents — routing by role, not by name",
      description:
        "The assistant now picks the Support, Copilot, Analytics or General agent by its role, so renaming an agent no longer changes which instructions and policies apply. Previously a renamed specialist could silently answer with General's settings.",
    },
    {
      date: "2026-09-12",
      feature: "Tool Runs — attempts and failure kinds",
      description:
        "Analytics → Tool Runs gains a failure-kind filter and, on a run's detail page, an Attempts on this call panel listing every attempt the assistant made at that tool in the same answer, with the arguments sent and why a rejected attempt was stopped.",
    },
    {
      date: "2026-09-13",
      feature: "Agent runs — approvals from the Portal and the welcome screen",
      description:
        "Customers see their autonomous agent and scheduled workflow runs in a new Agent runs page of the Customer Portal, where a run waiting for an approval or an answer is decided; a large widget also shows Needs you and Recent agent activity sections on its welcome screen. Analytics → Agent jobs gives you a read-only view of every job with its steps, approvals and events.",
    },
    {
      date: "2026-09-12",
      feature: "Truncated answers are flagged",
      description:
        "When a reply stops because it hit the model's length limit, the widget now shows an inline notice instead of leaving a cut-off answer looking complete.",
    },
    {
      date: "2026-09-12",
      feature: "Every widget runs all four agents",
      description:
        "General, Support, Copilot and Analytics are always on for every widget — there is no enable/disable switch any more — and the assistant falls back to General when the intent is unclear. The unused Default Instructions box was removed from widget settings. The knowledge base is now searched with up to three targeted queries per question, and the assistant skips it only when it is confident no documents are needed.",
    },
    {
      date: "2026-09-12",
      feature: "Chat — the assistant knows the date, time and time zone",
      description:
        'Every message carries the visitor\'s time zone, language and local time from their browser, so "yesterday" and "this week" resolve correctly; scheduled workflows use their schedule\'s time zone, and a run with neither works in UTC and says so.',
    },
    {
      date: "2026-09-12",
      feature: "Approvals and follow-ups keep the whole conversation",
      description:
        "After an approval the answer continues with the persona, skills, knowledge base and earlier lookups in view, on the same provider and model as the turn that asked; an open approval survives reloads and reconnects for two hours. Earlier turns' lookups and research notes are now remembered in later turns.",
    },
    {
      date: "2026-09-12",
      feature: "Tools — arguments checked before a call is sent",
      description:
        "Arguments are validated against what your toolkit declares (required fields, allowed values, date and e-mail formats, ranges) before a call leaves the platform; a rejected call is not sent, the assistant retries with corrected arguments — never the same ones — and stops after three failed attempts and reports them. Relative dates are converted using the visitor's time zone, the first unauthorised reply stops further retries of that tool, and toolkit parameters now show the assistant a format, an example and a unit.",
    },
    {
      date: "2026-09-12",
      feature: "Memory — fairer ranking, nothing from automated runs",
      description:
        "A customer's own memories keep at least half of the places when only a few fit; workflows and autonomous agents neither read nor write memory; data that appears only in a tool's reply is never stored as a memory on its own; injected memories are cut at a sentence, not mid-word; new memories appear within about an hour and are consolidated daily.",
    },
    {
      date: "2026-09-12",
      feature: "AI Gateway — smarter per-question routing and fallback",
      description:
        '"Yes" or "go ahead" inherits the level of the question it answers instead of being treated as trivial; a conversation too long for a model moves to the next model in your backup chain instead of failing; backup models that can return charts and tables are tried first; attachment contents are removed from every provider\'s request log; and the Anthropic output limit follows the model\'s real ceiling.',
    },
    {
      date: "2026-09-12",
      feature: "Long conversations — summaries no longer skip messages",
      description:
        "The rolling conversation summary now catches up on every message since the last summary instead of only the newest few, keeps the most recent facts within a fixed size, and shortens the oldest history first.",
    },
    {
      date: "2026-09-13",
      feature: "AI Gateway — full message text in Activity",
      description:
        "Every conversation turn now records the complete request and reply instead of only the first few hundred characters, and nothing shortens or deletes them later. The What is recorded setting is gone, older turns keep a Short extract badge to show their text was cut before the change, and message text is now removed only by asking StrikeUp support to erase a conversation.",
    },
    {
      date: "2026-09-13",
      feature: "Agent runs — resume after interruptions, run as the customer",
      description:
        "A run interrupted by a restart resumes from its last finished step without repeating it; a decider closing the browser no longer cancels the run; an approval that expires counts as denied and the run finishes with what it has; a step that changed data is not retried after a temporary error. Every autonomous run now acts as the customer its token belongs to — tokens not tied to a customer were revoked, and a deactivated customer's token is refused. Runs use the customer's saved API connections and never delete them on an expired authorization.",
    },
    {
      date: "2026-09-14",
      feature:
        "Background runs — per-customer limit replaces the concurrency cap",
      description:
        'A customer can start up to five background runs per minute across Run now, schedules and autonomous agents. Over the limit, Run now says "Too many runs in the last minute", the webhook answers 429 with Retry-After, and a schedule is retried later; nothing is dropped any more.',
    },
    {
      date: "2026-09-14",
      feature:
        "Multi-step answers — approvals name the records, results are checked",
      description:
        "When a multi-step answer needs your approval to change something, the approval card in the chat, the Portal's Agent runs page and Analytics → Agent jobs now list the exact records the step will act on (\"Acts on: 101, 103\"). Each research step's records now travel in full into the final answer (rankings, tables and charts are built from the exact figures), and the answer states plainly what a step could not verify — a call made outside the requested date window, or a source that does not provide a requested figure. When two steps disagree on a value, the answer shows both with their sources rather than picking one.",
    },
    {
      date: "2026-09-14",
      feature:
        "Agent runs — no unrequested e-mails, decisions land on the right run",
      description:
        "The platform no longer e-mails customers about waiting approvals — the Portal's Agent runs page and the widget's Needs you list are where they appear (a customer's own e-mail tool is unaffected). An approval decided while a new message was being sent now resumes the run it belongs to, a resumed run keeps the host page's custom headers, and the first attempt of a tool call is checked only against what the toolkit declares.",
    },
    {
      date: "2026-09-15",
      feature: "Multi-step answers — whole lists, no invented filters",
      description:
        'Asking for records "in pages of 10" now fetches every page and shows all of them — a page size no longer ends the fetch after the first page with a question about the next one — and a request that covers your whole account ("all tickets", "all accounts") is no longer narrowed to an account filter you did not name. Planning a multi-step answer is also faster: a plan that named a number (a page size, a timezone offset) used to be rejected and re-planned, and often abandoned, before the answer was built.',
    },
    {
      date: "2026-09-15",
      feature: "Chat — lookups listed inside the answer's steps",
      description:
        'When the assistant looks something up for you directly (without splitting the question into research steps), each lookup now appears as a line under "Executing tools…" inside the steps list of the answer — the same way research steps show their lookups — instead of a separate "Auto-approved execution" box above the answer for every call. Actions that wait for your approval still show as a card with Approve and Deny; once you approve, the action joins the same list, marked "approved by you", and the answer\'s steps cover the whole turn.',
    },
    {
      date: "2026-09-15",
      feature:
        "Chat — long lists fetched to the end, one set of rules for every answer",
      description:
        'Long lists are now fetched to the end: when a lookup has more pages than one research step can take in one go, the assistant continues from where it stopped and merges the pages into one result, saying how many of the total it fetched if a limit was reached; the Portal\'s Agent runs page and Analytics → Agent jobs show "iteration 2 of 5" on such a step. Every chat answer also follows one set of rules now — the same page limit as the research steps, what to say when a connection needs re-authorising or a lookup returns nothing, and no asking for a confirmation the approval card already asks for.',
    },
    {
      date: "2026-09-15",
      feature: "Chat — long lists in one go, and a cleaner trace",
      description:
        "When a lookup spans several pages, the assistant now fetches the pages in one go and works from one combined result, so a list of 22 records no longer takes eight rounds of requests; a page that repeats a record already seen (lists that re-sort between two calls) is counted once, and the answer says plainly when it holds fewer records than the total. Every record keeps its identifier through the fetch, so an assistant instructed to link records (for example ticket links) has what it needs, and the field you asked for by name (a ticket number, an order id) is kept even when the data layer would have trimmed it. The answer's steps list now shows one line per lookup, named after the tool as you named it, instead of two identical lines per page.",
    },
    {
      date: "2026-09-15",
      feature: "Chat — approvals show what they approve, and none for nothing",
      description:
        'An approval card for a multi-step answer now lists the tools the step will use and the records it will act on; before, the tools list was empty. When a step depends on records an earlier step did not find ("find tickets about X and close them" with no such ticket), the assistant no longer asks you to approve closing nothing — the step is skipped and the answer says so. An action asked for after an earlier lookup in the same answer now waits for your decision properly: the answer arrives after you decide, with the whole trace, instead of an empty bubble. The card\'s wording now matches the action ("update" for marking tickets resolved, not "retrieve"). Reports that list people now show their names: when a research step needs names next to ids, the assistant is given the lookup that resolves them even if the planner did not pick that group. Follow-up buttons are capped at four.',
    },
    {
      date: "2026-09-16",
      feature: "AI usage — who spends it, and optional hard limits",
      description:
        "A new Usage tab in AI Gateway shows who spent the tokens and how: by customer company, customer, source (chat, workflow, autonomous agent, Portal), workflow, autonomous agent and the costliest runs, for a period or this month; the same view sits on each customer company's page and on a customer's details. Budgets can now apply to one customer company and can be enforced as a hard limit: nothing is capped until you turn it on, and when an enforced budget is used up new conversations get a short notice and running agent jobs pause until the period resets or you raise the amount. The Customer Portal gets a Usage page (with its own on/off toggle) that shows customers only whether an allowance applies and how much of it is left, as a percentage. Analytics → Agent jobs shows each run's source and tokens.",
    },
    {
      date: "2026-09-16",
      feature:
        "AI Spend — one Dashboard page for cost and usage, for every workspace",
      description:
        "The Spend and Usage tabs of the AI Gateway are now one page, Dashboard → AI Spend: cost by model, stage, widget and day on top, and who spent it — by customer company, customer, source, workflow, autonomous agent and run — below, with one Usage choice for both. The page follows the same date range as the other Dashboard pages (looking back at most 90 days from the end of the range) and is shown to every workspace, with or without the AI Gateway. The older Token Usage by Model and Credits Usage charts on Dashboard → Usage were removed in its favour.",
    },
    {
      date: "2026-09-16",
      feature: "Budgets & limits — now under Configure",
      description:
        "Budgets moved from the AI Gateway to Configure → Budgets & limits, just above Embed, and no longer need the AI Gateway: every workspace can set alert thresholds and hard limits. Existing budgets, alerts and hard limits moved unchanged, and budget alert e-mails now link to the new page.",
    },
    {
      date: "2026-09-16",
      feature: "AI actions — Activity tab and a leaner AI Gateway",
      description:
        "The turn-by-turn Activity list is now a tab on Analytics → AI actions, next to the per-call list, and is shown to every workspace. Opening a turn shows each call's model, timing, tokens and cost, and View call opens the call's AI action page for the visitor's messages and the reply — the turn view no longer prints raw message text. The AI Gateway keeps only its model configuration — Connectors, Standard Mode, Advanced Mode and Current setup, where the Configuration changes list now lives.",
    },
    {
      date: "2026-09-16",
      feature: "Widget page context — best practices",
      description:
        "Configure → Embed now explains what to put into the hidden page context and what to leave out — start with who the visitor is, send the record's label and id once, skip counters and timestamps that move on their own, send only durable facts while a page is loading — with two copyable examples, and the Custom Trigger Open context example now shows a button-intent hint instead of a page description. The Widget Integration technical guide gains a matching best-practices section (7.1) with a checklist.",
    },
    {
      date: "2026-09-18",
      feature: "Job notifications — Telegram, Slack and per-person delivery",
      description:
        "A background run that stops for an approval or a question now reaches the person who owns it by email, Telegram or Slack when they are not there to see it. Companies get one channel per type on CRM → Companies → Integrations, where Telegram and Slack are new, and each of their people chooses where they are reached in the Portal under Profile → My notifications. Nothing is sent while the customer is watching the run, or if they decide within about a minute and a half.",
    },
    {
      date: "2026-09-18",
      feature: "Toolkit — editable Specification URL",
      description:
        "When a provider moves its API documentation file, you can now correct the address yourself: open the toolkit, click Edit, and change the Specification URL. The new address is checked as you type — you see how many endpoints it contains before you save, and Save stays disabled until it loads. Paste a documentation page and the specification behind it is found for you. Your tools are left untouched; run Check for Updates afterwards to review what changed.",
    },
    {
      date: "2026-09-21",
      feature:
        "Suggestions — skills, toolkit fixes, knowledge gaps, schedules and defects",
      description:
        "Configure → Suggestions now groups everything StrikeUp proposes by the need visitors kept asking about, one line per fix (open a line for what visitors typed and the full reasoning): a shortcut when the assistant already handles it, a new skill or an amendment to an existing skill when it handles it badly (review and edit in a dialog before anything is created), a toolkit change, a knowledge gap, a schedule for a weekly repeated request, and a defect when a tool keeps failing or returning nothing. About two weeks after you accept a skill, its details show whether friction on that need went down.",
    },
    {
      date: "2026-09-21",
      feature: "Suggestions — preview, Accept all, smarter diagnosis",
      description:
        'Every suggestion line now opens a preview of exactly what accepting does (the skill instructions, the section added to an existing skill, the tool switched on, the schedule). Each block has Accept all / Dismiss all. Behind it, StrikeUp now reads the assistant\'s failures against your real tools to tell "the tool is switched off" from "the assistant did not use it", merges different phrasings of the same need into one proposal, and only proposes a schedule for genuine recurring reports.',
    },
    {
      date: "2026-09-22",
      feature:
        "Setup guides for notification channels and support integrations",
      description:
        "Help — Technical docs now carries two illustrated, step-by-step guides: one for connecting email (SMTP), Telegram and Slack notification channels, and one for connecting Help Scout or Intercom. Each panel where you add one of these now has a Setup guide link that opens the guide at that provider — where to get a Telegram bot token, which four Slack scopes are required, the host and port for the common mail providers, and the webhook Intercom makes you register yourself.",
    },
    {
      date: "2026-09-23",
      feature: "Setup guide inside the Customer Portal",
      description:
        "Your customers no longer have to ask you where a bot token comes from. The notification channel guide now opens in a drawer inside the Customer Portal, right beside the form they are filling in, scrolled to the provider they picked. They only see guides you have published to the portal; everything else in Technical docs stays admin-only.",
    },
    {
      date: "2026-09-23",
      feature:
        "Setup guides focused on one provider, and finding a Slack member ID",
      description:
        "Where do I find these? next to an SMTP, Telegram or Slack form now opens only that provider's steps instead of the whole guide, both in the admin app and in the Customer Portal; click Show full guide to see the rest. In the Customer Portal, Profile → My notifications now shows your customers exactly where to copy their Slack member ID in Slack.",
    },
    {
      date: "2026-09-24",
      feature: "Job notifications for failed, paused and finished runs",
      description:
        "Your customers can now also be told when a background run fails, pauses because the AI allowance is used up, or finishes. In the Customer Portal under Profile → My notifications, failures and pauses start switched on and finished runs start switched off; approvals and questions still always reach them, and an answer in the chat is never announced.",
    },
    {
      date: "2026-09-24",
      feature: "Chat replies keep working after the tab closes",
      description:
        "Closing the chat or the browser tab no longer stops the assistant: it finishes the answer and saves it to the conversation. If the customer left before the reply arrived, they are told by email, Telegram or Slack — with the first lines of the reply if they switched on finished runs, or that it could not be answered if failures are on. Reopening the chat mid-answer shows it still working, and a second message asks them to wait for the reply first.",
    },
    {
      date: "2026-09-25",
      feature: "Customer Portal — company and personal workflows",
      description:
        "Workflows in the Customer Portal are now either company or personal, chosen when they are created. A company workflow is shared with everyone in the customer company — they all see it, its schedules and its runs, can decide its approvals and are told about it on the channels they switched on — while only Admin customers create, change, schedule or run it. A personal workflow is its creator's alone, runs and notifications included. Members now see the Workflows page too, existing workflows of people in a customer company became company workflows, and CRM → Companies → Workflows marks each one Company or Personal.",
    },
    {
      date: "2026-09-25",
      feature: "Customer Portal — Notifications page for everyone",
      description:
        "The Customer Portal's Integrations page is now called Notifications and every customer sees it. Admin customers add and manage their company's email, Telegram and Slack connections at the top; everyone, Members included, chooses below it under My notifications how they are told, which used to sit on the Profile page. In Configure → User Interface the Integrations card under Visible pages is now called Notification channels, and turning it off hides only the connections, not the page.",
    },
    {
      date: "2026-09-25",
      feature: "Customer Portal — setup guide beside the form",
      description:
        "In the Customer Portal, Setup guide and Where do I find these? now open the guide as a panel beside the page instead of on top of it, so your customers can read the steps and type into the form at the same time. Closing the guide gives the page its full width back.",
    },
    {
      date: "2026-09-25",
      feature: "Widget — resizable Custom Trigger pane",
      description:
        "Custom Trigger is now two display types in Configure → User Interface. Custom Trigger — Sidebar is the existing behaviour, unchanged. Custom Trigger — Panel adds a drag handle on the pane's left edge so visitors can widen the chat for long answers, wide tables and charts, and their browser remembers the width for next time. Both open from your own button and collapse with the same arrow tab, and the embed snippets are identical — switching between them needs no change to your page.",
    },
    {
      date: "2026-09-26",
      feature: "Notifications — Telegram and Slack tenant-wide",
      description:
        "Configure → Notifications now sets up Telegram and Slack as well as email, so a customer company without its own channel of a type falls back to yours. Test on a Telegram or Slack channel checks the bot token instead of emailing you, and a company channel that is still missing its token no longer blocks your fallback.",
    },
    {
      date: "2026-09-26",
      feature:
        "Job notifications — threads, one message per approval and Open buttons",
      description:
        "In Telegram and Slack, everything about one run or chat now arrives as one thread, and each approval or question is its own message — up to five, then one saying how many more are waiting. Set App page URL in Configure → User Interface → Customer Portal and every message gets an Open button that takes your customer to your page and opens that chat or run. Customers can also choose to be told when a background job starts, and a decision made in the Customer Portal now updates an open chat straight away.",
    },
    {
      date: "2026-09-26",
      feature:
        "Job notifications — Approve, Deny and chat from Slack and Telegram",
      description:
        "Where buttons and replies are on, approval messages in Slack and Telegram carry Approve and Deny, a question is answered by replying to it, and customers can chat with the assistant from the bot — /start-chat in Slack, /new in Telegram. Each company has a limit under Approvals from Slack and Telegram on what may be approved from a chat app (everything, by default). New Slack apps are created from a ready-made manifest, and customers confirm their Slack account, or connect through the Telegram bot, to use the buttons.",
    },
    {
      date: "2026-09-27",
      feature: "Job notifications — commands in Slack and Telegram",
      description:
        "Customers can now type /chats, /pending and /runs to see their recent chats, what is waiting for them and their recent runs, with links into your app, start a workflow with /run-workflow (in Telegram, /run) and the workflow's name, and stop an answer with /stop. A Telegram bot's command menu picks up the new commands the next time you save or test its channel.",
    },
    {
      date: "2026-10-01",
      feature:
        "Chat in Slack and Telegram — quick answers, charts, tables and live status",
      description:
        "Choices the assistant offers now arrive as buttons, charts and tables appear in the chat — native in Slack, a picture and a fixed-width table in Telegram — with each table's rows also sent as a CSV file, a status message shows what the assistant is doing until the answer arrives, and each chat gets a short name after its first answer. A Slack app created before this change needs updating from the manifest and reinstalling to send the CSV file.",
    },
    {
      date: "2026-10-01",
      feature:
        "Slack and Telegram — finished results in the message and sign-in Open links",
      description:
        "In Telegram and Slack, a run or chat that finishes now sends its whole answer — text, choices, chart, table and CSV file — instead of a short message with an Open button, and Open appears only when there is a chart or table to see in full. When your platform administrator has set up a hosted widget page, Open signs a person who connected their Telegram or Slack account straight into that chat or run, so your page's own sign-in or redirects no longer leave them on a new chat; the link works for 7 days. Slack now sends a chart it cannot draw itself, such as a stacked one or one with many points, as a picture, and a table the assistant writes into its text arrives as fixed-width columns in both apps. A Slack app that cannot send CSV files yet also needs updating from the manifest and reinstalling to send these pictures.",
    },
    {
      date: "2026-10-01",
      feature: "Slack and Telegram — continue earlier chats from /chats",
      description:
        "Each chat in the list from /chats now has a 💬 button that brings it into Slack or Telegram, even a chat started in your app, with its name and the start of its last answer, so the customer can carry on by replying in that message's thread in Slack or by sending their next message in Telegram. Tapping the chat's name still opens it in your app. The Slack app and Telegram bot need no changes.",
    },
    {
      date: "2026-10-03",
      feature: "Toolkits — valid field names from your response schema",
      description:
        "When a parameter picks which fields come back (fields or select), the assistant now sees the field names your API documentation declares for the response, so it asks for real names on its first call instead of guessing. On an existing toolkit, click Check for Updates and apply the changes to get them. Analytics → Tool Runs also now shows the error response for a call your API rejected, which was empty before.",
    },
    {
      date: "2026-10-06",
      feature: "Security — sign-in protection and safe custom icons",
      description:
        "Repeated wrong passwords now lock an account for a few minutes (longer if it continues), and sign-in, password-reset and widget PIN/password requests are limited per network address. New passwords must be at least 8 characters, including on password reset and on your Profile page. A Custom SVG Icon keeps only its drawing: scripts, event handlers and links are removed when you save, and SVG code that is not valid is rejected.",
    },
    {
      date: "2026-10-06",
      feature: "Security — read-only members and internal-address protection",
      description:
        "Members with the User or Guest role are now read-only: they can view the workspace, change their own password and two-factor settings, and run test retrievals, but only an Admin can change settings, manage users or see stored credentials and raw request logs. Nobody can change their own role or disable or delete their own account, and the last Admin cannot be demoted or disabled. Toolkits, token endpoints, specification URLs and knowledge-base website sources can no longer point at private or internal network addresses, and Amazon Bedrock is no longer offered for workspace AI connectors.",
    },
    {
      date: "2026-10-08",
      feature: "Job notifications — Microsoft Teams",
      description:
        "Microsoft Teams is now a fourth notification channel next to email, Telegram and Slack: add it with an Azure Bot and download the app package for the Teams admin center, and each person clicks Connect Microsoft Teams and sends the bot a link code — then approvals, answers in a box, quick answers, chat, plain-word commands, charts and tables work in Teams, with CSV files as Download links that work for 24 hours.",
    },
    {
      date: "2026-10-09",
      feature: "Web sources — exclude pages from the knowledge base",
      description:
        "When a website crawl pulls in pages you do not want answers to come from, open View Pages and exclude them — one at a time with the block icon, or several at once with the checkboxes. Excluded pages stop being used in answers within a few minutes and stay out on every future sync. You can include a page again at any time.",
    },
    {
      date: "2026-10-09",
      feature: "Widget — custom knowledge base badge",
      description:
        "The “Verified” badge on answers drawn from your uploaded documents can now say something that fits your brand. Set it per widget in Configure → User Interface → Chat labels, and translate it with + Add language.",
    },
    {
      date: "2026-10-10",
      feature: "Toolkits — capabilities update automatically",
      description:
        "Whenever you add, edit, enable, disable, or delete tools — or apply changes from Check for Updates — the AI now updates its picture of what the toolkit can do on its own within a few seconds, and open chats start using the new tools on their next message. Regenerate context and capabilities stays on the toolkit page as a fallback if that ever fails.",
    },
    {
      date: "2026-10-10",
      feature: "Tool editor — edit method and endpoint path",
      description:
        "You can now change a tool's HTTP method and endpoint path in the tool editor, so a tool pointing at the wrong endpoint can be fixed without deleting it. Tools you add by hand must now have an internal name made of letters, numbers, hyphens and underscores that no other tool in the toolkit uses (ignoring upper and lower case) — names with spaces stopped the AI from answering.",
    },
    {
      date: "2026-10-10",
      feature: "Toolkits — add a missing Specification URL",
      description:
        "Some copied toolkits had no Specification URL, and saving one failed. You can now add it from the toolkit's Edit form, and Check for Updates works from then on. Copying a toolkit now keeps its Specification URL.",
    },
    {
      date: "2026-10-11",
      feature: "Skills — search and refresh",
      description:
        "The Skills page has a Search skills box above the list that matches name, category, trigger context and description, and a Refresh button that reloads the list without leaving the page.",
    },
    {
      date: "2026-10-12",
      feature: "Notifications — who the AI can email",
      description:
        "Email channels have a new Who the AI can email setting. Signed-in user only, now the default for every email channel including existing ones, sends the AI's emails only to the signed-in user's own address, and chats where nobody is signed in cannot send; Allowed list adds the addresses and @domains you list; Any address restores the previous behaviour and shows a warning. Job notifications are not affected.",
    },
    {
      date: "2026-10-13",
      feature: "Tools — DELETE tools always ask for approval",
      description:
        "New tools whose HTTP method is DELETE are always created with High risk, Destructive and Requires Approval switched on, whatever the AI suggested. Other tools still get the AI's suggestion, which you can change in the tool editor. Existing tools keep their settings until you click Regenerate in the tool editor.",
    },
    {
      date: "2026-10-13",
      feature: "Customer companies — self-registered users join as members",
      description:
        "People who register themselves into a customer company now join as members, not admins. Only the first person in a customer company that has no users yet becomes its admin.",
    },
    {
      date: "2026-10-13",
      feature: "Notifications — email channel password and server checks",
      description:
        "Email channels now connect only to mail servers on the public internet, on the usual email ports (25, 465, 587, 2465, 2525, 2587). When you change an email channel's host, port or username, or turn SSL off, enter the password again: the stored one is only kept for the same server. The email test now says what to check instead of showing the mail server's own error text.",
    },
    {
      date: "2026-10-14",
      feature: "Security — sign-outs after password and role changes",
      description:
        "After this update everyone signs in to the admin app once more. Changing your password now signs you out on your other browsers and devices, resetting it signs you out everywhere, and Log out ends the sign-in on the server too. Customers are signed out when their role or company changes, when they are disabled, or when their company is deactivated or deleted. When the same customer signs in from two places at the same moment, one of them is asked to try again.",
    },
    {
      date: "2026-10-16",
      feature: "Tool Policies — set only by your system's sign-in",
      description:
        "A customer's tool policies now come only from the login ticket your system creates when it signs them in, and each new ticket replaces the last one's keys. Policy keys set by a script on your web page are ignored, so if your page sets them today, ask your developers to move them to the login ticket. Keys that don't match an active policy give no tools, customers signed in any other way can use every active tool, and renaming a policy's key keeps its tools.",
    },
    {
      date: "2026-10-16",
      feature: "Sign-up — confirm your email",
      description:
        "New accounts are created only after you click the confirmation link we email you, within 24 hours. Signing up again sends a new link, and older links stop working. Capital letters in email addresses no longer matter: Anna.Becker@example.com and anna.becker@example.com sign in to the same account.",
    },
    {
      date: "2026-10-17",
      feature:
        "Customer import — limits shown up front, upload in parts, existing customers left unchanged",
      description:
        "The Import from CSV page now shows the limit before you choose a file (1,000 customers and 5 MB per upload) and offers to upload a larger file in parts. Customers already in your list are skipped unless you tick Update existing customers, so importing a file again no longer overwrites anyone by accident.",
    },
    {
      date: "2026-10-17",
      feature:
        "Customer Portal — switched-off pages are blocked, not only hidden",
      description:
        "A Portal page you switch off under Visible pages is now refused by the server as well as hidden, so a customer cannot reach it with a direct request either. Enable Customer Portal still controls only the gear icon in the chat, and the ?mode=portal link keeps working while it is off.",
    },
    {
      date: "2026-10-17",
      feature: "AI memory — nothing is learned while memory is off",
      description:
        "When Enable AI memory is off for your company, the assistant no longer reads your conversations to learn from them, and conversations from that time are not read later when you turn it back on. Customers who switched memory off in the Portal are never read either. What is already stored is kept.",
    },
    {
      date: "2026-10-17",
      feature: "AI Gateway — connector tests are logged and limited",
      description:
        "Each Test on a connector is now shown on AI Spend under the stage connector_test and counts towards your budgets, like any other call. You can test one connector up to 5 times a minute and run up to 200 tests in 24 hours across your workspace.",
    },
    {
      date: "2026-10-17",
      feature: "Budgets — calls without a published price now count",
      description:
        "Budgets and hard limits now charge calls on models we hold no published price for at a typical rate, instead of counting them as free. AI Spend still marks those calls as not priced, so a budget can show more spend than AI Spend. Calls to a self-hosted endpoint you run yourself stay free unless StrikeUp sets a price for them.",
    },
    {
      date: "2026-10-17",
      feature: "AI emails — users confirm their address with a code",
      description:
        "The first time the AI emails a user whose address they typed in themselves, it sends a 6-digit code to that address and asks for it in the chat, and the email goes out once the user types the code. Addresses added through customer import, and those of customers who could already sign in, count as confirmed. The AI now sends at most 50 emails per user and 1,000 per workspace a day, and attachment names are cleaned up.",
    },
    {
      date: "2026-10-18",
      feature: "Sign out ends the session right away",
      description:
        "When you sign out, the session in that browser stops working immediately instead of staying usable for up to an hour. Your sessions on other devices stay signed in.",
    },
    {
      date: "2026-10-18",
      feature: "Web sources — filters that take too long stop the sync",
      description:
        "An Inclusion or Exclusion Filter so complex that checking one page address takes too long now stops the sync with a message naming that filter, instead of slowing down crawling. Edit or remove the filter and sync again.",
    },
    {
      date: "2026-10-18",
      feature: "Customers — see and clear saved toolkit credentials",
      description:
        "The Authorization card on a customer's page now lists the toolkits they saved credentials for, with the type and dates, and never the credentials themselves. Admins can click Clear Authorization to delete them, and the customer is asked to authorize those toolkits again the next time the assistant needs one.",
    },
    {
      date: "2026-10-18",
      feature: "Customer companies — deactivating also pauses workflows",
      description:
        "Workflows of a disabled customer, or of a customer whose company is deactivated, no longer run. Each skipped run shows as Cancelled with the reason and nothing is deleted, so runs resume once the customer or company is active again. Agent runs for those customers are refused too.",
    },
    {
      date: "2026-10-18",
      feature: "Toolkits — OAuth credentials and the API secret reach your API",
      description:
        "Tool calls now carry the OAuth access token a customer saved, filled into the toolkit's dynamic headers. For a Custom Endpoint toolkit, the {secret} placeholder now gets your API secret in the first token request too, not only when the token is refreshed.",
    },
    {
      date: "2026-10-18",
      feature: "Feedback — Save Triage works",
      description:
        "Saving a feedback item's triage status in Analytics → Feedback now works; before, it was refused with an error.",
    },
    {
      date: "2026-10-18",
      feature:
        "Human handoff — Intercom replies, reconnecting, mailboxes and ending a chat",
      description:
        "Intercom now accepts replies from your own Intercom app: enter its client secret when you connect. Connecting the same Intercom workspace again, or pressing Reconnect on a Help Scout integration, updates the existing integration instead of adding a second one, and an integration whose sign-in the helpdesk refuses now shows Error with a Reconnect button. Help Scout handoff now asks you to choose a mailbox. Visitors can press End chat with agent to go back to the AI assistant, and the widget's handoff messages now follow the widget's language.",
    },
    {
      date: "2026-10-18",
      feature: "Customer Portal — clearer messages and no run costs",
      description:
        "When a customer company is deactivated, its Portal pages now say so instead of showing a generic error. A knowledge base file over 100 MB is refused as soon as it is picked. The PIN and password hints now match the rules (4 to 8 digits, at least 8 characters), and the Agent runs page no longer shows customers a dollar amount for each run.",
    },
    {
      date: "2026-10-18",
      feature: "Widget — custom invite agent button",
      description:
        "The “Invite an agent” button can now say something that fits your brand, such as “Speak to an Advisor”. Set it per widget in Configure → User Interface → Chat labels, and translate it with + Add language.",
    },
    {
      date: "2026-10-20",
      feature: "Guardrails change history",
      description:
        "The Guardrails page now shows every saved version of your company policy — when it changed, who changed it and the full text — and highlights what each save changed in red and green, and lets you load an earlier version back into the editor to restore it.",
    },
    {
      date: "2026-10-21",
      feature: "Human handoff — visitor files reach Intercom agents",
      description:
        "Files a visitor attaches while talking to an agent, such as screenshots and PDFs, now arrive in the Intercom conversation as attachments; if Intercom does not accept a file, the agent gets the message with a line naming it, and Help Scout agents see the file names when the visitor sends files without text.",
    },
    {
      date: "2026-10-31",
      feature: "Knowledge base storage limit",
      description:
        "Each company now has a knowledge base storage limit — 10 MB by default — shared by uploaded documents and crawled web pages. The Knowledge Base page shows how much you have used, a file that does not fit is refused before it uploads, and a crawl that reaches the limit keeps the pages already stored and lists the rest as Skipped because over storage limit. Contact support to raise the limit.",
    },
  ],
};

export default helpContent;
